import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Button,
  Layout,
  Typography,
  Select,
  message,
  Modal,
  Input,
  Switch,
  Tooltip,
  Tag,
  Badge,
  Spin,
  Popover,
  Tabs,
  Segmented,
  Popconfirm,
  Upload,
  ConfigProvider,
  theme as antdTheme,
} from "antd";
import {
  CloseOutlined,
  CheckOutlined,
  PlayCircleOutlined,
  CopyOutlined,
  AudioMutedOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  VideoCameraAddOutlined,
  TeamOutlined,
  BgColorsOutlined,
  ShareAltOutlined,
  DesktopOutlined,
  DownOutlined,
  UpOutlined,
  SettingOutlined,
  MessageOutlined,
  SendOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  LayoutOutlined,
  PlusOutlined,
  MinusOutlined,
  UndoOutlined,
  RedoOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  SmileOutlined,
  CheckCircleFilled,
  ClearOutlined,
  EditOutlined,
  HighlightOutlined,
  FontSizeOutlined,
  CodeOutlined,
  SelectOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import MonacoEditor from "@monaco-editor/react";
import {
  CRAFTERHACK_DARK_THEME,
  CRAFTERHACK_LIGHT_THEME,
  defineCrafterhackThemes,
  getCrafterhackEditorOptions,
} from "../../common/monacoAppTheme";
import proxy from "../../api/axios";
import {
  defaultCode,
  languageMapping,
  COMPILER_LANGUAGES,
  getMonacoLanguage,
  isCompilerLanguage,
} from "../../common/constants";
import { io } from "socket.io-client";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { generateRandomString } from "../../common/utils";
import { Helmet } from "react-helmet";
import BrandMark from "../../components/BrandMark";
import { useSelector } from "react-redux";
import LiveCodingTour, {
  LIVE_TOUR_START_EVENT,
} from "../../components/LiveCodingTour";
import {
  getDefaultRtcConfiguration,
  getIceServers,
  fetchIceConfig,
  clearIceCache,
} from "../../common/webrtcIce";
import { createLiveMediaRelay } from "../../common/liveMediaRelay";
import {
  areShortcutsEnabled,
  setShortcutsEnabled,
  matchProblemShortcut,
  LIVE_CODING_SHORTCUTS,
} from "../../components/problems/problemShortcuts";
import * as bodyPix from "@tensorflow-models/body-pix";
import "@tensorflow/tfjs-backend-webgl";
import * as tf from "@tensorflow/tfjs";

const { Header, Content } = Layout;

/** Match CSS-ish breakpoints without pulling in MUI. */
function useIsNarrow(maxWidth = 900) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < maxWidth : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidth - 1}px)`);
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [maxWidth]);
  return narrow;
}
const { Title, Text } = Typography;

/** Same-origin in prod (avoids www vs apex CORS); local points at API. */
const SOCKET_URL = /localhost|127\.0\.0\.1/.test(window.location.hostname)
  ? "http://localhost:5000"
  : window.location.origin;

const DISPLAY_NAME_KEY = "live-coding-display-name";
const EDITOR_FONT_KEY = "live-coding-font-size";
const EDITOR_THEME_KEY = "live-coding-theme";
const CAMERA_DEVICE_KEY = "live-coding-camera-id";
const MIC_DEVICE_KEY = "live-coding-mic-id";
const SPEAKER_DEVICE_KEY = "live-coding-speaker-id";

const supportsAudioOutputSelect = () =>
  typeof HTMLMediaElement !== "undefined" &&
  typeof HTMLMediaElement.prototype.setSinkId === "function";

const applySinkId = async (el, deviceId) => {
  if (!el || !deviceId || typeof el.setSinkId !== "function") return;
  try {
    await el.setSinkId(deviceId);
  } catch (err) {
    console.warn("setSinkId failed:", err);
  }
};

const readStoredFontSize = () => {
  const n = Number(localStorage.getItem(EDITOR_FONT_KEY));
  return [12, 14, 16, 18, 20].includes(n) ? n : 14;
};

const readStoredTheme = (isAppDarkMode) => {
  const t = localStorage.getItem(EDITOR_THEME_KEY);
  if (t === CRAFTERHACK_LIGHT_THEME || t === CRAFTERHACK_DARK_THEME) return t;
  return isAppDarkMode ? CRAFTERHACK_DARK_THEME : CRAFTERHACK_LIGHT_THEME;
};

/**
 * Room chrome (header, editor toolbar, video cards, chat, ...) used to be
 * hardcoded to one dark palette regardless of the app's light/dark setting.
 * This builds the same style object shape from a small set of tokens instead,
 * so the whole page can follow isAppDarkMode.
 */
const getStyles = (isDark) => {
  const bgPage = isDark ? "#0f1419" : "#f7f9fb";
  const bgPanel = isDark ? "#161b22" : "#ffffff";
  const bgSunken = isDark ? "#0d1117" : "#f6f8fa";
  const bgStage = isDark ? "#010409" : "#eef2f5";
  const border = isDark ? "#30363d" : "rgba(15, 60, 70, 0.12)";
  const text = isDark ? "#e6edf3" : "#0f2a2b";
  const textBody = isDark ? "#c9d1d9" : "#1f2328";
  const muted = isDark ? "#8b949e" : "#5f6b73";
  const mutedFaint = isDark ? "#6e7681" : "#7c8a92";
  const scrim = isDark ? "rgba(1, 4, 9, 0.75)" : "rgba(15, 42, 43, 0.65)";
  const scrimLight = isDark ? "rgba(1, 4, 9, 0.72)" : "rgba(15, 42, 43, 0.55)";

  return {
    layout: {
      height: "calc(100dvh - 64px)",
      maxHeight: "calc(100dvh - 64px)",
      background: bgPage,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      background: bgPanel,
      borderBottom: `1px solid ${border}`,
      padding: "0 16px",
      height: 52,
      minHeight: 52,
      lineHeight: "52px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "nowrap",
      flexShrink: 0,
    },
    brand: {
      color: text,
      margin: 0,
      fontSize: 15,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      whiteSpace: "nowrap",
    },
    brandAccent: {
      color: "#137274",
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "nowrap",
      flexShrink: 0,
    },
    editorToolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      padding: "6px 12px",
      background: bgPanel,
      borderBottom: `1px solid ${border}`,
      flexShrink: 0,
      minHeight: 40,
    },
    editorToolbarLeft: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
    },
    editorToolbarRight: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexShrink: 0,
    },
    settingsRow: {
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },
    settingsLabel: {
      display: "block",
      color: muted,
      fontSize: 12,
      fontWeight: 600,
      marginBottom: 6,
      letterSpacing: "0.02em",
    },
    content: {
      padding: 0,
      flex: 1,
      minHeight: 0,
      height: "auto",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    mainRow: {
      flex: 1,
      display: "flex",
      minHeight: 0,
      overflow: "hidden",
    },
    editorPane: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      minHeight: 0,
      borderRight: `1px solid ${border}`,
      overflow: "hidden",
    },
    editorWrap: {
      flex: 1,
      minHeight: 0,
      position: "relative",
      overflow: "hidden",
    },
    screenStage: {
      flex: 1,
      minHeight: 0,
      position: "relative",
      background: bgStage,
      borderBottom: `1px solid ${border}`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    screenStageVideo: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      background: bgStage,
      display: "block",
    },
    screenStageBar: {
      position: "absolute",
      left: 12,
      top: 10,
      zIndex: 4,
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    editorShareBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "6px 12px",
      background: bgPanel,
      borderTop: `1px solid ${border}`,
      flexShrink: 0,
    },
    typingBadge: {
      position: "absolute",
      zIndex: 6,
      background: "#58a6ff",
      color: "#fff",
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 7px",
      borderRadius: 4,
      pointerEvents: "none",
      whiteSpace: "nowrap",
      boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
      transform: "translate(-2px, calc(-100% - 6px))",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "nowrap",
      minWidth: 0,
    },
    outputPanel: {
      borderTop: `1px solid ${border}`,
      background: bgSunken,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      minHeight: 0,
    },
    outputResizeHandle: {
      height: 6,
      cursor: "ns-resize",
      background: isDark ? "#21262d" : "#e8edf0",
      borderBottom: `1px solid ${border}`,
      flexShrink: 0,
      position: "relative",
    },
    sidePaneResizeHandle: {
      width: 6,
      cursor: "col-resize",
      background: isDark ? "#21262d" : "#e8edf0",
      borderLeft: `1px solid ${border}`,
      flexShrink: 0,
      position: "relative",
    },
    outputHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "6px 12px",
      flexShrink: 0,
    },
    outputBody: {
      padding: "0 14px 10px",
      overflow: "auto",
      flex: 1,
      minHeight: 0,
    },
    outputMeta: {
      color: muted,
      fontSize: 12,
    },
    outputText: {
      color: textBody,
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 13,
      whiteSpace: "pre-wrap",
      margin: 0,
    },
    sidePane: {
      width: 280,
      flexShrink: 0,
      background: bgPanel,
      display: "flex",
      flexDirection: "column",
      padding: 12,
      gap: 10,
      overflow: "auto",
      minHeight: 0,
    },
    videoCard: {
      position: "relative",
      background: bgSunken,
      borderRadius: 8,
      border: `1px solid ${border}`,
      overflow: "hidden",
      aspectRatio: "4 / 3",
    },
    videoEl: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "center",
      transform: "scaleX(-1)",
      background: bgStage,
      display: "block",
    },
    videoLabel: {
      position: "absolute",
      left: 8,
      top: 8,
      background: scrim,
      color: "#e6edf3",
      fontSize: 12,
      fontWeight: 600,
      padding: "3px 8px",
      borderRadius: 4,
      zIndex: 2,
      maxWidth: "72%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    videoControls: {
      position: "absolute",
      left: "50%",
      bottom: 8,
      transform: "translateX(-50%)",
      display: "flex",
      gap: 6,
      zIndex: 3,
      background: scrimLight,
      padding: "4px 8px",
      borderRadius: 20,
    },
    muteBadge: {
      position: "absolute",
      top: 8,
      right: 8,
      zIndex: 2,
    },
    chatPanel: {
      flex: 1,
      minHeight: 140,
      display: "flex",
      flexDirection: "column",
      borderRadius: 8,
      border: `1px solid ${border}`,
      background: bgSunken,
      overflow: "hidden",
    },
    chatHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 10px",
      borderBottom: `1px solid ${border}`,
      flexShrink: 0,
    },
    chatTitle: {
      color: text,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.02em",
      margin: 0,
    },
    chatList: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: "8px 10px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    chatBubbleMine: {
      alignSelf: "flex-end",
      maxWidth: "88%",
      background: isDark ? "rgba(19,114,116,0.28)" : "rgba(19,114,116,0.14)",
      border: "1px solid rgba(19,114,116,0.45)",
      borderRadius: "10px 10px 4px 10px",
      padding: "6px 9px",
    },
    chatBubblePeer: {
      alignSelf: "flex-start",
      maxWidth: "88%",
      background: bgPanel,
      border: `1px solid ${border}`,
      borderRadius: "10px 10px 10px 4px",
      padding: "6px 9px",
    },
    chatName: {
      display: "block",
      fontSize: 10,
      fontWeight: 700,
      color: muted,
      marginBottom: 2,
    },
    chatText: {
      color: text,
      fontSize: 12,
      lineHeight: 1.4,
      wordBreak: "break-word",
      whiteSpace: "pre-wrap",
    },
    chatTime: {
      display: "block",
      fontSize: 9,
      color: mutedFaint,
      marginTop: 3,
      textAlign: "right",
    },
    chatInputRow: {
      display: "flex",
      gap: 6,
      padding: 8,
      borderTop: `1px solid ${border}`,
      flexShrink: 0,
    },
    landing: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      padding: 24,
      background: `radial-gradient(700px 320px at 50% 0%, rgba(19,114,116,0.22), transparent 60%), ${bgPage}`,
    },
    landingCard: {
      width: "100%",
      maxWidth: 440,
      padding: "28px 28px 24px",
      borderRadius: 14,
      border: `1px solid ${border}`,
      background: bgPanel,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 14,
      textAlign: "center",
    },
    select: {
      width: 128,
    },
  };
};

/** One line per m= section: kind, direction, and whether a track/msid is present — for debugging one-way A/V. */
const summarizeSdp = (sdp) => {
  if (!sdp) return "(no sdp)";
  const lines = sdp.split("\r\n");
  const sections = [];
  let current = null;
  for (const line of lines) {
    if (line.startsWith("m=")) {
      if (current) sections.push(current);
      current = {
        kind: line.split(" ")[0].slice(2),
        direction: "?",
        hasMsid: false,
      };
    } else if (current) {
      if (/^a=(sendrecv|sendonly|recvonly|inactive)/.test(line)) {
        current.direction = line.slice(2);
      } else if (line.startsWith("a=msid:")) {
        current.hasMsid = true;
      }
    }
  }
  if (current) sections.push(current);
  return sections
    .map(
      (s) => `${s.kind}:${s.direction}${s.hasMsid ? "" : " (no msid/track)"}`,
    )
    .join(", ");
};

/** Short join chime via Web Audio API (no asset required). */
const playJoinSound = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [523.25, 659.25, 783.99];
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02 + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28 + i * 0.08);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.08);
      osc.stop(now + 0.35 + i * 0.08);
    });
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch (_) {
    /* ignore */
  }
};

/** Short two-note "pop" for an incoming chat message — distinct from the join chime. */
const playMessageSound = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [880, 1108.73];
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.09, now + 0.015 + i * 0.06);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16 + i * 0.06);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.06);
      osc.stop(now + 0.2 + i * 0.06);
    });
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch (_) {
    /* ignore */
  }
};

const REACTION_EMOJIS = ["👍", "❤️", "😂", "👏", "😢", "🎉"];
const REACTION_DURATION_MS = 2200;

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

/**
 * Virtual background (solid color / uploaded photo): draws the replacement
 * background first, then punches the BodyPix person-segmentation mask out of
 * a full video frame and composites just that foreground on top. Unlike
 * bodyPix's own drawMask (a flat translucent color overlay, not a true cutout)
 * this actually replaces the background pixels.
 */
const compositeSegmentedBackground = (canvas, video, segmentation, scratchRef, drawBackground) => {
  const ctx = canvas.getContext("2d");
  const w = canvas.width;
  const h = canvas.height;

  drawBackground(ctx, w, h);

  if (!scratchRef.current) {
    scratchRef.current = document.createElement("canvas");
  }
  const scratch = scratchRef.current;
  scratch.width = w;
  scratch.height = h;
  const sctx = scratch.getContext("2d");
  sctx.clearRect(0, 0, w, h);
  sctx.drawImage(video, 0, 0, w, h);

  const frame = sctx.getImageData(0, 0, w, h);
  const data = frame.data;
  const segW = segmentation.width;
  const segH = segmentation.height;
  const segData = segmentation.data;

  for (let y = 0; y < h; y++) {
    const sy = Math.min(segH - 1, (y * segH / h) | 0);
    const rowBase = sy * segW;
    for (let x = 0; x < w; x++) {
      const sx = Math.min(segW - 1, (x * segW / w) | 0);
      if (segData[rowBase + sx] !== 1) {
        data[(y * w + x) * 4 + 3] = 0;
      }
    }
  }
  sctx.putImageData(frame, 0, 0);
  ctx.drawImage(scratch, 0, 0);
};

/** Scroll-to-zoom + drag-to-pan for a video tile. The video itself stays a
 * plain <video> the rest of the app keeps setting srcObject on directly —
 * this only ever touches its CSS transform, never the element identity. */
const useZoomPan = () => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  const clampScale = (v) =>
    Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(v * 100) / 100));

  const applyScale = (next) => {
    setScale(next);
    if (next <= ZOOM_MIN) setOffset({ x: 0, y: 0 });
  };

  const zoomIn = () => applyScale(clampScale(scale + ZOOM_STEP));
  const zoomOut = () => applyScale(clampScale(scale - ZOOM_STEP));
  const reset = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  const onWheel = (e) => {
    // Require Ctrl/Cmd so a plain scroll over the video doesn't hijack the
    // page — browsers also mark trackpad pinch gestures as ctrlKey, so this
    // doubles as pinch-to-zoom support.
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    applyScale(clampScale(scale - Math.sign(e.deltaY) * ZOOM_STEP));
  };

  const onPointerDown = (e) => {
    if (scale <= ZOOM_MIN) return;
    draggingRef.current = true;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setOffset((o) => ({ x: o.x + dx, y: o.y + dy }));
  };
  const onPointerUp = () => {
    draggingRef.current = false;
  };

  return {
    scale,
    offset,
    zoomIn,
    zoomOut,
    reset,
    isZoomed: scale > ZOOM_MIN,
    handlers: { onWheel, onPointerDown, onPointerMove, onPointerUp },
  };
};

const ZoomControls = ({ zoom, style }) => (
  <div
    style={{
      position: "absolute",
      right: 8,
      bottom: 8,
      zIndex: 3,
      display: "flex",
      alignItems: "center",
      gap: 4,
      background: "rgba(15, 23, 26, 0.65)",
      borderRadius: 20,
      padding: "3px 4px",
      ...style,
    }}
    // Keep drag/wheel-zoom gestures on the video from also firing through
    // these buttons.
    onWheel={(e) => e.stopPropagation()}
    onPointerDown={(e) => e.stopPropagation()}
  >
    {zoom.isZoomed && (
      <Tooltip title="Reset zoom">
        <Button
          size="small"
          shape="circle"
          icon={<UndoOutlined />}
          onClick={zoom.reset}
        />
      </Tooltip>
    )}
    <Tooltip title="Zoom out">
      <Button
        size="small"
        shape="circle"
        icon={<MinusOutlined />}
        disabled={zoom.scale <= ZOOM_MIN}
        onClick={zoom.zoomOut}
      />
    </Tooltip>
    <span style={{ color: "#fff", fontSize: 11, minWidth: 32, textAlign: "center" }}>
      {Math.round(zoom.scale * 100)}%
    </span>
    <Tooltip title="Zoom in">
      <Button
        size="small"
        shape="circle"
        icon={<PlusOutlined />}
        disabled={zoom.scale >= ZOOM_MAX}
        onClick={zoom.zoomIn}
      />
    </Tooltip>
  </div>
);

/**
 * Non-executable editor modes, kept local to the live-coding room rather
 * than in the shared compilerLanguages list — that list also drives the
 * IDE page's per-language landing routes and the problem-solve dropdown,
 * neither of which should suddenly grow a "Plain Text" URL/entry.
 */
const NOTE_LANGUAGES = [
  { id: "plaintext", label: "Plain Text", monaco: "plaintext", starter: "" },
  { id: "markdown", label: "Markdown", monaco: "markdown", starter: "" },
];
const isNoteLanguage = (id) => NOTE_LANGUAGES.some((lang) => lang.id === id);
const getNoteLanguage = (id) => NOTE_LANGUAGES.find((lang) => lang.id === id);

// Virtual-background solid-color presets (Meet-style plain backdrops).
const BG_COLORS = [
  "#137274",
  "#0f2a2b",
  "#1971c2",
  "#495057",
  "#2f9e44",
  "#e03131",
];

const WB_COLORS = [
  "#137274",
  "#e03131",
  "#1971c2",
  "#2f9e44",
  "#f08c00",
  "#000000",
  "#ffffff",
];
const WB_WIDTHS = [
  { label: "Thin", value: 2, dot: 6 },
  { label: "Medium", value: 4, dot: 10 },
  { label: "Thick", value: 8, dot: 14 },
  { label: "Extra thick", value: 14, dot: 18 },
  { label: "Huge", value: 24, dot: 22 },
];
const WB_FONT_OPTIONS = [
  { label: "Sans-serif", value: "Arial, Helvetica, sans-serif" },
  { label: "Serif", value: "Georgia, 'Times New Roman', serif" },
  { label: "Monospace", value: "'Courier New', Courier, monospace" },
  { label: "Handwritten", value: "'Comic Sans MS', 'Comic Sans', cursive" },
];
const WB_DEFAULT_FONT_FAMILY = WB_FONT_OPTIONS[0].value;
// The whiteboard "page" is bigger than the visible panel and scrolls/zooms
// within it (like a real drawing canvas), instead of the drawable area just
// being whatever size the panel happens to be. Every stroke/shape/text
// coordinate is normalized against these FIXED base dimensions — never
// against the page's current (possibly since-extended) size — specifically
// so that "make the page bigger" only ever adds blank room beyond the
// existing content's edge, and never shifts/rescales anything already
// drawn (which is what would happen if positions were normalized against a
// size that keeps changing).
const WB_PAGE_BASE_WIDTH = 2400;
const WB_PAGE_BASE_HEIGHT = 1600;
const WB_PAGE_GROW_STEP = 900;
const WB_PAGE_MAX_WIDTH = 7200;
const WB_PAGE_MAX_HEIGHT = 6400;
const WB_ZOOM_MIN = 0.25;
const WB_ZOOM_MAX = 2.5;
const WB_ZOOM_STEP = 0.15;

/** Antd has no eraser glyph — a small two-tone eraser body reads clearly at
 * icon size without being confused with Clear/Delete. */
const EraserIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    style={{ verticalAlign: "-0.125em" }}
  >
    <g transform="rotate(-20 12 12)">
      <rect
        x="5"
        y="8.5"
        width="14"
        height="9"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <line x1="5" y1="13" x2="19" y2="13" stroke="currentColor" strokeWidth="1.6" />
    </g>
  </svg>
);

const WidthDot = ({ size }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 26,
      height: 26,
    }}
  >
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "currentColor",
      }}
    />
  </span>
);

const MAIN_PANE_TABS = [
  { key: "editor", label: "Editor", icon: <CodeOutlined /> },
  { key: "whiteboard", label: "Whiteboard", icon: <HighlightOutlined /> },
];

const WB_TOOL_ICON_STYLE = { fontSize: 20, display: "inline-flex", padding: "2px 4px" };

const WB_TOOL_OPTIONS = [
  {
    value: "pen",
    label: (
      <Tooltip title="Pen">
        <span style={WB_TOOL_ICON_STYLE}><EditOutlined /></span>
      </Tooltip>
    ),
  },
  {
    value: "highlighter",
    label: (
      <Tooltip title="Highlighter">
        <span style={WB_TOOL_ICON_STYLE}><HighlightOutlined /></span>
      </Tooltip>
    ),
  },
  {
    value: "eraser",
    label: (
      <Tooltip title="Eraser">
        <span style={WB_TOOL_ICON_STYLE}><EraserIcon /></span>
      </Tooltip>
    ),
  },
  {
    value: "text",
    label: (
      <Tooltip title="Text">
        <span style={WB_TOOL_ICON_STYLE}><FontSizeOutlined /></span>
      </Tooltip>
    ),
  },
  {
    value: "select",
    label: (
      <Tooltip title="Select & move">
        <span style={WB_TOOL_ICON_STYLE}><SelectOutlined /></span>
      </Tooltip>
    ),
  },
];

// Antd has no shape glyphs matching this style, so these are small custom
// outlines — kept visually consistent with EraserIcon above.
const RectangleIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <rect x="4" y="6" width="16" height="12" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const EllipseIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <ellipse cx="12" cy="12" rx="8" ry="6" fill="none" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const LineIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <line x1="4" y1="20" x2="18" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M11 6 H18 V13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const DoubleArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M12 5 H19 V12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 19 H5 V12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
// A bendable line: start/end plus a 3rd "mid" handle the user can drag
// anywhere to tilt/bend it — see the `zigzag` shape type below.
const ZigzagIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <polyline
      points="4,19 15,15 9,9 20,5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DiamondIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <polygon points="12,4 20,12 12,20 4,12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const TriangleIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <polygon points="12,4 20,20 4,20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const ParallelogramIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <polygon points="8,5 21,5 16,19 3,19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const HexagonIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <polygon points="12,4 19,8 19,16 12,20 5,16 5,8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const CylinderIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <path d="M4 6 L4 18 A8 3 0 0 0 20 18 L20 6" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <ellipse cx="12" cy="6" rx="8" ry="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const CloudIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <path
      d="M6.5 18 C3.5 18 3 13.5 6 12.8 C5.5 8.5 11.5 7 13 10.3 C16 8.5 19.5 11 18 14.2 C21 14.8 20 18 17.5 18 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);
const StarIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ verticalAlign: "-0.125em" }}>
    <polygon
      points="12,2 14.59,8.36 21.51,8.82 16.24,13.14 18,20.18 12,16.35 6,20.18 7.76,13.14 2.49,8.82 9.41,8.36"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const SHAPE_TOOL_OPTIONS = [
  { value: "rectangle", label: "Rectangle", icon: <RectangleIcon /> },
  { value: "ellipse", label: "Ellipse", icon: <EllipseIcon /> },
  { value: "diamond", label: "Diamond", icon: <DiamondIcon /> },
  { value: "triangle", label: "Triangle", icon: <TriangleIcon /> },
  { value: "parallelogram", label: "Parallelogram", icon: <ParallelogramIcon /> },
  { value: "hexagon", label: "Hexagon", icon: <HexagonIcon /> },
  { value: "cylinder", label: "Database", icon: <CylinderIcon /> },
  { value: "cloud", label: "Cloud", icon: <CloudIcon /> },
  { value: "star", label: "Star", icon: <StarIcon /> },
  { value: "line", label: "Line", icon: <LineIcon /> },
  { value: "arrow", label: "Arrow", icon: <ArrowIcon /> },
  { value: "doubleArrow", label: "Double arrow", icon: <DoubleArrowIcon /> },
  { value: "zigzag", label: "Bendable line", icon: <ZigzagIcon /> },
];
const SHAPE_TOOL_VALUES = new Set(SHAPE_TOOL_OPTIONS.map((s) => s.value));
// Shapes whose geometry is just an axis-aligned bounding box (x1,y1)-(x2,y2)
// — these all share the same 4-corner nw/ne/sw/se resize handles and, for
// z-order click-to-cycle hit-testing, are approximated by that box (exact
// per-shape polygon hit-testing isn't worth it for a "which one's on top"
// check).
const BOX_SHAPE_TYPES = new Set([
  "rectangle",
  "ellipse",
  "diamond",
  "triangle",
  "parallelogram",
  "hexagon",
  "cylinder",
  "cloud",
  "star",
]);
// Number of independently-draggable bend points along a zigzag/bendable
// line, between its fixed start and end — gives a few tilt points along its
// length rather than just one.
const ZIGZAG_BEND_COUNT = 3;

/** Triangle points for an arrowhead at (tipX,tipY), pointing along angleRad. */
const arrowHeadPoints = (tipX, tipY, angleRad, size) => {
  const spread = Math.PI / 7;
  const p1x = tipX - size * Math.cos(angleRad - spread);
  const p1y = tipY - size * Math.sin(angleRad - spread);
  const p2x = tipX - size * Math.cos(angleRad + spread);
  const p2y = tipY - size * Math.sin(angleRad + spread);
  return `${tipX},${tipY} ${p1x},${p1y} ${p2x},${p2y}`;
};

/** A rounded cloud-blob outline scaled to fit the given bounding box. */
const cloudPathD = (minX, minY, maxX, maxY) => {
  const w = maxX - minX;
  const h = maxY - minY;
  const X = (f) => minX + f * w;
  const Y = (f) => minY + f * h;
  return `M ${X(0.18)} ${Y(0.75)}
    C ${X(-0.05)} ${Y(0.75)} ${X(-0.05)} ${Y(0.4)} ${X(0.2)} ${Y(0.38)}
    C ${X(0.18)} ${Y(0.1)} ${X(0.55)} ${Y(0.02)} ${X(0.65)} ${Y(0.28)}
    C ${X(0.85)} ${Y(0.12)} ${X(1.08)} ${Y(0.4)} ${X(0.88)} ${Y(0.55)}
    C ${X(1.05)} ${Y(0.6)} ${X(1.0)} ${Y(0.85)} ${X(0.8)} ${Y(0.85)}
    L ${X(0.2)} ${Y(0.85)}
    C ${X(0.02)} ${Y(0.85)} ${X(0.0)} ${Y(0.78)} ${X(0.18)} ${Y(0.75)}
    Z`;
};

/** A 5-point star polygon inscribed in the given bounding box. */
const starPolygonPoints = (minX, minY, maxX, maxY) => {
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const outerRx = (maxX - minX) / 2;
  const outerRy = (maxY - minY) / 2;
  const innerRatio = 0.42;
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? 1 : innerRatio;
    pts.push(`${cx + outerRx * r * Math.cos(angle)},${cy + outerRy * r * Math.sin(angle)}`);
  }
  return pts.join(" ");
};

const SHAPE_RESIZE_HANDLE_STYLE = {
  fill: "#137274",
  stroke: "#fff",
  strokeWidth: 1.5,
};

const WB_WIDTH_OPTIONS = WB_WIDTHS.map((w) => ({
  value: w.value,
  label: (
    <Tooltip title={w.label}>
      <span><WidthDot size={w.dot} /></span>
    </Tooltip>
  ),
}));

/**
 * Shared canvas — freehand drawing + click-to-place text, synced live over
 * the room's socket the same way code_update is (stream points as they're
 * drawn, not periodic full-canvas snapshots). Coordinates are normalized
 * (0..1) so two clients with differently sized windows still agree on where
 * a stroke landed. Stays mounted for the whole room session (just hidden via
 * `active`) so local state survives switching the main pane back to the
 * editor and back — the server is still the source of truth on (re)mount via
 * wb_request_state.
 */
const Whiteboard = ({ socketRef, roomId, active, viewOnly, isDark }) => {
  // containerRef: the fixed-size "page" (pageSize.width x pageSize.height,
  // CSS-scaled by `zoom`) — canvas/SVG/text all live inside it in
  // unscaled page-space coordinates. viewportRef: the surrounding panel
  // that actually scrolls (overflow: auto) to pan around the page.
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const canvasRef = useRef(null);
  // Kept in sync with the dpr actually baked into the canvas's pixel
  // buffer (see the sizing effect) so drawSegment can convert fixed-base
  // normalized coordinates straight to buffer pixels without re-deriving
  // it from canvas.width, which would create a rounding feedback loop.
  const canvasDprRef = useRef(1);
  const strokesRef = useRef([]); // finalized strokes: { id, points:[{x,y}], color, width, tool }
  const remoteActiveRef = useRef(new Map()); // id -> in-progress stroke from peer
  const myStrokeRef = useRef(null); // my own in-progress stroke
  const drawingRef = useRef(false);

  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(WB_COLORS[0]);
  const [strokeWidth, setStrokeWidth] = useState(WB_WIDTHS[1].value);
  const [fontFamily, setFontFamily] = useState(WB_DEFAULT_FONT_FAMILY);
  const [texts, setTexts] = useState([]); // { id, x, y, text, color, fontSize }
  const [draftPos, setDraftPos] = useState(null); // { id, x, y } — text box currently being edited
  const [draftText, setDraftText] = useState("");
  // Shapes are kept as editable objects (not baked into the raster canvas)
  // so they can be selected, moved, and resized after the fact — rendered as
  // an SVG overlay on top of the canvas rather than drawn pixels.
  const [shapes, setShapes] = useState([]); // { id, type, x1,y1,x2,y2 (normalized), color, width }
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  // Selecting a text box (select tool only — the text tool's click still
  // opens it straight for editing) so Delete/Backspace can remove it the
  // same way it already does for shapes.
  const [selectedTextId, setSelectedTextId] = useState(null);
  // The page's current drawable size — starts at the base and can only grow
  // (via growPageWidth/growPageHeight below), independent of the visible
  // panel's actual size.
  const [pageSize, setPageSize] = useState({
    width: WB_PAGE_BASE_WIDTH,
    height: WB_PAGE_BASE_HEIGHT,
  });
  const [zoom, setZoom] = useState(1);
  const shapeDragRef = useRef(null); // { mode: 'create'|'move'|'resize', id, handle, startX, startY, orig }
  // A resize handle is a tiny (10px) target — once the pointer moves past its
  // edge mid-drag, setPointerCapture doesn't reliably keep delivering
  // pointermove there, so the drag silently stops tracking. Window-level
  // listeners (added below) avoid depending on capture/hit-testing at all.
  const [isDraggingShape, setIsDraggingShape] = useState(false);

  // Undo/redo — a command-style history local to this tab, not a full-state
  // snapshot. Each entry describes ONE invertible operation (e.g. "this exact
  // stroke was added", "this shape moved from A to B") and undo/redo replays
  // just that entry via the same granular add/update/delete events already
  // used for live sync. That matters for correctness in a 2-person room: if
  // this used a whole-board snapshot instead, undoing something from 5
  // minutes ago would silently wipe out anything the peer drew in between —
  // the command-based approach only ever touches the specific item(s) *this*
  // action affected, so concurrent edits from the peer are never clobbered.
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const MAX_WB_HISTORY = 50;

  const getCtx = () => canvasRef.current?.getContext("2d");

  const drawSegment = useCallback((from, to, segColor, segWidth, segTool) => {
    const ctx = getCtx();
    if (!ctx) return;
    ctx.save();
    if (segTool === "eraser") {
      ctx.globalCompositeOperation = "destination-out";
    } else if (segTool === "highlighter") {
      // Translucent + multiply so overlapping strokes darken like a real
      // marker instead of just stacking flat opaque color.
      ctx.globalCompositeOperation = "multiply";
      ctx.globalAlpha = 0.4;
    } else {
      ctx.globalCompositeOperation = "source-over";
    }
    ctx.strokeStyle = segColor;
    // A "thin" eraser at pen width is barely usable — eraser strokes render
    // 3x the selected width so every tier stays big enough to actually erase.
    ctx.lineWidth =
      segTool === "highlighter"
        ? segWidth * 2
        : segTool === "eraser"
          ? segWidth * 3
          : segWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    // Points are normalized against the fixed page base, not canvas.width —
    // canvas.width tracks the page's current (possibly extended) size, and
    // multiplying against that directly would shift every existing stroke
    // whenever the page is made bigger. canvasDprRef converts to the actual
    // pixel buffer, which is DPR-scaled independent of the page's own size.
    const dpr = canvasDprRef.current;
    ctx.moveTo(from.x * WB_PAGE_BASE_WIDTH * dpr, from.y * WB_PAGE_BASE_HEIGHT * dpr);
    ctx.lineTo(to.x * WB_PAGE_BASE_WIDTH * dpr, to.y * WB_PAGE_BASE_HEIGHT * dpr);
    ctx.stroke();
    ctx.restore();
  }, []);

  const redrawAll = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const stroke of strokesRef.current) {
      for (let i = 1; i < stroke.points.length; i++) {
        drawSegment(
          stroke.points[i - 1],
          stroke.points[i],
          stroke.color,
          stroke.width,
          stroke.tool,
        );
      }
      if (stroke.points.length === 1) {
        drawSegment(
          stroke.points[0],
          stroke.points[0],
          stroke.color,
          stroke.width,
          stroke.tool,
        );
      }
    }
  }, [drawSegment]);

  // Keep the canvas bitmap's pixel buffer in sync with the page's own size
  // (devicePixelRatio-aware) — unlike before, this is no longer tied to the
  // visible panel's size (a ResizeObserver on it), since the page can now be
  // bigger than the panel and scroll/zoom within it. Only changes when the
  // page itself is made bigger.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvasDprRef.current = dpr;
    canvas.width = Math.max(1, Math.round(pageSize.width * dpr));
    canvas.height = Math.max(1, Math.round(pageSize.height * dpr));
    redrawAll();
  }, [pageSize, redrawAll]);

  // (Re)hydrate from the server whenever the panel becomes active — covers
  // both the first mount and switching back after being away.
  useEffect(() => {
    if (!active) return undefined;
    const socket = socketRef.current;
    if (!socket || !roomId) return undefined;

    const onState = (wb) => {
      strokesRef.current = Array.isArray(wb?.strokes) ? wb.strokes : [];
      setTexts(Array.isArray(wb?.texts) ? wb.texts : []);
      setShapes(Array.isArray(wb?.shapes) ? wb.shapes : []);
      redrawAll();
      // A fresh hydration (mount, tab switch back, reconnect) can hand us
      // board content our local history never saw come in — undo/redo
      // entries from before it would target ids that may no longer make
      // sense, so start clean rather than risk a confusing replay.
      undoStackRef.current = [];
      redoStackRef.current = [];
      setCanUndo(false);
      setCanRedo(false);
    };
    socket.on("wb_state", onState);
    socket.emit("wb_request_state", { roomId });
    return () => socket.off("wb_state", onState);
  }, [active, socketRef, roomId, redrawAll]);

  // Remote drawing — applied via imperative refs so the parent's single
  // centralized socket listener block can forward events here without this
  // component needing to independently manage subscription timing.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;

    const onStrokeStart = (stroke) => {
      remoteActiveRef.current.set(stroke.id, stroke);
      const p = stroke.points[0];
      drawSegment(p, p, stroke.color, stroke.width, stroke.tool);
    };
    const onStrokePoint = ({ id, x, y }) => {
      const stroke = remoteActiveRef.current.get(id);
      if (!stroke) return;
      const last = stroke.points[stroke.points.length - 1];
      const pos = { x, y };
      stroke.points.push(pos);
      drawSegment(last, pos, stroke.color, stroke.width, stroke.tool);
    };
    const onStrokeEnd = ({ id }) => {
      const stroke = remoteActiveRef.current.get(id);
      remoteActiveRef.current.delete(id);
      if (stroke) strokesRef.current.push(stroke);
    };
    const onText = (entry) => {
      setTexts((prev) => {
        const idx = prev.findIndex((t) => t.id === entry.id);
        if (!entry.text) return idx === -1 ? prev : prev.filter((t) => t.id !== entry.id);
        if (idx === -1) return [...prev, entry];
        const next = [...prev];
        next[idx] = entry;
        return next;
      });
    };
    const onClear = () => {
      strokesRef.current = [];
      remoteActiveRef.current.clear();
      setTexts([]);
      setShapes([]);
      setSelectedShapeId(null);
      redrawAll();
      // The peer wiped the board — any local undo/redo entries now refer to
      // content that's gone, so drop them rather than let undo resurrect
      // stuff the peer just deliberately cleared.
      undoStackRef.current = [];
      redoStackRef.current = [];
      setCanUndo(false);
      setCanRedo(false);
    };
    const onShapeUpsert = (shape) => {
      setShapes((prev) => {
        const idx = prev.findIndex((s) => s.id === shape.id);
        if (idx === -1) return [...prev, shape];
        const next = [...prev];
        next[idx] = shape;
        return next;
      });
    };
    const onShapeDelete = ({ id }) => {
      setShapes((prev) => prev.filter((s) => s.id !== id));
      setSelectedShapeId((prev) => (prev === id ? null : prev));
    };
    // Peer undid one of their own strokes, or redid an "undo clear" of ours.
    const onStrokeDelete = ({ id }) => {
      strokesRef.current = strokesRef.current.filter((s) => s.id !== id);
      redrawAll();
    };
    // Peer redid a stroke they'd undone, or undid a clear that removed it.
    const onStrokeRestore = (stroke) => {
      if (!stroke?.id) return;
      if (strokesRef.current.some((s) => s.id === stroke.id)) return;
      strokesRef.current = [...strokesRef.current, stroke];
      redrawAll();
    };

    socket.on("wb_stroke_start", onStrokeStart);
    socket.on("wb_stroke_point", onStrokePoint);
    socket.on("wb_stroke_end", onStrokeEnd);
    socket.on("wb_stroke_delete", onStrokeDelete);
    socket.on("wb_stroke_restore", onStrokeRestore);
    socket.on("wb_text", onText);
    socket.on("wb_clear", onClear);
    socket.on("wb_shape_upsert", onShapeUpsert);
    socket.on("wb_shape_delete", onShapeDelete);
    return () => {
      socket.off("wb_stroke_start", onStrokeStart);
      socket.off("wb_stroke_point", onStrokePoint);
      socket.off("wb_stroke_end", onStrokeEnd);
      socket.off("wb_stroke_delete", onStrokeDelete);
      socket.off("wb_stroke_restore", onStrokeRestore);
      socket.off("wb_text", onText);
      socket.off("wb_clear", onClear);
      socket.off("wb_shape_upsert", onShapeUpsert);
      socket.off("wb_shape_delete", onShapeDelete);
    };
  }, [socketRef, drawSegment, redrawAll]);

  // Normalized against the FIXED page base (not the panel, not the page's
  // current — possibly extended — size), so content position is stable
  // across zoom, scroll, and page-size changes. rect.left/top already
  // account for scroll position and the current CSS transform scale
  // (getBoundingClientRect reports the post-transform box), so dividing by
  // `zoom` here (rather than rect.width) is what keeps this correct
  // regardless of how far zoomed in/out the page currently is.
  const toNorm = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left) / zoom / WB_PAGE_BASE_WIDTH,
      y: (clientY - rect.top) / zoom / WB_PAGE_BASE_HEIGHT,
    };
  };

  // --- Undo / redo -----------------------------------------------------
  // See the field comment on undoStackRef for why this replays granular
  // operations instead of restoring whole-board snapshots.
  const pushHistoryEntry = (entry) => {
    undoStackRef.current.push(entry);
    if (undoStackRef.current.length > MAX_WB_HISTORY) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  };

  const applyHistoryEntry = (entry, direction) => {
    const undoing = direction === "undo";
    switch (entry.type) {
      case "stroke": {
        if (undoing) {
          strokesRef.current = strokesRef.current.filter((s) => s.id !== entry.stroke.id);
          redrawAll();
          socketRef.current?.emit("wb_stroke_delete", { roomId, id: entry.stroke.id });
        } else {
          strokesRef.current = [...strokesRef.current, entry.stroke];
          redrawAll();
          socketRef.current?.emit("wb_stroke_restore", { roomId, stroke: entry.stroke });
        }
        break;
      }
      case "shape-create": {
        if (undoing) {
          setShapes((prev) => prev.filter((s) => s.id !== entry.shape.id));
          setSelectedShapeId((prev) => (prev === entry.shape.id ? null : prev));
          socketRef.current?.emit("wb_shape_delete", { roomId, id: entry.shape.id });
        } else {
          setShapes((prev) => [...prev, entry.shape]);
          socketRef.current?.emit("wb_shape_upsert", { roomId, ...entry.shape });
        }
        break;
      }
      case "shape-delete": {
        if (undoing) {
          setShapes((prev) => [...prev, entry.shape]);
          socketRef.current?.emit("wb_shape_upsert", { roomId, ...entry.shape });
        } else {
          setShapes((prev) => prev.filter((s) => s.id !== entry.shape.id));
          setSelectedShapeId((prev) => (prev === entry.shape.id ? null : prev));
          socketRef.current?.emit("wb_shape_delete", { roomId, id: entry.shape.id });
        }
        break;
      }
      case "shape-transform": {
        const coords = undoing ? entry.before : entry.after;
        setShapes((prev) => {
          const next = prev.map((s) => (s.id === entry.id ? { ...s, ...coords } : s));
          const updated = next.find((s) => s.id === entry.id);
          if (updated) socketRef.current?.emit("wb_shape_upsert", { roomId, ...updated });
          return next;
        });
        break;
      }
      case "text": {
        const target = undoing ? entry.before : entry.after;
        setTexts((prev) => {
          const idx = prev.findIndex((t) => t.id === entry.id);
          if (!target) return idx === -1 ? prev : prev.filter((t) => t.id !== entry.id);
          if (idx === -1) return [...prev, target];
          const next = [...prev];
          next[idx] = target;
          return next;
        });
        socketRef.current?.emit(
          "wb_text",
          target ? { roomId, ...target } : { roomId, id: entry.id, x: entry.x, y: entry.y, text: "" },
        );
        break;
      }
      case "clear": {
        if (undoing) {
          // Restore by merging back in — never removes anything the peer
          // may have added since the clear, unlike a wholesale overwrite.
          const existingStrokeIds = new Set(strokesRef.current.map((s) => s.id));
          const restoreStrokes = entry.strokes.filter((s) => !existingStrokeIds.has(s.id));
          if (restoreStrokes.length) {
            strokesRef.current = [...strokesRef.current, ...restoreStrokes];
            redrawAll();
            restoreStrokes.forEach((s) =>
              socketRef.current?.emit("wb_stroke_restore", { roomId, stroke: s }),
            );
          }
          setShapes((prev) => {
            const existingIds = new Set(prev.map((s) => s.id));
            const restore = entry.shapes.filter((s) => !existingIds.has(s.id));
            restore.forEach((s) => socketRef.current?.emit("wb_shape_upsert", { roomId, ...s }));
            return restore.length ? [...prev, ...restore] : prev;
          });
          setTexts((prev) => {
            const existingIds = new Set(prev.map((t) => t.id));
            const restore = entry.texts.filter((t) => !existingIds.has(t.id));
            restore.forEach((t) => socketRef.current?.emit("wb_text", { roomId, ...t }));
            return restore.length ? [...prev, ...restore] : prev;
          });
        } else {
          // Redo the clear — remove exactly the ids it removed before.
          const strokeIds = new Set(entry.strokes.map((s) => s.id));
          strokesRef.current = strokesRef.current.filter((s) => !strokeIds.has(s.id));
          redrawAll();
          entry.strokes.forEach((s) =>
            socketRef.current?.emit("wb_stroke_delete", { roomId, id: s.id }),
          );

          const shapeIds = new Set(entry.shapes.map((s) => s.id));
          setShapes((prev) => prev.filter((s) => !shapeIds.has(s.id)));
          entry.shapes.forEach((s) =>
            socketRef.current?.emit("wb_shape_delete", { roomId, id: s.id }),
          );

          const textIds = new Set(entry.texts.map((t) => t.id));
          setTexts((prev) => prev.filter((t) => !textIds.has(t.id)));
          entry.texts.forEach((t) =>
            socketRef.current?.emit("wb_text", { roomId, id: t.id, x: t.x, y: t.y, text: "" }),
          );
        }
        break;
      }
      default:
        break;
    }
  };

  const undo = () => {
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    applyHistoryEntry(entry, "undo");
    redoStackRef.current.push(entry);
    if (redoStackRef.current.length > MAX_WB_HISTORY) redoStackRef.current.shift();
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
  };

  const redo = () => {
    const entry = redoStackRef.current.pop();
    if (!entry) return;
    applyHistoryEntry(entry, "redo");
    undoStackRef.current.push(entry);
    if (undoStackRef.current.length > MAX_WB_HISTORY) undoStackRef.current.shift();
    setCanRedo(redoStackRef.current.length > 0);
    setCanUndo(true);
  };

  // --- Shape hit-testing (for z-order click-to-cycle selection) --------
  // px, py must already be in page-space (unscaled, zoom-divided-out) px —
  // see the callers.
  const hitTestShapePoint = (shape, px, py) => {
    const x1 = shape.x1 * WB_PAGE_BASE_WIDTH;
    const y1 = shape.y1 * WB_PAGE_BASE_HEIGHT;
    const x2 = shape.x2 * WB_PAGE_BASE_WIDTH;
    const y2 = shape.y2 * WB_PAGE_BASE_HEIGHT;
    if (shape.type === "ellipse") {
      const cx = (x1 + x2) / 2;
      const cy = (y1 + y2) / 2;
      const rx = Math.abs(x2 - x1) / 2 || 1;
      const ry = Math.abs(y2 - y1) / 2 || 1;
      const nx = (px - cx) / rx;
      const ny = (py - cy) / ry;
      return nx * nx + ny * ny <= 1;
    }
    const distToSegment = (ax, ay, bx, by) => {
      const dx = bx - ax;
      const dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      let t = lenSq === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      const projX = ax + t * dx;
      const projY = ay + t * dy;
      return Math.hypot(px - projX, py - projY);
    };
    const tolerance = Math.max(shape.width || 3, 16) / 2;
    if (shape.type === "zigzag") {
      const bendPts = (shape.bends || []).map((p) => ({
        x: p.x * WB_PAGE_BASE_WIDTH,
        y: p.y * WB_PAGE_BASE_HEIGHT,
      }));
      const pts = [{ x: x1, y: y1 }, ...bendPts, { x: x2, y: y2 }];
      for (let i = 1; i < pts.length; i++) {
        if (distToSegment(pts[i - 1].x, pts[i - 1].y, pts[i].x, pts[i].y) <= tolerance) return true;
      }
      return false;
    }
    if (shape.type === "line" || shape.type === "arrow" || shape.type === "doubleArrow") {
      return distToSegment(x1, y1, x2, y2) <= tolerance;
    }
    // rectangle + all other box-like shapes (diamond, triangle,
    // parallelogram, hexagon, cylinder, cloud, star) — approximated by
    // their bounding box, see BOX_SHAPE_TYPES.
    return (
      px >= Math.min(x1, x2) &&
      px <= Math.max(x1, x2) &&
      py >= Math.min(y1, y2) &&
      py <= Math.max(y1, y2)
    );
  };

  const onPointerDown = (e) => {
    if (viewOnly) return;
    // The canvas itself can't hold focus, so a real (trusted) mousedown's
    // default action is to blur whatever currently has focus — which fires
    // AFTER this handler returns and would immediately blur the text input
    // we're about to create and focus below, before the user can type a
    // single character. preventDefault() suppresses that native blur.
    e.preventDefault();
    const pos = toNorm(e.clientX, e.clientY);

    if (tool === "text") {
      // Clicking elsewhere on the canvas while a draft is already open would
      // otherwise just silently discard it: the preventDefault above (needed
      // to stop the browser blurring the input the instant it's created)
      // also means the browser never blurs THIS click's previous draft
      // either, so React just repositions the same input and wipes its
      // value — commit whatever was already being typed first.
      if (draftPos) commitDraftText();
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setDraftPos({ id, x: pos.x, y: pos.y, isNew: true });
      setDraftText("");
      return;
    }

    if (tool === "select") {
      // Clicking empty canvas (not a shape/text — those handle their own
      // pointerdown/click separately) clears the current selection.
      setSelectedShapeId(null);
      setSelectedTextId(null);
      return;
    }

    if (SHAPE_TOOL_VALUES.has(tool)) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const shape = { id, type: tool, x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y, color, width: strokeWidth };
      shapeDragRef.current = { mode: "create", id };
      setShapes((prev) => [...prev, shape]);
      socketRef.current?.emit("wb_shape_upsert", { roomId, ...shape });
      e.currentTarget.setPointerCapture?.(e.pointerId);
      return;
    }

    drawingRef.current = true;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const stroke = { id, points: [pos], color, width: strokeWidth, tool };
    myStrokeRef.current = stroke;
    drawSegment(pos, pos, color, strokeWidth, tool);
    socketRef.current?.emit("wb_stroke_start", {
      roomId,
      id,
      x: pos.x,
      y: pos.y,
      color,
      width: strokeWidth,
      tool,
    });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (shapeDragRef.current?.mode === "create") {
      const pos = toNorm(e.clientX, e.clientY);
      const { id } = shapeDragRef.current;
      setShapes((prev) => {
        const next = prev.map((s) => (s.id === id ? { ...s, x2: pos.x, y2: pos.y } : s));
        const updated = next.find((s) => s.id === id);
        if (updated) socketRef.current?.emit("wb_shape_upsert", { roomId, ...updated });
        return next;
      });
      return;
    }

    if (!drawingRef.current || !myStrokeRef.current) return;
    const pos = toNorm(e.clientX, e.clientY);
    const stroke = myStrokeRef.current;
    const last = stroke.points[stroke.points.length - 1];
    stroke.points.push(pos);
    drawSegment(last, pos, stroke.color, stroke.width, stroke.tool);
    socketRef.current?.emit("wb_stroke_point", {
      roomId,
      id: stroke.id,
      x: pos.x,
      y: pos.y,
    });
  };

  const onPointerUp = () => {
    if (shapeDragRef.current?.mode === "create") {
      const { id } = shapeDragRef.current;
      shapeDragRef.current = null;
      const current = shapes.find((s) => s.id === id);
      if (!current) return;
      // Normalize corners so resize handles have an unambiguous nw/ne/sw/se
      // mapping — but only for box shapes. Line/arrow endpoints are
      // directional (arrowheads point from x1,y1 to x2,y2) so swapping them
      // would flip which end the arrowhead is on.
      let finalShape = current;
      if (BOX_SHAPE_TYPES.has(current.type)) {
        finalShape = {
          ...current,
          x1: Math.min(current.x1, current.x2),
          y1: Math.min(current.y1, current.y2),
          x2: Math.max(current.x1, current.x2),
          y2: Math.max(current.y1, current.y2),
        };
      } else if (current.type === "zigzag") {
        // The bend handles start evenly spaced along the segment — only
        // meaningful once the endpoints are final, so set them here rather
        // than at creation start.
        finalShape = {
          ...current,
          bends: Array.from({ length: ZIGZAG_BEND_COUNT }, (_, i) => {
            const t = (i + 1) / (ZIGZAG_BEND_COUNT + 1);
            return {
              x: current.x1 + (current.x2 - current.x1) * t,
              y: current.y1 + (current.y2 - current.y1) * t,
            };
          }),
        };
      }
      setShapes((prev) => prev.map((s) => (s.id === id ? finalShape : s)));
      socketRef.current?.emit("wb_shape_upsert", { roomId, ...finalShape });
      pushHistoryEntry({ type: "shape-create", shape: finalShape });
      if (finalShape.type === "zigzag") {
        // A bendable line is fiddly to reshape immediately — jump straight
        // into select mode with it selected so its bend handles are already
        // showing, instead of making the user switch tools and click it.
        setTool("select");
        setSelectedShapeId(finalShape.id);
      }
      return;
    }

    if (!drawingRef.current || !myStrokeRef.current) return;
    drawingRef.current = false;
    const stroke = myStrokeRef.current;
    myStrokeRef.current = null;
    strokesRef.current.push(stroke);
    socketRef.current?.emit("wb_stroke_end", { roomId, id: stroke.id });
    pushHistoryEntry({ type: "stroke", stroke });
  };

  const commitDraftText = () => {
    if (!draftPos) return;
    const text = draftText.trim();
    const entry = {
      id: draftPos.id,
      x: draftPos.x,
      y: draftPos.y,
      text,
      color,
      fontSize: 16,
      fontFamily,
    };
    if (!draftPos.isNew || text) {
      const before = texts.find((t) => t.id === entry.id) || null;
      const after = text ? entry : null;
      setTexts((prev) => {
        const idx = prev.findIndex((t) => t.id === entry.id);
        if (!text) return idx === -1 ? prev : prev.filter((t) => t.id !== entry.id);
        if (idx === -1) return [...prev, entry];
        const next = [...prev];
        next[idx] = entry;
        return next;
      });
      socketRef.current?.emit("wb_text", { roomId, ...entry });
      pushHistoryEntry({ type: "text", id: entry.id, x: entry.x, y: entry.y, before, after });
    }
    setDraftPos(null);
    setDraftText("");
  };

  const cancelDraftText = () => {
    setDraftPos(null);
    setDraftText("");
  };

  const clearBoard = () => {
    const clearedStrokes = strokesRef.current;
    const clearedTexts = texts;
    const clearedShapes = shapes;
    strokesRef.current = [];
    remoteActiveRef.current.clear();
    setTexts([]);
    setShapes([]);
    setSelectedShapeId(null);
    redrawAll();
    socketRef.current?.emit("wb_clear", { roomId });
    if (clearedStrokes.length || clearedTexts.length || clearedShapes.length) {
      pushHistoryEntry({
        type: "clear",
        strokes: clearedStrokes,
        texts: clearedTexts,
        shapes: clearedShapes,
      });
    }
  };

  const deleteShape = (id) => {
    const shape = shapes.find((s) => s.id === id);
    setShapes((prev) => prev.filter((s) => s.id !== id));
    setSelectedShapeId((prev) => (prev === id ? null : prev));
    socketRef.current?.emit("wb_shape_delete", { roomId, id });
    if (shape) pushHistoryEntry({ type: "shape-delete", shape });
  };

  const deleteText = (id) => {
    const before = texts.find((t) => t.id === id);
    if (!before) return;
    setTexts((prev) => prev.filter((t) => t.id !== id));
    setSelectedTextId((prev) => (prev === id ? null : prev));
    // Same "empty text = delete" convention commitDraftText already uses,
    // so the peer and the undo/redo history (which already knows how to
    // invert a text entry via before/after) both handle this for free.
    socketRef.current?.emit("wb_text", { roomId, id, x: before.x, y: before.y, text: "" });
    pushHistoryEntry({ type: "text", id, x: before.x, y: before.y, before, after: null });
  };

  // Delete/Backspace removes the selected shape or text — skipped while typing
  // anywhere (the text draft input, chat, etc).
  useEffect(() => {
    if (viewOnly) return undefined;
    const onKeyDown = (e) => {
      if (!selectedShapeId && !selectedTextId) return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        if (selectedShapeId) deleteShape(selectedShapeId);
        if (selectedTextId) deleteText(selectedTextId);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShapeId, selectedTextId, viewOnly]);

  // Ctrl/Cmd+Z to undo, Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y to redo — gated on
  // `active` so this never fires while the Editor tab (Monaco has its own
  // undo/redo) is what's actually showing, and skipped while typing so it
  // doesn't fight a text input's native undo.
  useEffect(() => {
    if (viewOnly || !active) return undefined;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      if (key !== "z" && key !== "y") return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      e.preventDefault();
      if (key === "y" || (key === "z" && e.shiftKey)) {
        redo();
      } else {
        undo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, viewOnly]);

  const shapeOrigCoords = (shape) => ({
    x1: shape.x1,
    y1: shape.y1,
    x2: shape.x2,
    y2: shape.y2,
    ...(shape.type === "zigzag" ? { bends: (shape.bends || []).map((p) => ({ ...p })) } : {}),
  });

  const startShapeMove = (e, shape) => {
    if (viewOnly || tool !== "select") return;
    e.stopPropagation();
    e.preventDefault();

    // Overlapping shapes all sit in the same SVG, so native hit-testing
    // always resolves a click to whichever one is drawn on top — there was
    // previously no way to reach a shape "underneath" a bigger one on top
    // of it. Clicking again on a spot where the CURRENTLY selected shape
    // already sits now cycles to the next shape underneath at that same
    // point instead of just re-selecting the same top one every time.
    let target = shape;
    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      // Page-space (unscaled) px, matching what hitTestShapePoint expects —
      // rect is the current post-zoom render box, so dividing out `zoom`
      // here undoes that scale.
      const px = (e.clientX - rect.left) / zoom;
      const py = (e.clientY - rect.top) / zoom;
      const stack = shapes.filter((s) => hitTestShapePoint(s, px, py)).reverse();
      if (stack.length > 1 && selectedShapeId === shape.id) {
        const idx = stack.findIndex((s) => s.id === shape.id);
        target = stack[(idx + 1) % stack.length];
      }
    }

    setSelectedShapeId(target.id);
    setSelectedTextId(null);
    shapeDragRef.current = {
      mode: "move",
      id: target.id,
      startX: e.clientX,
      startY: e.clientY,
      orig: shapeOrigCoords(target),
    };
    setIsDraggingShape(true);
  };

  const startShapeResize = (e, shape, handle) => {
    if (viewOnly) return;
    e.stopPropagation();
    e.preventDefault();
    shapeDragRef.current = {
      mode: "resize",
      id: shape.id,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      orig: shapeOrigCoords(shape),
    };
    setIsDraggingShape(true);
  };

  const onShapePointerMove = (e) => {
    const drag = shapeDragRef.current;
    if (!drag || (drag.mode !== "move" && drag.mode !== "resize")) return;
    if (!containerRef.current) return;
    // Normalized against the fixed page base (see toNorm) — `zoom` undoes
    // the CSS scale so a screen-pixel drag distance maps to the same
    // normalized delta regardless of current zoom level.
    const dx = (e.clientX - drag.startX) / zoom / WB_PAGE_BASE_WIDTH;
    const dy = (e.clientY - drag.startY) / zoom / WB_PAGE_BASE_HEIGHT;

    setShapes((prev) => {
      const next = prev.map((s) => {
        if (s.id !== drag.id) return s;
        let { x1, y1, x2, y2 } = drag.orig;
        let bends = s.bends;
        if (drag.mode === "move") {
          x1 += dx;
          y1 += dy;
          x2 += dx;
          y2 += dy;
          if (drag.orig.bends) {
            bends = drag.orig.bends.map((p) => ({ x: p.x + dx, y: p.y + dy }));
          }
        } else if (drag.handle === "se") {
          x2 += dx;
          y2 += dy;
        } else if (drag.handle === "nw") {
          x1 += dx;
          y1 += dy;
        } else if (drag.handle === "ne") {
          x2 += dx;
          y1 += dy;
        } else if (drag.handle === "sw") {
          x1 += dx;
          y2 += dy;
        } else if (drag.handle === "start") {
          x1 += dx;
          y1 += dy;
        } else if (drag.handle === "end") {
          x2 += dx;
          y2 += dy;
        } else if (typeof drag.handle === "string" && drag.handle.startsWith("bend")) {
          // A bend handle — the whole point of the zigzag/bendable line is
          // that each of these can be dragged independently, anywhere, to
          // tilt that point of the line without affecting the others.
          const idx = Number(drag.handle.slice(4));
          const origPoint = drag.orig.bends?.[idx];
          if (origPoint && s.bends) {
            bends = s.bends.map((p, i) =>
              i === idx ? { x: origPoint.x + dx, y: origPoint.y + dy } : p,
            );
          }
        }
        return { ...s, x1, y1, x2, y2, ...(s.type === "zigzag" ? { bends } : {}) };
      });
      const updated = next.find((s) => s.id === drag.id);
      if (updated) socketRef.current?.emit("wb_shape_upsert", { roomId, ...updated });
      return next;
    });
  };

  const onShapePointerUp = () => {
    const drag = shapeDragRef.current;
    if (drag?.mode === "resize" || drag?.mode === "move") {
      const before = drag.orig;
      // This handler is bound once per drag session (see the isDraggingShape
      // effect below) and never re-subscribed as `shapes` updates mid-drag,
      // so its own closure over `shapes` is stale by the time the drag ends.
      // Routing through setShapes's updater form reads the TRUE current
      // state instead — the updater itself runs synchronously the moment
      // setShapes is called, so `finalCoords` is populated before this
      // function call returns.
      let finalCoords = null;
      setShapes((prev) => {
        const current = prev.find((s) => s.id === drag.id);
        if (!current) return prev;
        if (drag.mode === "resize" && BOX_SHAPE_TYPES.has(current.type)) {
          const normalized = {
            ...current,
            x1: Math.min(current.x1, current.x2),
            y1: Math.min(current.y1, current.y2),
            x2: Math.max(current.x1, current.x2),
            y2: Math.max(current.y1, current.y2),
          };
          finalCoords = shapeOrigCoords(normalized);
          socketRef.current?.emit("wb_shape_upsert", { roomId, ...normalized });
          return prev.map((s) => (s.id === drag.id ? normalized : s));
        }
        finalCoords = shapeOrigCoords(current);
        return prev;
      });

      if (finalCoords) {
        const changed =
          finalCoords.x1 !== before.x1 ||
          finalCoords.y1 !== before.y1 ||
          finalCoords.x2 !== before.x2 ||
          finalCoords.y2 !== before.y2 ||
          JSON.stringify(finalCoords.bends || null) !== JSON.stringify(before.bends || null);
        if (changed) {
          pushHistoryEntry({
            type: "shape-transform",
            id: drag.id,
            before,
            after: finalCoords,
          });
        }
      }
    }
    if (drag?.mode === "move" || drag?.mode === "resize") {
      shapeDragRef.current = null;
    }
    setIsDraggingShape(false);
  };

  useEffect(() => {
    if (!isDraggingShape) return undefined;
    const move = (e) => onShapePointerMove(e);
    const up = () => onShapePointerUp();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraggingShape]);

  return (
    <div
      style={{
        display: active ? "flex" : "none",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      {!viewOnly && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
            padding: "8px 12px",
            borderBottom: `1px solid ${isDark ? "#30363d" : "rgba(15,60,70,0.12)"}`,
          }}
        >
          <Segmented
            size="middle"
            value={tool}
            onChange={setTool}
            options={WB_TOOL_OPTIONS}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {WB_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Color ${c}`}
                onClick={() => setColor(c)}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: c,
                  cursor: "pointer",
                  border:
                    color === c
                      ? "2px solid #137274"
                      : `1px solid ${isDark ? "#30363d" : "rgba(15,60,70,0.25)"}`,
                  boxShadow: color === c ? "0 0 0 2px rgba(19,114,116,0.25)" : "none",
                  padding: 0,
                }}
              />
            ))}
          </div>
          {tool === "text" && (
            <Tooltip title="Font">
              <Select
                value={fontFamily}
                onChange={setFontFamily}
                size="middle"
                style={{ width: 150 }}
                options={WB_FONT_OPTIONS.map((f) => ({
                  value: f.value,
                  label: <span style={{ fontFamily: f.value }}>{f.label}</span>,
                }))}
              />
            </Tooltip>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Tooltip title="Undo (Ctrl+Z)">
              <Button
                size="small"
                icon={<UndoOutlined />}
                disabled={!canUndo}
                onClick={undo}
              />
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Shift+Z)">
              <Button
                size="small"
                icon={<RedoOutlined />}
                disabled={!canRedo}
                onClick={redo}
              />
            </Tooltip>
          </div>
          <Segmented
            size="middle"
            value={strokeWidth}
            onChange={setStrokeWidth}
            options={WB_WIDTH_OPTIONS}
          />
          <Popconfirm
            title="Clear the whole board?"
            description="This removes every stroke and text box for everyone."
            okText="Clear"
            okButtonProps={{ danger: true }}
            onConfirm={clearBoard}
            placement="bottom"
          >
            <Tooltip title="Clear board">
              <Button size="small" icon={<ClearOutlined />} />
            </Tooltip>
          </Popconfirm>
        </div>
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0, position: "relative" }}>
        {!viewOnly && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              padding: "10px 6px",
              overflowY: "auto",
              borderRight: `1px solid ${isDark ? "#30363d" : "rgba(15,60,70,0.12)"}`,
              background: isDark ? "#0d1117" : "#f6f8fa",
            }}
          >
            {SHAPE_TOOL_OPTIONS.map((opt) => (
              <Tooltip key={opt.value} title={opt.label} placement="right">
                <Button
                  size="middle"
                  type={tool === opt.value ? "primary" : "default"}
                  onClick={() => setTool(opt.value)}
                  icon={<span style={{ fontSize: 18, display: "inline-flex" }}>{opt.icon}</span>}
                />
              </Tooltip>
            ))}
          </div>
        )}
        <div
          ref={viewportRef}
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            // Native scrolling is the pan mechanism — it doesn't conflict
            // with pointerdown-based drawing/selecting the way a custom
            // drag-to-pan would, and it comes with scrollbars, momentum,
            // and trackpad/wheel support for free.
            overflow: "auto",
            background: isDark ? "#0d1117" : "#ffffff",
          }}
          onWheel={(e) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            // Ctrl/Cmd+scroll (also how trackpad pinch-zoom is reported in
            // Chrome) zooms instead of the browser's own page zoom.
            e.preventDefault();
            setZoom((z) => {
              const next = e.deltaY < 0 ? z + WB_ZOOM_STEP : z - WB_ZOOM_STEP;
              return Math.min(WB_ZOOM_MAX, Math.max(WB_ZOOM_MIN, Math.round(next * 100) / 100));
            });
          }}
        >
        <div
          ref={containerRef}
          style={{
            position: "relative",
            width: pageSize.width,
            height: pageSize.height,
            transform: `scale(${zoom})`,
            transformOrigin: "0 0",
            cursor: viewOnly ? "default" : tool === "text" ? "text" : "crosshair",
          }}
        >
        <canvas
          ref={canvasRef}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        />

        <svg
          width={pageSize.width}
          height={pageSize.height}
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
        >
            {shapes.map((s) => {
              const x1 = s.x1 * WB_PAGE_BASE_WIDTH;
              const y1 = s.y1 * WB_PAGE_BASE_HEIGHT;
              const x2 = s.x2 * WB_PAGE_BASE_WIDTH;
              const y2 = s.y2 * WB_PAGE_BASE_HEIGHT;
              const minX = Math.min(x1, x2);
              const minY = Math.min(y1, y2);
              const maxX = Math.max(x1, x2);
              const maxY = Math.max(y1, y2);
              const cx = (minX + maxX) / 2;
              const cy = (minY + maxY) / 2;
              const bendPts = (s.bends || []).map((p) => ({
                x: p.x * WB_PAGE_BASE_WIDTH,
                y: p.y * WB_PAGE_BASE_HEIGHT,
              }));
              const isBox = BOX_SHAPE_TYPES.has(s.type);
              const interactive = tool === "select" && !viewOnly;
              const isSelected = tool === "select" && selectedShapeId === s.id;
              const handlers = interactive
                ? { onPointerDown: (e) => startShapeMove(e, s) }
                : {};
              const boxShapeStyle = {
                cursor: interactive ? "move" : "default",
                pointerEvents: interactive ? "all" : "none",
              };

              let shapeEl;
              if (s.type === "rectangle") {
                shapeEl = (
                  <rect
                    x={minX}
                    y={minY}
                    width={maxX - minX}
                    height={maxY - minY}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={s.width}
                    style={boxShapeStyle}
                    {...handlers}
                  />
                );
              } else if (s.type === "ellipse") {
                shapeEl = (
                  <ellipse
                    cx={cx}
                    cy={cy}
                    rx={(maxX - minX) / 2}
                    ry={(maxY - minY) / 2}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={s.width}
                    style={boxShapeStyle}
                    {...handlers}
                  />
                );
              } else if (s.type === "diamond") {
                shapeEl = (
                  <polygon
                    points={`${cx},${minY} ${maxX},${cy} ${cx},${maxY} ${minX},${cy}`}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={s.width}
                    strokeLinejoin="round"
                    style={boxShapeStyle}
                    {...handlers}
                  />
                );
              } else if (s.type === "triangle") {
                shapeEl = (
                  <polygon
                    points={`${cx},${minY} ${maxX},${maxY} ${minX},${maxY}`}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={s.width}
                    strokeLinejoin="round"
                    style={boxShapeStyle}
                    {...handlers}
                  />
                );
              } else if (s.type === "parallelogram") {
                const skew = (maxX - minX) * 0.2;
                shapeEl = (
                  <polygon
                    points={`${minX + skew},${minY} ${maxX},${minY} ${maxX - skew},${maxY} ${minX},${maxY}`}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={s.width}
                    strokeLinejoin="round"
                    style={boxShapeStyle}
                    {...handlers}
                  />
                );
              } else if (s.type === "hexagon") {
                const qy = minY + (maxY - minY) * 0.25;
                const qy2 = minY + (maxY - minY) * 0.75;
                shapeEl = (
                  <polygon
                    points={`${cx},${minY} ${maxX},${qy} ${maxX},${qy2} ${cx},${maxY} ${minX},${qy2} ${minX},${qy}`}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={s.width}
                    strokeLinejoin="round"
                    style={boxShapeStyle}
                    {...handlers}
                  />
                );
              } else if (s.type === "cylinder") {
                const rx = (maxX - minX) / 2;
                const ry = Math.min((maxY - minY) * 0.18, rx * 0.6) || 1;
                shapeEl = (
                  <g style={boxShapeStyle} {...handlers}>
                    <path
                      d={`M ${minX} ${minY + ry} L ${minX} ${maxY - ry} A ${rx} ${ry} 0 0 0 ${maxX} ${maxY - ry} L ${maxX} ${minY + ry} A ${rx} ${ry} 0 0 0 ${minX} ${minY + ry} Z`}
                      fill="transparent"
                      stroke={s.color}
                      strokeWidth={s.width}
                    />
                    <ellipse
                      cx={cx}
                      cy={minY + ry}
                      rx={rx}
                      ry={ry}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={s.width}
                      style={{ pointerEvents: "none" }}
                    />
                  </g>
                );
              } else if (s.type === "cloud") {
                shapeEl = (
                  <path
                    d={cloudPathD(minX, minY, maxX, maxY)}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={s.width}
                    strokeLinejoin="round"
                    style={boxShapeStyle}
                    {...handlers}
                  />
                );
              } else if (s.type === "star") {
                shapeEl = (
                  <polygon
                    points={starPolygonPoints(minX, minY, maxX, maxY)}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={s.width}
                    strokeLinejoin="round"
                    style={boxShapeStyle}
                    {...handlers}
                  />
                );
              } else if (s.type === "zigzag") {
                const allPts = [{ x: x1, y: y1 }, ...bendPts, { x: x2, y: y2 }];
                const points = allPts.map((p) => `${p.x},${p.y}`).join(" ");
                shapeEl = (
                  <g style={{ cursor: interactive ? "move" : "default" }} {...handlers}>
                    {/* Fat invisible hit-path so a thin bent line is still
                        easy to click on in select mode. */}
                    <polyline
                      points={points}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={Math.max(s.width, 16)}
                      style={{ pointerEvents: interactive ? "stroke" : "none" }}
                    />
                    <polyline
                      points={points}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={s.width}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ pointerEvents: "none" }}
                    />
                  </g>
                );
              } else {
                const angleEnd = Math.atan2(y2 - y1, x2 - x1);
                const angleStart = angleEnd + Math.PI;
                const headSize = 6 + s.width * 1.5;
                shapeEl = (
                  <g style={{ cursor: interactive ? "move" : "default" }} {...handlers}>
                    {/* Fat invisible hit-line so a thin line/arrow is still
                        easy to click on in select mode. */}
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="transparent"
                      strokeWidth={Math.max(s.width, 16)}
                      style={{ pointerEvents: interactive ? "stroke" : "none" }}
                    />
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke={s.color}
                      strokeWidth={s.width}
                      strokeLinecap="round"
                      style={{ pointerEvents: "none" }}
                    />
                    {(s.type === "arrow" || s.type === "doubleArrow") && (
                      <polygon points={arrowHeadPoints(x2, y2, angleEnd, headSize)} fill={s.color} />
                    )}
                    {s.type === "doubleArrow" && (
                      <polygon points={arrowHeadPoints(x1, y1, angleStart, headSize)} fill={s.color} />
                    )}
                  </g>
                );
              }

              return (
                <React.Fragment key={s.id}>
                  {shapeEl}
                  {isSelected && isBox && (
                    <>
                      <rect
                        x={minX - 6}
                        y={minY - 6}
                        width={maxX - minX + 12}
                        height={maxY - minY + 12}
                        fill="none"
                        stroke="#137274"
                        strokeDasharray="4 3"
                        style={{ pointerEvents: "none" }}
                      />
                      {[
                        { handle: "nw", hx: minX, hy: minY },
                        { handle: "ne", hx: maxX, hy: minY },
                        { handle: "sw", hx: minX, hy: maxY },
                        { handle: "se", hx: maxX, hy: maxY },
                      ].map((h) => (
                        <circle
                          key={h.handle}
                          cx={h.hx}
                          cy={h.hy}
                          r={5}
                          {...SHAPE_RESIZE_HANDLE_STYLE}
                          style={{ cursor: `${h.handle}-resize`, pointerEvents: "all" }}
                          onPointerDown={(e) => startShapeResize(e, s, h.handle)}
                        />
                      ))}
                    </>
                  )}
                  {isSelected && !isBox && (
                    <>
                      <circle
                        cx={x1}
                        cy={y1}
                        r={5}
                        {...SHAPE_RESIZE_HANDLE_STYLE}
                        style={{ cursor: "move", pointerEvents: "all" }}
                        onPointerDown={(e) => startShapeResize(e, s, "start")}
                      />
                      <circle
                        cx={x2}
                        cy={y2}
                        r={5}
                        {...SHAPE_RESIZE_HANDLE_STYLE}
                        style={{ cursor: "move", pointerEvents: "all" }}
                        onPointerDown={(e) => startShapeResize(e, s, "end")}
                      />
                      {s.type === "zigzag" &&
                        bendPts.map((p, i) => (
                          <circle
                            key={`bend-${i}`}
                            cx={p.x}
                            cy={p.y}
                            r={5}
                            fill="#f5a623"
                            stroke="#fff"
                            strokeWidth={1.5}
                            style={{ cursor: "move", pointerEvents: "all" }}
                            onPointerDown={(e) => startShapeResize(e, s, `bend${i}`)}
                          />
                        ))}
                    </>
                  )}
                </React.Fragment>
              );
            })}
        </svg>

        {tool === "select" && selectedShapeId && !viewOnly && (
          <Tooltip title="Delete shape">
            <button
              type="button"
              aria-label="Delete shape"
              onClick={() => deleteShape(selectedShapeId)}
              style={{
                position: "absolute",
                left: (() => {
                  const s = shapes.find((sh) => sh.id === selectedShapeId);
                  return s ? Math.min(s.x1, s.x2) * WB_PAGE_BASE_WIDTH : 0;
                })(),
                top: (() => {
                  const s = shapes.find((sh) => sh.id === selectedShapeId);
                  return s ? Math.min(s.y1, s.y2) * WB_PAGE_BASE_HEIGHT : 0;
                })(),
                transform: "translate(-100%, -100%)",
                marginLeft: -6,
                marginTop: -6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "none",
                background: "#e03131",
                color: "#fff",
                cursor: "pointer",
                zIndex: 6,
              }}
            >
              <DeleteOutlined style={{ fontSize: 12 }} />
            </button>
          </Tooltip>
        )}

        {texts.map((t) => {
          if (draftPos?.id === t.id) return null;
          const openForEdit = () => {
            if (viewOnly) return;
            setSelectedTextId(null);
            setDraftPos({ id: t.id, x: t.x, y: t.y, isNew: false });
            setDraftText(t.text);
            // Otherwise the color/font pickers keep whatever was last
            // picked (possibly for a different text box entirely), and
            // hitting Done without touching either would silently
            // reformat this text to match — restore its own values so
            // editing doesn't accidentally reformat it.
            setColor(t.color);
            setFontFamily(t.fontFamily || WB_DEFAULT_FONT_FAMILY);
          };
          const isTextSelected = tool === "select" && selectedTextId === t.id;
          return (
            <div
              key={t.id}
              onClick={() => {
                if (viewOnly) return;
                if (tool === "select") {
                  setSelectedShapeId(null);
                  setSelectedTextId(t.id);
                  return;
                }
                if (tool === "text") openForEdit();
              }}
              onDoubleClick={openForEdit}
              style={{
                position: "absolute",
                // Fixed-base px, not a %, so text stays put (rather than
                // silently drifting) if the page is later made bigger — see
                // WB_PAGE_BASE_WIDTH.
                left: t.x * WB_PAGE_BASE_WIDTH,
                top: t.y * WB_PAGE_BASE_HEIGHT,
                color: t.color,
                fontSize: t.fontSize,
                fontFamily: t.fontFamily || WB_DEFAULT_FONT_FAMILY,
                fontWeight: 600,
                whiteSpace: "pre-wrap",
                cursor: viewOnly ? "default" : tool === "select" ? "pointer" : "text",
                maxWidth: "60%",
                // A near-white/near-black text color would otherwise vanish
                // against a same-toned board — a faint backdrop keeps any
                // color legible without changing what was actually chosen.
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                borderRadius: 3,
                padding: "1px 4px",
                margin: "-1px -4px",
                outline: isTextSelected ? "2px dashed #137274" : "none",
                outlineOffset: 2,
              }}
            >
              {t.text}
            </div>
          );
        })}

        {tool === "select" && selectedTextId && !viewOnly && (
          <Tooltip title="Delete text">
            <button
              type="button"
              aria-label="Delete text"
              onClick={() => deleteText(selectedTextId)}
              style={{
                position: "absolute",
                left: (() => {
                  const t = texts.find((tx) => tx.id === selectedTextId);
                  return t ? t.x * WB_PAGE_BASE_WIDTH : 0;
                })(),
                top: (() => {
                  const t = texts.find((tx) => tx.id === selectedTextId);
                  return t ? t.y * WB_PAGE_BASE_HEIGHT : 0;
                })(),
                transform: "translate(-100%, -100%)",
                marginLeft: -6,
                marginTop: -6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "none",
                background: "#e03131",
                color: "#fff",
                cursor: "pointer",
                zIndex: 6,
              }}
            >
              <DeleteOutlined style={{ fontSize: 12 }} />
            </button>
          </Tooltip>
        )}


        {draftPos && (
          <div
            style={{
              position: "absolute",
              left: draftPos.x * WB_PAGE_BASE_WIDTH,
              top: draftPos.y * WB_PAGE_BASE_HEIGHT,
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: isDark ? "#161b22" : "#fff",
              border: "2px solid #137274",
              borderRadius: 4,
              padding: "4px 6px",
              minWidth: 160,
              boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
              zIndex: 5,
            }}
          >
            {/* Swatch shows what color will actually be committed — the
                input's own text always stays high-contrast against its box
                so typing is legible even if that color is near-white/black. */}
            <span
              aria-hidden
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: color,
                border: `1px solid ${isDark ? "#30363d" : "rgba(15,60,70,0.3)"}`,
                flexShrink: 0,
              }}
            />
            <input
              autoFocus
              value={draftText}
              placeholder="Type here…"
              onChange={(e) => setDraftText(e.target.value)}
              onBlur={commitDraftText}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  e.preventDefault();
                  cancelDraftText();
                }
              }}
              style={{
                color: isDark ? "#e6edf3" : "#1f2328",
                fontSize: 16,
                fontFamily,
                fontWeight: 600,
                background: "transparent",
                border: "none",
                outline: "none",
                flex: 1,
                minWidth: 0,
              }}
            />
            <Tooltip title="Done">
              <button
                type="button"
                aria-label="Done"
                // Commit on mousedown, before the input's own blur (which
                // also commits) — keeps a single explicit affordance for
                // anyone whose blur/Enter isn't registering for some reason.
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitDraftText();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: "none",
                  background: "#137274",
                  color: "#fff",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <CheckOutlined style={{ fontSize: 11 }} />
              </button>
            </Tooltip>
            <Tooltip title="Cancel">
              <button
                type="button"
                aria-label="Cancel"
                onMouseDown={(e) => {
                  e.preventDefault();
                  cancelDraftText();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: `1px solid ${isDark ? "#30363d" : "rgba(15,60,70,0.3)"}`,
                  background: "transparent",
                  color: isDark ? "#8b949e" : "#57606a",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <CloseOutlined style={{ fontSize: 11 }} />
              </button>
            </Tooltip>
          </div>
        )}
        </div>

        {/* A sibling of the zoomed/scrolled page div (not a child of it), so
            this stays pinned to the visible panel's own top edge instead of
            scrolling away with the page content underneath it. */}
        {!viewOnly && tool === "text" && !draftPos && (
          <div
            style={{
              position: "absolute",
              top: 10,
              left: "50%",
              transform: "translateX(-50%)",
              background: isDark ? "rgba(22,27,34,0.9)" : "rgba(255,255,255,0.95)",
              color: isDark ? "#8b949e" : "#57606a",
              border: `1px solid ${isDark ? "#30363d" : "rgba(15,60,70,0.2)"}`,
              borderRadius: 20,
              padding: "4px 12px",
              fontSize: 12,
              fontWeight: 600,
              pointerEvents: "none",
              zIndex: 4,
            }}
          >
            Click anywhere on the board to add text
          </div>
        )}
        </div>

        {!viewOnly && (
          <div
            style={{
              position: "absolute",
              right: 8,
              bottom: 8,
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: isDark ? "rgba(13,17,23,0.9)" : "rgba(255,255,255,0.95)",
              border: `1px solid ${isDark ? "#30363d" : "rgba(15,60,70,0.2)"}`,
              borderRadius: 20,
              padding: "3px 4px",
            }}
          >
            <Tooltip title="Add space below">
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  setPageSize((p) => ({
                    ...p,
                    height: Math.min(WB_PAGE_MAX_HEIGHT, p.height + WB_PAGE_GROW_STEP),
                  }))
                }
              >
                Page
              </Button>
            </Tooltip>
            <Tooltip title="Zoom out">
              <Button
                size="small"
                shape="circle"
                icon={<MinusOutlined />}
                disabled={zoom <= WB_ZOOM_MIN}
                onClick={() =>
                  setZoom((z) => Math.max(WB_ZOOM_MIN, Math.round((z - WB_ZOOM_STEP) * 100) / 100))
                }
              />
            </Tooltip>
            <Tooltip title="Reset zoom">
              <span
                role="button"
                tabIndex={0}
                onClick={() => setZoom(1)}
                style={{
                  fontSize: 12,
                  minWidth: 36,
                  textAlign: "center",
                  cursor: "pointer",
                  color: isDark ? "#8b949e" : "#57606a",
                }}
              >
                {Math.round(zoom * 100)}%
              </span>
            </Tooltip>
            <Tooltip title="Zoom in">
              <Button
                size="small"
                shape="circle"
                icon={<PlusOutlined />}
                disabled={zoom >= WB_ZOOM_MAX}
                onClick={() =>
                  setZoom((z) => Math.min(WB_ZOOM_MAX, Math.round((z + WB_ZOOM_STEP) * 100) / 100))
                }
              />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

const LiveCodingRoom = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const userInfo = useSelector((state) => state.user.userInfo);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isAppDarkMode = useSelector((state) => state.theme.isDarkMode);
  const styles = useMemo(() => getStyles(isAppDarkMode), [isAppDarkMode]);
  const textColor = isAppDarkMode ? "#e6edf3" : "#0f2a2b";
  const mutedColor = isAppDarkMode ? "#8b949e" : "#5f6b73";
  const mutedFaintColor = isAppDarkMode ? "#6e7681" : "#7c8a92";

  const urlViewOnly = searchParams.get("viewOnly") === "1";

  const [language, setLanguage] = useState("java");
  const [code, setCode] = useState(defaultCode.java);
  const [fontSize, setFontSize] = useState(readStoredFontSize);
  const [theme, setTheme] = useState(() => readStoredTheme(isAppDarkMode));
  useEffect(() => {
    setTheme(isAppDarkMode ? CRAFTERHACK_DARK_THEME : CRAFTERHACK_LIGHT_THEME);
  }, [isAppDarkMode]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shortcutsOn, setShortcutsOn] = useState(areShortcutsEnabled);
  const [output, setOutput] = useState("");
  const [outputMeta, setOutputMeta] = useState("");
  const [outputError, setOutputError] = useState(false);
  const [outputVisible, setOutputVisible] = useState(false);
  const [outputHeight, setOutputHeight] = useState(180);
  const [sidePaneWidth, setSidePaneWidth] = useState(280);
  const [isCompiling, setIsCompiling] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const screenShareZoom = useZoomPan();
  const remoteVideoZoom = useZoomPan();
  /** Enforced on this client (from URL or host broadcast). Host stays editable. */
  const [isViewOnly, setIsViewOnly] = useState(urlViewOnly);
  /** Host preference when sharing — appends ?viewOnly=1 to invite link */
  const [shareAsViewOnly, setShareAsViewOnly] = useState(false);
  // Join with mic/camera off by default — participants opt in explicitly.
  const [isMuted, setIsMuted] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [isRemoteMuted, setIsRemoteMuted] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);
  const [blurAmount, setBlurAmount] = useState(8); // 8 = light, 18 = strong
  const [blurPickerOpen, setBlurPickerOpen] = useState(false);
  useEffect(() => {
    if (!blurPickerOpen) return;
    // On the very first open, antd's Popover content (and these <video>
    // refs) hasn't mounted yet at the moment the click handler fires, so
    // binding srcObject there would silently no-op and leave the tiles
    // black. Effects run after the DOM commit, so the refs are guaranteed
    // to exist here on every open, including the first.
    [bgPreviewNoneRef, bgPreviewLightRef, bgPreviewStrongRef].forEach((r) => {
      if (r.current && localStream.current) {
        r.current.srcObject = localStream.current;
        r.current.play?.().catch(() => {});
      }
    });
  }, [blurPickerOpen]);
  // What the background effect actually renders once isBlurred is on.
  const [bgMode, setBgMode] = useState("blur"); // 'blur' | 'color' | 'image'
  const [bgColor, setBgColor] = useState(BG_COLORS[0]);
  const [bgImageUrl, setBgImageUrl] = useState(null); // data URL of an uploaded photo
  const blurAmountRef = useRef(8);
  const bgModeRef = useRef("blur");
  const bgColorRef = useRef(BG_COLORS[0]);
  const bgImageElRef = useRef(null); // loaded <img> for the uploaded background
  const bgScratchCanvasRef = useRef(null); // offscreen buffer reused for compositing
  const bgPreviewNoneRef = useRef(null); // live-preview <video> tiles in the blur popover
  const bgPreviewLightRef = useRef(null);
  const bgPreviewStrongRef = useRef(null);
  useEffect(() => {
    blurAmountRef.current = blurAmount;
  }, [blurAmount]);
  useEffect(() => {
    bgModeRef.current = bgMode;
  }, [bgMode]);
  useEffect(() => {
    bgColorRef.current = bgColor;
  }, [bgColor]);
  useEffect(() => {
    if (!bgImageUrl) {
      bgImageElRef.current = null;
      return;
    }
    const img = new Image();
    img.onload = () => {
      bgImageElRef.current = img;
    };
    img.src = bgImageUrl;
  }, [bgImageUrl]);
  const [reactions, setReactions] = useState([]);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRemoteScreenSharing, setIsRemoteScreenSharing] = useState(false);
  const [editorOpenDuringShare, setEditorOpenDuringShare] = useState(false);
  const [roomSize, setRoomSize] = useState(0);
  const [hasRemoteStream, setHasRemoteStream] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [nameReady, setNameReady] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [peerName, setPeerName] = useState("Peer");
  const [typingInfo, setTypingInfo] = useState(null); // { name, line, column, top, left }
  const [isHost, setIsHost] = useState(false);
  // Guest side: blocked in a waiting room until the host admits them.
  const [awaitingAdmission, setAwaitingAdmission] = useState(false);
  const [roomNotStarted, setRoomNotStarted] = useState(false);
  // Host side: someone is waiting to be let in — { socketId, name }.
  const [pendingGuestRequest, setPendingGuestRequest] = useState(null);
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [outputDevices, setOutputDevices] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState(
    () => localStorage.getItem(MIC_DEVICE_KEY) || "",
  );
  const [selectedCameraId, setSelectedCameraId] = useState(
    () => localStorage.getItem(CAMERA_DEVICE_KEY) || "",
  );
  const [selectedSpeakerId, setSelectedSpeakerId] = useState(
    () => localStorage.getItem(SPEAKER_DEVICE_KEY) || "",
  );
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [avStatus, setAvStatus] = useState("idle"); // idle | connecting | connected | failed | relay
  const [relayMode, setRelayMode] = useState(false);
  const isMobile = useIsNarrow(900);
  const languageOptions = useMemo(
    () => [
      {
        label: "Languages",
        title: "Languages",
        options: COMPILER_LANGUAGES.map((lang) => ({
          value: lang.id,
          label: isMobile ? lang.shortLabel : lang.label,
        })),
      },
      {
        label: "Notes",
        title: "Notes",
        options: NOTE_LANGUAGES.map((lang) => ({
          value: lang.id,
          label: lang.label,
        })),
      },
    ],
    [isMobile],
  );
  /** On phone, video feeds start collapsed so the editor owns the viewport. */
  const [videoFeedsOpen, setVideoFeedsOpen] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= 900,
  );

  useEffect(() => {
    setVideoFeedsOpen(!isMobile);
  }, [isMobile]);

  /** Desktop-only: swap the editor pane for a full-width, side-by-side video
   * view of both participants. Auto-exits if a screen share starts, since
   * that needs the main pane back. */
  const [videoOnlyMode, setVideoOnlyMode] = useState(false);
  // What the main pane shows when videoOnlyMode is off — 'editor' (Monaco) or
  // 'whiteboard'. Orthogonal to videoOnlyMode itself: the Layout popover
  // presents Editor/Whiteboard/Video as one 3-way choice, but under the hood
  // "Video" is just videoOnlyMode=true and the other two toggle this.
  const [mainPaneMode, setMainPaneMode] = useState("editor");
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);
  useEffect(() => {
    if ((isScreenSharing || isRemoteScreenSharing) && videoOnlyMode) {
      setVideoOnlyMode(false);
    }
    if ((isScreenSharing || isRemoteScreenSharing) && mainPaneMode === "whiteboard") {
      setMainPaneMode("editor");
    }
  }, [isScreenSharing, isRemoteScreenSharing, videoOnlyMode, mainPaneMode]);

  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen isn't available in this browser");
      });
    }
  }, []);

  // Stale zoom shouldn't carry over once the thing being viewed changes.
  useEffect(() => {
    if (!isScreenSharing && !isRemoteScreenSharing) screenShareZoom.reset();
  }, [isScreenSharing, isRemoteScreenSharing]);
  useEffect(() => {
    if (!hasRemoteStream) remoteVideoZoom.reset();
  }, [hasRemoteStream]);

  const canvasRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const stageVideoRef = useRef(null);
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const rawVideoTrackRef = useRef(null);
  const screenStreamRef = useRef(null);
  const screenTrackRef = useRef(null);
  const isScreenSharingRef = useRef(false);
  const screenShareStartingRef = useRef(false);
  const socketRef = useRef(null);
  const blurRafRef = useRef(null);
  const blurActiveRef = useRef(false);
  // The canvas.captureStream() track currently standing in for the camera,
  // whenever a background effect is on — createPeerConnection needs this to
  // pick the right initial video track (see outboundVideo below): a brand
  // new/rebuilt PeerConnection otherwise has no way to know blur was active
  // and silently falls back to the raw camera for that peer.
  const blurTrackRef = useRef(null);
  const bodyPixNetRef = useRef(null);
  const offerSentRef = useRef(false);
  const pendingIceRef = useRef([]);
  const remoteStreamRef = useRef(null);
  const isMutedRef = useRef(true);
  const videoEnabledRef = useRef(false);
  const applyingRemoteRef = useRef(false);
  const emitTimerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const remoteTypingClearRef = useRef(null);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const remoteDecoRef = useRef([]);
  const typingWidgetRef = useRef(null);
  const revisionRef = useRef(0);
  const displayNameRef = useRef("");
  const isViewOnlyRef = useRef(urlViewOnly);
  const mySocketIdRef = useRef(null);
  // The connected peer's current socketId — needed so the host can target a
  // "remove this guest" action; this is a 1:1 room, so there's ever at most
  // one, tracked from joined_room's participant list and user_joined.
  const peerSocketIdRef = useRef(null);
  // Stable per-tab id so the server can recognize a socket.io reconnect (new
  // socket.id after a network blip) as the same participant resuming, rather
  // than bouncing them as "room full" or tearing down the peer's A/V.
  // Persisted in sessionStorage (not just a ref) so it also survives a full
  // page reload/navigation — e.g. editing the URL to strip ?viewOnly=1 and
  // hitting enter remounts the whole app, and a ref alone would hand the
  // server a brand-new "never seen this client" id, defeating the server's
  // sticky view-only enforcement (see socketHandler.js join_room).
  const clientIdRef = useRef(null);
  if (!clientIdRef.current) {
    let stored = null;
    try {
      stored = sessionStorage.getItem("ch_live_client_id");
    } catch (_) {
      stored = null;
    }
    clientIdRef.current =
      stored ||
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    try {
      sessionStorage.setItem("ch_live_client_id", clientIdRef.current);
    } catch (_) {
      /* ignore */
    }
  }

  // Only true for the browser that actually clicked "Create Room" (see
  // createRoom below) — lets the server tell "you're the host bootstrapping
  // a brand-new room" apart from "you're a guest who got here before the
  // host ever did". Sourced from router state on the initial navigation and
  // mirrored into sessionStorage (keyed per room) so it survives a refresh.
  const isCreatorRef = useRef(false);
  if (roomId) {
    if (location.state?.isCreator) {
      isCreatorRef.current = true;
      try {
        sessionStorage.setItem(`ch_live_creator_${roomId}`, "1");
      } catch (_) {
        /* ignore */
      }
    } else if (!isCreatorRef.current) {
      try {
        isCreatorRef.current =
          sessionStorage.getItem(`ch_live_creator_${roomId}`) === "1";
      } catch (_) {
        /* ignore */
      }
    }
  }

  const sendOfferRef = useRef(null);
  const createPeerConnectionRef = useRef(null);
  const cleanupMediaRef = useRef(null);
  const stopScreenShareRef = useRef(null);
  const notifyScreenShareRef = useRef(null);
  const applyRemoteCursorRef = useRef(null);
  const joinedOnceRef = useRef(false);
  // True once we've ever sent join_room and heard back (seated OR waiting on
  // the host) — gates the reconnect handler so a network blip while still in
  // the waiting room also triggers a rejoin, not just after full admission.
  const hasJoinAttemptRef = useRef(false);
  const isRemoteScreenSharingRef = useRef(false);
  const pendingOfferRef = useRef(null);
  const iceConfigRef = useRef(null);
  const makingOfferRef = useRef(false);
  const roomSizeRef = useRef(0);
  const chatListRef = useRef(null);
  const chatOpenRef = useRef(false);
  const isOffererRef = useRef(false);
  const iceRestartAttemptsRef = useRef(0);
  const mediaRelayRef = useRef(null);
  const relayModeRef = useRef(false);
  const selectedMicIdRef = useRef(selectedMicId);
  const selectedCameraIdRef = useRef(selectedCameraId);
  const selectedSpeakerIdRef = useRef(selectedSpeakerId);

  useEffect(() => {
    chatOpenRef.current = chatOpen;
  }, [chatOpen]);

  useEffect(() => {
    roomSizeRef.current = roomSize;
  }, [roomSize]);

  useEffect(() => {
    relayModeRef.current = relayMode;
  }, [relayMode]);

  const startServerRelay = useCallback(() => {
    if (relayModeRef.current) return;
    mediaRelayRef.current = createLiveMediaRelay({
      getSocket: () => socketRef.current,
      getRoomId: () => roomId,
      getLocalStream: () => localStream.current,
      getScreenStream: () =>
        isScreenSharingRef.current ? screenStreamRef.current : null,
      // Live getters, not snapshots — stageVideoEl only mounts once a share
      // is active, so capturing it once at relay-start time (before anyone
      // has shared) would permanently pin it to null and screen-share frames
      // would never reach the stage.
      getRemoteVideoEl: () => remoteVideoRef.current,
      getStageVideoEl: () => stageVideoRef.current,
      getAudioOutputId: () => selectedSpeakerIdRef.current,
    });
    mediaRelayRef.current.start();
    relayModeRef.current = true;
    setRelayMode(true);
    setAvStatus("relay");
    setHasRemoteStream(true);
    message.info({
      content: "Using free server relay for audio/video",
      key: "relay-mode",
      duration: 4,
    });
  }, [roomId]);

  const stopServerRelay = useCallback(() => {
    mediaRelayRef.current?.stop();
    mediaRelayRef.current = null;
    relayModeRef.current = false;
    setRelayMode(false);
  }, []);

  const startServerRelayRef = useRef(startServerRelay);
  const stopServerRelayRef = useRef(stopServerRelay);
  startServerRelayRef.current = startServerRelay;
  stopServerRelayRef.current = stopServerRelay;

  useEffect(() => {
    selectedMicIdRef.current = selectedMicId;
  }, [selectedMicId]);

  useEffect(() => {
    selectedCameraIdRef.current = selectedCameraId;
  }, [selectedCameraId]);

  useEffect(() => {
    selectedSpeakerIdRef.current = selectedSpeakerId;
  }, [selectedSpeakerId]);

  useEffect(() => {
    displayNameRef.current = displayName;
  }, [displayName]);

  useEffect(() => {
    isViewOnlyRef.current = isViewOnly;
  }, [isViewOnly]);

  // Gate live-room tour until display name is confirmed
  useEffect(() => {
    if (!roomId) {
      document.body.dataset.liveNameReady = "1";
      return undefined;
    }
    document.body.dataset.liveNameReady = nameReady ? "1" : "0";
    return () => {
      delete document.body.dataset.liveNameReady;
    };
  }, [roomId, nameReady]);

  // Resolve display name before joining room media/socket
  useEffect(() => {
    if (!roomId) return;
    const fromUser =
      (isLoggedIn && (userInfo?.name || userInfo?.username)) || "";
    const stored = localStorage.getItem(DISPLAY_NAME_KEY) || "";
    const resolved = (fromUser || stored).trim();
    if (resolved) {
      setDisplayName(resolved);
      setNameDraft(resolved);
      setNameReady(true);
      localStorage.setItem(DISPLAY_NAME_KEY, resolved);
    } else {
      setNameReady(false);
      setNameDraft("");
    }
  }, [roomId, isLoggedIn, userInfo]);

  const createRoom = () => {
    const newRoomId = generateRandomString(7);
    navigate(`/live-coding/${newRoomId}`, { state: { isCreator: true } });
  };

  const confirmDisplayName = () => {
    const next = nameDraft.trim();
    if (next.length < 2) {
      message.warning("Please enter at least 2 characters");
      return;
    }
    localStorage.setItem(DISPLAY_NAME_KEY, next);
    setDisplayName(next);
    setNameReady(true);
  };

  const respondToJoinRequest = useCallback(
    (socketId, admit) => {
      if (socketRef.current && roomId) {
        socketRef.current.emit("admit_guest", { roomId, socketId, admit });
      }
      setPendingGuestRequest((prev) =>
        prev?.socketId === socketId ? null : prev,
      );
    },
    [roomId],
  );

  const removeGuest = useCallback(() => {
    const targetSocketId = peerSocketIdRef.current;
    if (!socketRef.current || !roomId || !targetSocketId) return;
    socketRef.current.emit("remove_participant", {
      roomId,
      socketId: targetSocketId,
    });
  }, [roomId]);

  const cleanupMedia = useCallback(() => {
    blurActiveRef.current = false;
    isScreenSharingRef.current = false;
    if (blurRafRef.current) {
      cancelAnimationFrame(blurRafRef.current);
      blurRafRef.current = null;
    }
    if (screenTrackRef.current) {
      try {
        screenTrackRef.current.onended = null;
      } catch (_) {
        /* ignore */
      }
      screenTrackRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {
          /* ignore */
        }
      });
      screenStreamRef.current = null;
    }
    if (peerConnection.current) {
      try {
        peerConnection.current.onicecandidate = null;
        peerConnection.current.ontrack = null;
        peerConnection.current.close();
      } catch (_) {
        /* ignore */
      }
      peerConnection.current = null;
    }
    if (localStream.current) {
      localStream.current.getTracks().forEach((track) => track.stop());
      localStream.current = null;
    }
    rawVideoTrackRef.current = null;
    remoteStreamRef.current = null;
    pendingIceRef.current = [];
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    if (stageVideoRef.current) stageVideoRef.current.srcObject = null;
    setHasRemoteStream(false);
    setIsScreenSharing(false);
    isRemoteScreenSharingRef.current = false;
    setIsRemoteScreenSharing(false);
    offerSentRef.current = false;
  }, []);

  const flushPendingIce = useCallback(async () => {
    const pc = peerConnection.current;
    if (!pc?.remoteDescription) return;
    const queued = pendingIceRef.current.splice(0);
    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (err) {
        console.warn("Failed to add queued ICE candidate:", err);
      }
    }
  }, []);

  const bindRemotePlayback = useCallback((stream) => {
    if (!stream) return;

    // Video tiles are display-only (muted). Audio always plays from a dedicated
    // <audio> element so both sides hear even when feeds are collapsed / stage is muted.
    if (remoteVideoRef.current) {
      if (remoteVideoRef.current.srcObject !== stream) {
        remoteVideoRef.current.srcObject = stream;
      }
      remoteVideoRef.current.muted = true;
      remoteVideoRef.current.play?.().catch(() => {});
    }

    const audioEl = remoteAudioRef.current;
    if (audioEl) {
      const audioTracks = stream.getAudioTracks();
      const current = audioEl.srcObject;
      const currentIds = current
        ? [...current.getAudioTracks()]
            .map((t) => t.id)
            .sort()
            .join(",")
        : "";
      const nextIds = audioTracks
        .map((t) => t.id)
        .sort()
        .join(",");
      if (currentIds !== nextIds) {
        audioEl.srcObject = audioTracks.length
          ? new MediaStream(audioTracks)
          : null;
      }
      audioEl.muted = false;
      audioEl.volume = 1;
      applySinkId(audioEl, selectedSpeakerIdRef.current);
      const playPromise = audioEl.play?.();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          // Autoplay may be blocked until a user gesture — retry on next interaction
        });
      }
    }

    if (
      isRemoteScreenSharingRef.current &&
      !isScreenSharingRef.current &&
      stageVideoRef.current
    ) {
      if (stageVideoRef.current.srcObject !== stream) {
        stageVideoRef.current.srcObject = stream;
      }
      stageVideoRef.current.muted = true;
      stageVideoRef.current.play?.().catch(() => {});
    }
  }, []);

  const attachRemoteStream = useCallback(
    (event) => {
      let stream = event.streams && event.streams[0];
      if (!stream) {
        if (!remoteStreamRef.current) {
          remoteStreamRef.current = new MediaStream();
        }
        if (event.track) {
          // Swap same-kind tracks (camera ↔ screen) instead of stacking
          remoteStreamRef.current.getTracks().forEach((existing) => {
            if (
              existing.kind === event.track.kind &&
              existing.id !== event.track.id
            ) {
              remoteStreamRef.current.removeTrack(existing);
            }
          });
          const already = remoteStreamRef.current
            .getTracks()
            .some((t) => t.id === event.track.id);
          if (!already) remoteStreamRef.current.addTrack(event.track);
        }
        stream = remoteStreamRef.current;
      } else {
        remoteStreamRef.current = stream;
      }

      bindRemotePlayback(stream);
      setHasRemoteStream(true);
    },
    [bindRemotePlayback],
  );

  const bindRemotePlaybackRef = useRef(bindRemotePlayback);
  bindRemotePlaybackRef.current = bindRemotePlayback;

  const createPeerConnection = useCallback(async () => {
    if (peerConnection.current) {
      try {
        peerConnection.current.onicecandidate = null;
        peerConnection.current.ontrack = null;
        peerConnection.current.onconnectionstatechange = null;
        peerConnection.current.oniceconnectionstatechange = null;
        peerConnection.current.close();
      } catch (_) {
        /* ignore */
      }
    }

    pendingIceRef.current = [];

    try {
      const config = await fetchIceConfig();
      iceConfigRef.current = config.iceServers;
      if (!config.hasTurn) {
        console.warn(
          "[live] No TURN configured — remote peers on other networks may fail",
        );
      }
    } catch (_) {
      iceConfigRef.current = await getIceServers().catch(() => null);
    }

    const forceRelay =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("forceRelay") === "1";

    const pc = new RTCPeerConnection(
      getDefaultRtcConfiguration(iceConfigRef.current, { forceRelay }),
    );
    peerConnection.current = pc;
    setAvStatus("connecting");

    // Always create audio + video senders up front so camera↔screen replaceTrack
    // works without renegotiation (even when camera was denied).
    const local = localStream.current;
    const outboundAudio = local?.getAudioTracks?.()?.[0] || null;
    // A blurred/virtual background is a canvas.captureStream() track standing
    // in for the camera (see the BodyPix effect) — a brand new PeerConnection
    // (first connect, or rebuilt on reconnect/ICE-restart) has no memory of
    // that, so without this check it silently reattaches the raw camera and
    // the peer stops seeing the effect even though isBlurred/the local
    // preview still show it as on.
    const outboundVideo =
      (isScreenSharingRef.current &&
        screenTrackRef.current?.readyState === "live" &&
        screenTrackRef.current) ||
      (blurActiveRef.current &&
        blurTrackRef.current?.readyState === "live" &&
        blurTrackRef.current) ||
      local?.getVideoTracks?.()?.[0] ||
      null;

    const audioTransceiver = pc.addTransceiver("audio", {
      direction: "sendrecv",
      streams: local ? [local] : [],
    });
    const videoTransceiver = pc.addTransceiver("video", {
      direction: "sendrecv",
      streams: local ? [local] : [],
    });

    // Must be awaited: a sendrecv transceiver whose sender still has no track
    // attached gets answered/offered as recvonly by the browser (per JSEP),
    // regardless of the configured direction — so if createOffer/createAnswer
    // runs before these resolve, our own outbound audio/video silently drops
    // to receive-only for that whole negotiation.
    if (outboundAudio) {
      try {
        await audioTransceiver.sender.replaceTrack(outboundAudio);
        console.log("[live] outbound audio track attached:", outboundAudio.id);
      } catch (err) {
        console.error("[live] replaceTrack(audio) failed:", err);
      }
    } else {
      console.warn(
        "[live] no local audio track to attach (mic denied/unavailable)",
      );
    }
    if (outboundVideo) {
      try {
        await videoTransceiver.sender.replaceTrack(outboundVideo);
        console.log(
          "[live] outbound video track attached:",
          outboundVideo.id,
          "readyState:",
          outboundVideo.readyState,
        );
      } catch (err) {
        console.error("[live] replaceTrack(video) failed:", err);
      }
    } else {
      console.warn(
        "[live] no local video track to attach (camera denied/unavailable)",
      );
    }

    pc.ontrack = (event) => {
      console.log(
        "[live] ontrack:",
        event.track.kind,
        event.track.id,
        "muted:",
        event.track.muted,
        "readyState:",
        event.track.readyState,
        "streams:",
        event.streams?.length ?? 0,
      );
      attachRemoteStream(event);
      setAvStatus("connected");
      iceRestartAttemptsRef.current = 0;
      // When peer replaces camera→screen, re-bind stage if they're sharing
      if (isRemoteScreenSharingRef.current) {
        requestAnimationFrame(() => {
          if (remoteStreamRef.current) {
            bindRemotePlaybackRef.current?.(remoteStreamRef.current);
          }
        });
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current && roomId) {
        // Helpful diagnostics in production
        if (event.candidate.type === "relay") {
          console.log("[live] TURN relay candidate gathered");
        }
        socketRef.current.emit("ice-candidate", {
          roomId,
          candidate: event.candidate,
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      console.log("[live] iceConnectionState:", state);
      if (state === "connected" || state === "completed") {
        setAvStatus("connected");
        iceRestartAttemptsRef.current = 0;
      }
      if (state === "failed") {
        setAvStatus("failed");
        if (isOffererRef.current && iceRestartAttemptsRef.current < 2) {
          iceRestartAttemptsRef.current += 1;
          console.warn(
            `[live] ICE failed — renegotiating (attempt ${iceRestartAttemptsRef.current})`,
          );
          setTimeout(() => {
            sendOfferRef.current?.({ iceRestart: true });
          }, 600);
        } else {
          // Free fallback through our own server (no paid TURN)
          startServerRelayRef.current?.();
        }
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      console.log("[live] connectionState:", state);
      if (state === "connected") {
        setAvStatus("connected");
        stopServerRelayRef.current?.();
      }
      if (state === "failed") {
        setAvStatus("failed");
        console.warn("WebRTC connection failed — trying free server relay");
        if (isOffererRef.current && iceRestartAttemptsRef.current < 2) {
          iceRestartAttemptsRef.current += 1;
          setTimeout(() => {
            sendOfferRef.current?.({ iceRestart: true });
          }, 800);
        } else {
          startServerRelayRef.current?.();
        }
      }
    };

    return pc;
  }, [roomId, attachRemoteStream]);

  const sendOffer = useCallback(
    async ({ iceRestart = false } = {}) => {
      if (!socketRef.current || !roomId) return;
      if (!iceRestart && (offerSentRef.current || makingOfferRef.current))
        return;

      if (!peerConnection.current) {
        await createPeerConnectionRef.current?.();
      }
      if (!peerConnection.current) return;

      try {
        makingOfferRef.current = true;
        offerSentRef.current = true;
        isOffererRef.current = true;
        setAvStatus("connecting");

        if (iceRestart) {
          // Refresh TURN credentials then rebuild PC for a clean restart
          clearIceCache();
          await createPeerConnectionRef.current?.();
        }

        const pc = peerConnection.current;
        if (!pc) return;

        const localForOffer = localStream.current;
        for (const t of pc.getTransceivers()) {
          const kind = t.sender?.track?.kind || t.receiver?.track?.kind;
          if (!kind) continue;
          const localTrack =
            kind === "video"
              ? (isScreenSharingRef.current &&
                  screenTrackRef.current?.readyState === "live" &&
                  screenTrackRef.current) ||
                localForOffer?.getVideoTracks?.()?.[0]
              : localForOffer?.getAudioTracks?.()?.[0];
          if (!t.sender.track && localTrack) {
            try {
              await t.sender.replaceTrack(localTrack);
              console.log(
                `[live] re-synced outbound ${kind} track before offering:`,
                localTrack.id,
              );
            } catch (err) {
              console.error(
                `[live] re-sync replaceTrack(${kind}) failed:`,
                err,
              );
            }
          }
          if (localTrack && t.direction !== "sendrecv") {
            console.warn(
              `[live] transceiver(${kind}) direction was "${t.direction}", forcing sendrecv`,
            );
            t.direction = "sendrecv";
          }
        }

        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
          iceRestart: !!iceRestart,
        });
        await pc.setLocalDescription(offer);
        console.log(
          "[live] sending offer:",
          summarizeSdp(pc.localDescription?.sdp),
        );
        socketRef.current.emit("offer", {
          roomId,
          offer: pc.localDescription,
        });
      } catch (err) {
        offerSentRef.current = false;
        console.error("Error creating offer:", err);
        setAvStatus("failed");
      } finally {
        makingOfferRef.current = false;
      }
    },
    [roomId],
  );

  sendOfferRef.current = sendOffer;
  createPeerConnectionRef.current = createPeerConnection;
  cleanupMediaRef.current = cleanupMedia;
  const flushPendingIceRef = useRef(flushPendingIce);
  flushPendingIceRef.current = flushPendingIce;

  const clearTypingWidget = useCallback(() => {
    const editor = editorRef.current;
    if (editor && typingWidgetRef.current) {
      try {
        editor.removeContentWidget(typingWidgetRef.current);
      } catch (_) {
        /* ignore */
      }
      typingWidgetRef.current = null;
    }
  }, []);

  const applyRemoteCursor = useCallback(
    (cursor, name) => {
      const editor = editorRef.current;
      const monaco = monacoRef.current;
      if (!editor || !monaco || !cursor) return;

      const line = Math.max(1, cursor.lineNumber || 1);
      const column = Math.max(1, cursor.column || 1);
      const range = new monaco.Range(line, column, line, column + 1);

      remoteDecoRef.current = editor.deltaDecorations(remoteDecoRef.current, [
        {
          range,
          options: {
            className: "remote-cursor-line",
            isWholeLine: true,
            overviewRuler: {
              color: "#58a6ff",
              position: monaco.editor.OverviewRulerLane.Full,
            },
            hoverMessage: { value: `**${name || "Peer"}** is editing here` },
            glyphMarginClassName: "remote-cursor-glyph",
          },
        },
        {
          range: new monaco.Range(line, column, line, column),
          options: {
            beforeContentClassName: "remote-cursor-caret",
          },
        },
      ]);

      // Name label above the remote caret (Monaco content widget)
      clearTypingWidget();
      const domNode = document.createElement("div");
      domNode.className = "remote-typing-label";
      domNode.textContent = name || "Peer";

      const widget = {
        getId: () => "live.remote.typing.label",
        getDomNode: () => domNode,
        getPosition: () => ({
          position: { lineNumber: line, column },
          preference: [
            monaco.editor.ContentWidgetPositionPreference.ABOVE,
            monaco.editor.ContentWidgetPositionPreference.BELOW,
          ],
        }),
      };
      typingWidgetRef.current = widget;
      editor.addContentWidget(widget);
      editor.layoutContentWidget(widget);

      // Also keep overlay coords for fallback badge
      const visible = editor.getScrolledVisiblePosition({
        lineNumber: line,
        column,
      });
      if (visible) {
        setTypingInfo({
          name: name || "Peer",
          line,
          column,
          top: visible.top,
          left: visible.left,
        });
      } else {
        setTypingInfo({ name: name || "Peer", line, column });
      }
    },
    [clearTypingWidget],
  );

  applyRemoteCursorRef.current = applyRemoteCursor;
  const clearTypingWidgetRef = useRef(clearTypingWidget);
  clearTypingWidgetRef.current = clearTypingWidget;

  // Socket + media — only after display name is ready
  useEffect(() => {
    if (!roomId || !nameReady || !displayName) return undefined;

    joinedOnceRef.current = false;

    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      autoConnect: true,
    });
    socketRef.current = socket;

    const onRoomFull = (response) => {
      message.error(response?.message || "Room is full");
      navigate("/live-coding");
    };

    const onWaitingForHost = (response) => {
      hasJoinAttemptRef.current = true;
      setAwaitingAdmission(true);
      setRoomNotStarted(!!response?.notStarted);
      message.info({
        content: response?.message || "Waiting for the host to let you in…",
        key: "live-waiting",
        duration: 3,
      });
    };

    const onJoinDenied = (response) => {
      setAwaitingAdmission(false);
      setRoomNotStarted(false);
      message.warning(response?.message || "The host didn't admit you");
      navigate("/live-coding");
    };

    const onJoinRequest = (payload) => {
      if (!payload?.socketId) return;
      setPendingGuestRequest({
        socketId: payload.socketId,
        name: payload.name || "Someone",
      });
      playJoinSound();
    };

    const onJoinRequestCancelled = (payload) => {
      setPendingGuestRequest((prev) =>
        prev && prev.socketId === payload?.socketId ? null : prev,
      );
    };

    const onJoinedRoom = async (response) => {
      hasJoinAttemptRef.current = true;
      setAwaitingAdmission(false);
      setRoomNotStarted(false);
      const isFirstJoin = !joinedOnceRef.current;
      if (!joinedOnceRef.current) {
        joinedOnceRef.current = true;
        message.success({
          content: "Connected to live room",
          key: "live-joined",
          duration: 2,
        });
      }
      const size = response?.size ?? 1;
      setRoomSize(size);
      setIsHost(!!response?.isHost);

      // Host just started this room and nobody else is here yet — prompt
      // them to share the invite link instead of waiting for them to find
      // the "Invite" button themselves.
      if (isFirstJoin && response?.isHost && size === 1) {
        setIsModalVisible(true);
      }
      if (response?.socketId) {
        mySocketIdRef.current = response.socketId;
      }

      if (typeof response?.code === "string" && response.code.length) {
        applyingRemoteRef.current = true;
        setCode(response.code);
        setTimeout(() => {
          applyingRemoteRef.current = false;
        }, 0);
      }
      if (
        response?.language &&
        (isCompilerLanguage(response.language) ||
          isNoteLanguage(response.language))
      ) {
        setLanguage(response.language);
      }

      if (Array.isArray(response?.messages)) {
        setChatMessages(response.messages);
      }

      if (Array.isArray(response?.participants)) {
        const peer = response.participants.find(
          (p) => p.socketId !== (response.socketId || mySocketIdRef.current),
        );
        if (peer?.name) setPeerName(peer.name);
        peerSocketIdRef.current = peer?.socketId || null;

        // The server's per-participant record is authoritative (sticky across
        // rejoins) — trust it over the URL flag. Otherwise a guest who strips
        // ?viewOnly=1 and reloads would see an editable-looking editor even
        // though the server silently drops their edits: correct on the wire,
        // but confusing. `||` so this can only ever add restriction, never
        // remove it based on stale local state.
        const me = response.participants.find(
          (p) => p.socketId === (response.socketId || mySocketIdRef.current),
        );
        if (me && !isViewOnlyRef.current && me.viewOnly) {
          isViewOnlyRef.current = true;
          setIsViewOnly(true);
        }
      }

      // Sync exclusive screen-share lock from room state
      const activeSharer = response?.screenSharerSocketId;
      const me = response?.socketId || mySocketIdRef.current;
      const peerSharing = !!(activeSharer && activeSharer !== me);
      isRemoteScreenSharingRef.current = peerSharing;
      setIsRemoteScreenSharing(peerSharing);

      // Reconnect case: we were sharing before a network drop — reclaim the lock
      if (isScreenSharingRef.current) {
        notifyScreenShareRef.current?.(true);
      }

      // Only the 2nd joiner starts WebRTC (avoids offer glare)
      if (size >= 2 && sendOfferRef.current) {
        // Small delay so peer's PC + tracks are ready; retry once if media never arrives
        setTimeout(() => {
          sendOfferRef.current?.();
        }, 500);
        setTimeout(() => {
          if (
            roomSizeRef.current >= 2 &&
            !remoteStreamRef.current &&
            peerConnection.current?.iceConnectionState !== "connected" &&
            peerConnection.current?.iceConnectionState !== "completed"
          ) {
            offerSentRef.current = false;
            sendOfferRef.current?.();
          }
        }, 2500);
        // If still no P2P after ~8s, use free server relay
        setTimeout(() => {
          if (
            roomSizeRef.current >= 2 &&
            !relayModeRef.current &&
            peerConnection.current?.connectionState !== "connected" &&
            peerConnection.current?.iceConnectionState !== "connected" &&
            peerConnection.current?.iceConnectionState !== "completed"
          ) {
            startServerRelayRef.current?.();
          }
        }, 8000);
      }
    };

    const onUserJoined = async (payload) => {
      playJoinSound();
      message.info({
        content: `${payload?.name || "Someone"} joined the room`,
        key: "live-peer-joined",
        duration: 2,
      });
      setRoomSize(payload?.size ?? 2);
      if (payload?.name) setPeerName(payload.name);
      if (payload?.socketId) peerSocketIdRef.current = payload.socketId;
      if (Array.isArray(payload?.participants)) {
        const peer = payload.participants.find(
          (p) => p.socketId !== mySocketIdRef.current,
        );
        if (peer?.name) setPeerName(peer.name);
      }
      // Existing user waits for the newcomer's offer — do NOT send one here
      offerSentRef.current = false;
      pendingIceRef.current = [];
      // If P2P never comes up, fall back to free server relay
      setTimeout(() => {
        if (
          roomSizeRef.current >= 2 &&
          !relayModeRef.current &&
          peerConnection.current?.connectionState !== "connected" &&
          peerConnection.current?.iceConnectionState !== "connected" &&
          peerConnection.current?.iceConnectionState !== "completed"
        ) {
          startServerRelayRef.current?.();
        }
      }, 9000);
    };

    const onRoomSize = (size) => {
      setRoomSize(size);
      if (size < 2) {
        setHasRemoteStream(false);
        setIsRemoteMuted(false);
        isRemoteScreenSharingRef.current = false;
        setIsRemoteScreenSharing(false);
        setPeerName("Peer");
        setTypingInfo(null);
        remoteStreamRef.current = null;
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
        if (stageVideoRef.current && !isScreenSharingRef.current) {
          stageVideoRef.current.srcObject = null;
        }
        offerSentRef.current = false;
      }
    };

    const onCodeUpdate = (payload) => {
      const nextCode = typeof payload === "string" ? payload : payload?.code;
      if (typeof nextCode !== "string") return;

      applyingRemoteRef.current = true;
      setCode(nextCode);
      if (
        payload?.language &&
        (isCompilerLanguage(payload.language) ||
          isNoteLanguage(payload.language))
      ) {
        setLanguage(payload.language);
      }
      if (payload?.name) {
        setTypingInfo({
          name: payload.name,
          line: payload.cursor?.lineNumber,
          column: payload.cursor?.column,
        });
        if (remoteTypingClearRef.current)
          clearTimeout(remoteTypingClearRef.current);
        remoteTypingClearRef.current = setTimeout(() => {
          setTypingInfo(null);
          clearTypingWidgetRef.current?.();
        }, 1800);
      }
      if (payload?.cursor && applyRemoteCursorRef.current) {
        applyRemoteCursorRef.current(payload.cursor, payload.name);
      }
      setTimeout(() => {
        applyingRemoteRef.current = false;
      }, 0);
    };

    const onTyping = (payload) => {
      if (!payload?.isTyping) {
        setTypingInfo(null);
        clearTypingWidgetRef.current?.();
        return;
      }
      if (payload.cursor && applyRemoteCursorRef.current) {
        applyRemoteCursorRef.current(payload.cursor, payload.name);
      } else {
        setTypingInfo({
          name: payload.name || "Peer",
          line: payload.cursor?.lineNumber,
          column: payload.cursor?.column,
        });
      }
      if (remoteTypingClearRef.current)
        clearTimeout(remoteTypingClearRef.current);
      remoteTypingClearRef.current = setTimeout(() => {
        setTypingInfo(null);
        clearTypingWidgetRef.current?.();
      }, 2000);
    };

    const onRunResult = (payload) => {
      const who = payload?.name || "Someone";
      const ok = !!payload?.success;
      setOutput(payload?.output ?? "");
      setOutputError(!ok);
      setOutputMeta(`${who} ran the code${ok ? "" : " (error)"}`);
      setOutputVisible(true);
      setIsCompiling(false);
    };

    const onViewOnly = ({ viewOnly }) => {
      if (!urlViewOnly) {
        setIsViewOnly(!!viewOnly);
        message.info(
          viewOnly
            ? "Host enabled view-only mode"
            : "Host disabled view-only mode",
        );
      }
    };

    const onParticipantsUpdate = ({ participants }) => {
      if (!Array.isArray(participants)) return;
      const peer = participants.find(
        (p) => p.socketId !== mySocketIdRef.current,
      );
      if (peer?.name) setPeerName(peer.name);
    };

    const handleRemoteOffer = async (offer) => {
      if (!offer) return;
      if (!peerConnection.current) {
        pendingOfferRef.current = offer;
        return;
      }
      try {
        isOffererRef.current = false;
        // If we somehow also made an offer, prefer answering (we are the waiter)
        if (peerConnection.current.signalingState === "have-local-offer") {
          try {
            await peerConnection.current.setLocalDescription({
              type: "rollback",
            });
          } catch (_) {
            // Rollback unsupported — recreate PC and re-add tracks
            await createPeerConnectionRef.current?.();
          }
          offerSentRef.current = false;
        }
        await peerConnection.current.setRemoteDescription(
          offer instanceof RTCSessionDescription
            ? offer
            : new RTCSessionDescription(offer),
        );
        console.log(
          "[live] received offer:",
          summarizeSdp(
            offer.sdp || peerConnection.current.remoteDescription?.sdp,
          ),
        );
        await flushPendingIceRef.current?.();

        // Defensive re-sync: guarantee our own tracks are attached before
        // answering. A sendrecv transceiver with no sender track gets
        // answered recvonly regardless of its configured direction, so if
        // ours ever ends up empty here for any reason, fix it up now rather
        // than silently going one-way for the whole negotiation.
        const localForAnswer = localStream.current;
        for (const t of peerConnection.current.getTransceivers()) {
          const kind = t.receiver?.track?.kind || t.sender?.track?.kind;
          if (!kind) continue;

          console.log(
            `[live] transceiver(${kind}) before resync — direction: ${t.direction}, currentDirection: ${t.currentDirection}, hasSenderTrack: ${!!t.sender.track}`,
          );

          const localTrack =
            kind === "video"
              ? (isScreenSharingRef.current &&
                  screenTrackRef.current?.readyState === "live" &&
                  screenTrackRef.current) ||
                localForAnswer?.getVideoTracks?.()?.[0]
              : localForAnswer?.getAudioTracks?.()?.[0];

          if (!t.sender.track && localTrack) {
            try {
              await t.sender.replaceTrack(localTrack);
              console.log(
                `[live] re-synced outbound ${kind} track before answering:`,
                localTrack.id,
              );
            } catch (err) {
              console.error(
                `[live] re-sync replaceTrack(${kind}) failed:`,
                err,
              );
            }
          }

          // Belt-and-braces: force the direction we actually want, regardless
          // of whatever narrowed it (rollback, a prior negotiation, etc.) —
          // createAnswer() only offers sendrecv if the transceiver itself
          // still says sendrecv at the moment it's called.
          if (localTrack && t.direction !== "sendrecv") {
            console.warn(
              `[live] transceiver(${kind}) direction was "${t.direction}", forcing sendrecv`,
            );
            t.direction = "sendrecv";
          }
        }

        const answer = await peerConnection.current.createAnswer();
        await peerConnection.current.setLocalDescription(answer);
        console.log(
          "[live] sending answer:",
          summarizeSdp(peerConnection.current.localDescription?.sdp),
        );
        socket.emit("answer", {
          roomId,
          answer: peerConnection.current.localDescription,
        });
        pendingOfferRef.current = null;
        setAvStatus("connecting");
      } catch (err) {
        console.error("Error handling offer:", err);
        setAvStatus("failed");
      }
    };

    const onOffer = async (offer) => {
      await handleRemoteOffer(offer);
    };

    const onAnswer = async (answer) => {
      try {
        if (!peerConnection.current || !answer) return;
        if (peerConnection.current.signalingState === "stable") return;
        await peerConnection.current.setRemoteDescription(
          answer instanceof RTCSessionDescription
            ? answer
            : new RTCSessionDescription(answer),
        );
        console.log(
          "[live] received answer:",
          summarizeSdp(
            answer.sdp || peerConnection.current.remoteDescription?.sdp,
          ),
        );
        await flushPendingIceRef.current?.();
      } catch (err) {
        console.error("Error handling answer:", err);
      }
    };

    const onIceCandidate = async (candidate) => {
      try {
        if (!candidate) return;
        const pc = peerConnection.current;
        if (!pc) return;
        const ice =
          candidate instanceof RTCIceCandidate
            ? candidate
            : new RTCIceCandidate(candidate);
        if (!pc.remoteDescription) {
          pendingIceRef.current.push(ice);
          return;
        }
        await pc.addIceCandidate(ice);
      } catch (err) {
        console.error("Error adding ICE candidate:", err);
      }
    };

    const onIsMuted = (muted) => setIsRemoteMuted(!!muted);

    const onChatMessage = (msg) => {
      if (!msg?.text) return;
      setChatMessages((prev) => {
        if (msg.id && prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg].slice(-100);
      });
      const mine = msg.socketId && msg.socketId === mySocketIdRef.current;
      if (!mine) {
        playMessageSound();
        if (!chatOpenRef.current) {
          setChatUnread((n) => n + 1);
        }
      }
    };

    const onReaction = (payload) => {
      const emoji = payload?.emoji;
      if (!emoji) return;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setReactions((prev) => [...prev, { id, emoji, target: "remote" }]);
      setTimeout(() => {
        setReactions((prev) => prev.filter((r) => r.id !== id));
      }, REACTION_DURATION_MS);
    };

    const onMediaRelay = (packet) => {
      if (!packet || packet.from === mySocketIdRef.current) return;
      if (!mediaRelayRef.current) {
        startServerRelayRef.current?.();
      }
      mediaRelayRef.current?.onPacket(packet);
      setHasRemoteStream(true);
      setAvStatus((s) => (s === "connected" ? s : "relay"));
      setRelayMode(true);
      relayModeRef.current = true;
    };

    const onMediaRelayMode = (payload) => {
      if (payload?.from === mySocketIdRef.current) return;
      if (payload?.enabled) {
        // Peer switched to relay — join them so both send/receive
        startServerRelayRef.current?.();
        mediaRelayRef.current?.onRemoteMode(true);
      } else {
        mediaRelayRef.current?.onRemoteMode(false);
      }
    };

    const onScreenSharing = (payload) => {
      const sharing =
        typeof payload === "object" && payload !== null
          ? !!payload.isSharing
          : !!payload;
      const sharerId =
        typeof payload === "object" && payload !== null
          ? payload.sharerId
          : null;
      const me = mySocketIdRef.current;
      // Ignore our own echo; only track peer share state
      if (sharerId && me && sharerId === me) return;

      isRemoteScreenSharingRef.current = sharing;
      setIsRemoteScreenSharing(sharing);

      if (sharing && isScreenSharingRef.current) {
        // Peer took the stage — release locally (message comes from preempted event)
        stopScreenShareRef.current?.({ silent: true });
      }

      // Attach remote stream to stage when peer starts sharing. The stage
      // <video> only mounts once isRemoteScreenSharing flips true (conditional
      // render), and that state update hasn't committed yet at this point in
      // the handler — stageVideoRef.current is still null, so binding here
      // synchronously is a no-op. Defer to the next frame so it runs after
      // the DOM commits; the [isRemoteScreenSharing] effect also re-binds
      // once it re-runs, so this is a reinforcing second path, not the only one.
      if (sharing && remoteStreamRef.current) {
        requestAnimationFrame(() => {
          if (remoteStreamRef.current) {
            bindRemotePlaybackRef.current?.(remoteStreamRef.current);
          }
        });
      }
      if (!sharing && stageVideoRef.current && !isScreenSharingRef.current) {
        stageVideoRef.current.srcObject = null;
      }
    };

    const onScreenSharePreempted = (payload) => {
      if (!isScreenSharingRef.current) return;
      stopScreenShareRef.current?.({ silent: true });
      message.warning({
        content: `${payload?.byName || "Peer"} started sharing — only one screen at a time`,
        key: "screen-preempted",
        duration: 3,
      });
    };

    const onUserLeft = (response) => {
      message.info(response?.message || "Participant left");
      setHasRemoteStream(false);
      setIsRemoteMuted(false);
      isRemoteScreenSharingRef.current = false;
      setIsRemoteScreenSharing(false);
      setPeerName("Peer");
      peerSocketIdRef.current = null;
      setTypingInfo(null);
      clearTypingWidgetRef.current?.();
      remoteStreamRef.current = null;
      pendingIceRef.current = [];
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
      if (stageVideoRef.current && !isScreenSharingRef.current) {
        stageVideoRef.current.srcObject = null;
      }
      offerSentRef.current = false;
      setRoomSize(response?.size ?? 1);
    };

    const onRoomEnded = (payload) => {
      message.warning({
        content: payload?.message || "The host ended the session",
        key: "room-ended",
        duration: 3,
      });
      cleanupMediaRef.current?.();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      navigate("/live-coding");
    };

    // Host removed just this guest — unlike room_ended, the room stays open
    // for the host, but this guest needs to bail exactly the same way (drop
    // media, disconnect, leave the page) so they don't linger on a dead
    // session or silently auto-rejoin.
    const onRemovedFromRoom = (payload) => {
      message.warning({
        content: payload?.message || "The host removed you from the room",
        key: "removed-from-room",
        duration: 4,
      });
      cleanupMediaRef.current?.();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      navigate("/live-coding");
    };

    // The peer's network dropped or they refreshed — the server holds their
    // seat open for a grace period rather than ending the session, so just
    // let them know instead of treating it as a real departure (that's what
    // user_left / room_ended are for, once the grace period actually expires).
    const onPeerConnectionLost = (payload) => {
      message.warning({
        content: `${payload?.name || "Your peer"}'s connection dropped — waiting for them to reconnect…`,
        key: "peer-connection-lost",
        duration: 0,
      });
    };

    const onPeerReconnected = (payload) => {
      message.success({
        content: `${payload?.name || "Your peer"} reconnected`,
        key: "peer-connection-lost",
        duration: 2,
      });
    };

    const onConnectError = (error) => {
      console.error("Connection Error:", error);
      message.error("Failed to connect to the server");
    };

    // socket.io-client reconnects automatically after a network blip, but it
    // gets a brand-new socket id — the server's in-memory room no longer knows
    // us, so we must explicitly rejoin (and rebuild the PC) or A/V + screen
    // share silently stay dead until a manual page refresh.
    const onSocketConnect = async () => {
      if (!hasJoinAttemptRef.current) return; // initial connect — startMedia() drives the first join
      console.warn("[live] socket reconnected — rejoining room");
      message.info({
        content: "Reconnected — restoring session…",
        key: "live-reconnect",
        duration: 2,
      });
      offerSentRef.current = false;
      makingOfferRef.current = false;
      isOffererRef.current = false;
      iceRestartAttemptsRef.current = 0;
      pendingIceRef.current = [];
      pendingOfferRef.current = null;
      await createPeerConnectionRef.current?.();
      socket.emit("join_room", {
        roomId,
        name: displayName,
        viewOnly: urlViewOnly,
        clientId: clientIdRef.current,
        isCreator: isCreatorRef.current,
      });
    };

    const onSocketDisconnect = (reason) => {
      console.warn("[live] socket disconnected:", reason);
      if (joinedOnceRef.current) {
        setAvStatus("connecting");
      }
    };

    socket.on("connect", onSocketConnect);
    socket.on("disconnect", onSocketDisconnect);
    socket.on("room_full", onRoomFull);
    socket.on("waiting_for_host", onWaitingForHost);
    socket.on("join_denied", onJoinDenied);
    socket.on("join_request", onJoinRequest);
    socket.on("join_request_cancelled", onJoinRequestCancelled);
    socket.on("joined_room", onJoinedRoom);
    socket.on("user_joined", onUserJoined);
    socket.on("room_size", onRoomSize);
    socket.on("code_update", onCodeUpdate);
    socket.on("typing", onTyping);
    socket.on("run_result", onRunResult);
    socket.on("view_only", onViewOnly);
    socket.on("participants_update", onParticipantsUpdate);
    socket.on("offer", onOffer);
    socket.on("answer", onAnswer);
    socket.on("ice-candidate", onIceCandidate);
    socket.on("isMuted", onIsMuted);
    socket.on("chat_message", onChatMessage);
    socket.on("reaction", onReaction);
    socket.on("media_relay", onMediaRelay);
    socket.on("media_relay_mode", onMediaRelayMode);
    socket.on("screen_sharing", onScreenSharing);
    socket.on("screen_share_preempted", onScreenSharePreempted);
    socket.on("user_left", onUserLeft);
    socket.on("room_ended", onRoomEnded);
    socket.on("removed_from_room", onRemovedFromRoom);
    socket.on("peer_connection_lost", onPeerConnectionLost);
    socket.on("peer_reconnected", onPeerReconnected);
    socket.on("connect_error", onConnectError);

    let cancelled = false;

    const startMedia = async () => {
      // Prefetch TURN/STUN before getUserMedia so PC is ready ASAP
      try {
        const config = await fetchIceConfig({ force: true });
        iceConfigRef.current = config.iceServers;
        if (!config.hasTurn) {
          message.warning({
            content:
              "Video may fail across networks — TURN server not configured on server.",
            key: "no-turn",
            duration: 5,
          });
        }
      } catch (_) {
        /* fall back inside createPeerConnection */
      }

      const micId = selectedMicIdRef.current;
      const camId = selectedCameraIdRef.current;
      const audioConstraint = micId
        ? {
            deviceId: { ideal: micId },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        : {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          };
      const videoConstraint = camId
        ? {
            deviceId: { ideal: camId },
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: "user",
          }
        : {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: "user",
          };

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraint,
          audio: audioConstraint,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        localStream.current = stream;
        rawVideoTrackRef.current = stream.getVideoTracks()[0] || null;

        stream.getAudioTracks().forEach((track) => {
          track.enabled = !isMutedRef.current;
        });
        stream.getVideoTracks().forEach((track) => {
          track.enabled = videoEnabledRef.current;
        });

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.play?.().catch(() => {});
        }

        // Enumerate devices after permission (labels become available)
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          setAudioDevices(devices.filter((d) => d.kind === "audioinput"));
          setVideoDevices(devices.filter((d) => d.kind === "videoinput"));
          setOutputDevices(devices.filter((d) => d.kind === "audiooutput"));
          const audioTrack = stream.getAudioTracks()[0];
          const videoTrack = stream.getVideoTracks()[0];
          if (audioTrack?.getSettings?.().deviceId) {
            setSelectedMicId(audioTrack.getSettings().deviceId);
          }
          if (videoTrack?.getSettings?.().deviceId) {
            setSelectedCameraId(videoTrack.getSettings().deviceId);
          }
          const savedSpeaker = localStorage.getItem(SPEAKER_DEVICE_KEY);
          if (savedSpeaker) {
            setSelectedSpeakerId(savedSpeaker);
            applySinkId(remoteVideoRef.current, savedSpeaker);
            applySinkId(stageVideoRef.current, savedSpeaker);
          }
        } catch (_) {
          /* ignore */
        }

        await createPeerConnectionRef.current?.();

        socket.emit("join_room", {
          roomId,
          name: displayName,
          viewOnly: urlViewOnly,
          clientId: clientIdRef.current,
          isCreator: isCreatorRef.current,
        });

        // Answer any offer that arrived while PC was being created
        if (pendingOfferRef.current) {
          const queued = pendingOfferRef.current;
          pendingOfferRef.current = null;
          await handleRemoteOffer(queued);
        }
      } catch (error) {
        console.error("Error starting video stream:", error);
        message.warning("Camera/mic unavailable — code sharing still works");
        await createPeerConnectionRef.current?.();
        socket.emit("join_room", {
          roomId,
          name: displayName,
          viewOnly: urlViewOnly,
          clientId: clientIdRef.current,
          isCreator: isCreatorRef.current,
        });
        if (pendingOfferRef.current) {
          const queued = pendingOfferRef.current;
          pendingOfferRef.current = null;
          await handleRemoteOffer(queued);
        }
      }
    };

    startMedia();

    return () => {
      cancelled = true;
      if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (remoteTypingClearRef.current)
        clearTimeout(remoteTypingClearRef.current);
      socket.off("connect", onSocketConnect);
      socket.off("disconnect", onSocketDisconnect);
      socket.off("room_full", onRoomFull);
      socket.off("waiting_for_host", onWaitingForHost);
      socket.off("join_denied", onJoinDenied);
      socket.off("join_request", onJoinRequest);
      socket.off("join_request_cancelled", onJoinRequestCancelled);
      socket.off("joined_room", onJoinedRoom);
      socket.off("user_joined", onUserJoined);
      socket.off("room_size", onRoomSize);
      socket.off("code_update", onCodeUpdate);
      socket.off("typing", onTyping);
      socket.off("run_result", onRunResult);
      socket.off("view_only", onViewOnly);
      socket.off("participants_update", onParticipantsUpdate);
      socket.off("offer", onOffer);
      socket.off("answer", onAnswer);
      socket.off("ice-candidate", onIceCandidate);
      socket.off("isMuted", onIsMuted);
      socket.off("chat_message", onChatMessage);
      socket.off("reaction", onReaction);
      socket.off("media_relay", onMediaRelay);
      socket.off("media_relay_mode", onMediaRelayMode);
      socket.off("screen_sharing", onScreenSharing);
      socket.off("screen_share_preempted", onScreenSharePreempted);
      socket.off("user_left", onUserLeft);
      socket.off("room_ended", onRoomEnded);
      socket.off("removed_from_room", onRemovedFromRoom);
      socket.off("peer_connection_lost", onPeerConnectionLost);
      socket.off("peer_reconnected", onPeerReconnected);
      socket.off("connect_error", onConnectError);
      // Only emit leave on unmount if we're not ending via end_room
      if (socket.connected) {
        socket.emit("leave_room");
      }
      socket.disconnect();
      socketRef.current = null;
      stopServerRelayRef.current?.();
      cleanupMediaRef.current?.();
    };
  }, [roomId, nameReady, displayName, urlViewOnly, navigate]);

  // Auto-scroll chat to latest
  useEffect(() => {
    if (!chatOpen) return;
    const el = chatListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chatMessages, chatOpen]);

  // Re-attach remote stream if video element remounts
  useEffect(() => {
    if (!hasRemoteStream || !remoteStreamRef.current) return;
    bindRemotePlayback(remoteStreamRef.current);
  }, [hasRemoteStream, bindRemotePlayback]);

  // Re-attach the local preview once the real room UI mounts. getUserMedia
  // runs immediately on join, but a guest waiting on host admission is shown
  // a different screen (no <video> element yet), so that first srcObject
  // assignment silently no-ops — this catches up once admitted.
  useEffect(() => {
    if (localVideoRef.current && localStream.current) {
      if (localVideoRef.current.srcObject !== localStream.current) {
        localVideoRef.current.srcObject = localStream.current;
      }
      localVideoRef.current.play?.().catch(() => {});
    }
  }, [awaitingAdmission]);

  // Keep the main screen stage in sync (local share or peer share)
  useEffect(() => {
    const stage = stageVideoRef.current;
    if (!stage) return;

    if (isScreenSharing && screenStreamRef.current) {
      if (stage.srcObject !== screenStreamRef.current) {
        stage.srcObject = screenStreamRef.current;
      }
      stage.muted = true;
      stage.play?.().catch(() => {});
      return;
    }

    if (isRemoteScreenSharing && remoteStreamRef.current) {
      bindRemotePlayback(remoteStreamRef.current);
      return;
    }

    if (!isScreenSharing) {
      stage.srcObject = null;
    }
  }, [
    isScreenSharing,
    isRemoteScreenSharing,
    hasRemoteStream,
    bindRemotePlayback,
  ]);

  // Unlock remote audio after a user gesture (browser autoplay policies)
  useEffect(() => {
    const unlock = () => {
      const audio = remoteAudioRef.current;
      if (audio?.srcObject) {
        audio.muted = false;
        audio.play?.().catch(() => {});
      }
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [roomId, nameReady]);

  // When peer starts sharing, collapse editor by default for focus
  useEffect(() => {
    if (isRemoteScreenSharing || isScreenSharing) {
      setEditorOpenDuringShare(false);
    }
  }, [isRemoteScreenSharing, isScreenSharing]);

  // replaceTrack keeps the same transceiver — no renegotiation needed.
  const replaceOutboundVideoTrack = useCallback(async (track) => {
    const pc = peerConnection.current;
    if (!pc || !track) return false;

    try {
      if ("contentHint" in track) track.contentHint = "detail";
    } catch (_) {
      /* ignore */
    }

    let sender =
      pc.getSenders().find((s) => s.track?.kind === "video") ||
      pc.getTransceivers().find((t) => t.receiver?.track?.kind === "video")
        ?.sender ||
      null;

    if (!sender) {
      try {
        sender = pc.addTransceiver("video", { direction: "sendrecv" }).sender;
      } catch (err) {
        console.warn("addTransceiver(video) failed:", err);
        return false;
      }
    }

    try {
      await sender.replaceTrack(track);
      return true;
    } catch (err) {
      console.warn("replaceTrack failed:", err);
      return false;
    }
  }, []);

  const notifyScreenShare = useCallback(
    (sharing) => {
      if (socketRef.current && roomId) {
        socketRef.current.emit("toggle_screen_share", {
          roomId,
          isSharing: !!sharing,
        });
      }
    },
    [roomId],
  );
  notifyScreenShareRef.current = notifyScreenShare;

  const syncStageVideo = useCallback(() => {
    const stage = stageVideoRef.current;
    if (!stage) return;

    if (isScreenSharingRef.current && screenStreamRef.current) {
      if (stage.srcObject !== screenStreamRef.current) {
        stage.srcObject = screenStreamRef.current;
      }
      stage.muted = true;
      stage.play?.().catch(() => {});
      return;
    }

    if (isRemoteScreenSharingRef.current && remoteStreamRef.current) {
      bindRemotePlaybackRef.current?.(remoteStreamRef.current);
      return;
    }

    stage.srcObject = null;
  }, []);

  const stopScreenShare = useCallback(
    async ({ silent = false } = {}) => {
      const wasSharing = isScreenSharingRef.current;
      isScreenSharingRef.current = false;
      setIsScreenSharing(false);

      if (screenTrackRef.current) {
        try {
          screenTrackRef.current.onended = null;
        } catch (_) {
          /* ignore */
        }
        try {
          screenTrackRef.current.stop();
        } catch (_) {
          /* ignore */
        }
        screenTrackRef.current = null;
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch (_) {
            /* ignore */
          }
        });
        screenStreamRef.current = null;
      }

      // Restore camera into local preview + outbound sender
      const camera = rawVideoTrackRef.current;
      if (camera && camera.readyState !== "ended") {
        camera.enabled = videoEnabledRef.current;
        await replaceOutboundVideoTrack(camera);
        // See startScreenShare — replaceTrack() alone doesn't reliably make
        // the guest's decoder pick up the source change back to the camera.
        if (wasSharing && !relayModeRef.current && peerConnection.current) {
          await sendOfferRef.current?.({ iceRestart: true });
        }
      }
      if (localVideoRef.current && localStream.current) {
        localVideoRef.current.srcObject = localStream.current;
        localVideoRef.current.play?.().catch(() => {});
      }

      // Don't wipe the stage if the peer is (or just became) the sharer
      syncStageVideo();

      if (wasSharing) {
        notifyScreenShare(false);
        if (!silent) message.success("Screen sharing stopped");
      }
    },
    [notifyScreenShare, replaceOutboundVideoTrack, syncStageVideo],
  );

  const startScreenShare = useCallback(async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      message.error("Screen sharing is not supported in this browser");
      return;
    }
    if (isScreenSharingRef.current || screenShareStartingRef.current) return;

    const beginShare = async () => {
      screenShareStartingRef.current = true;
      let displayStream = null;
      try {
        // Blur uses replaceTrack on the same video sender — turn it off first
        if (isBlurred) {
          setIsBlurred(false);
        }

        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: "always",
            frameRate: { ideal: 15, max: 30 },
          },
          audio: false,
        });

        // Aborted / raced while picker was open
        if (isScreenSharingRef.current) {
          displayStream.getTracks().forEach((t) => t.stop());
          return;
        }

        const displayTrack = displayStream.getVideoTracks()[0];
        if (!displayTrack) {
          displayStream.getTracks().forEach((t) => t.stop());
          message.error("No screen video track available");
          return;
        }

        // Stop any previous share leftovers
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach((t) => t.stop());
        }

        screenStreamRef.current = displayStream;
        screenTrackRef.current = displayTrack;
        isScreenSharingRef.current = true;
        setIsScreenSharing(true);
        // We own the stage now — clear remote-share UI flag locally
        isRemoteScreenSharingRef.current = false;
        setIsRemoteScreenSharing(false);

        const replaced = await replaceOutboundVideoTrack(displayTrack);
        if (!replaced) {
          message.error(
            "Could not send screen to peer — try Retry A/V first, then share again",
          );
          displayTrack.stop();
          displayStream.getTracks().forEach((t) => t.stop());
          screenStreamRef.current = null;
          screenTrackRef.current = null;
          isScreenSharingRef.current = false;
          setIsScreenSharing(false);
          return;
        }

        const iceState = peerConnection.current?.iceConnectionState;
        if (
          iceState === "failed" ||
          iceState === "disconnected" ||
          iceState === "closed" ||
          !peerConnection.current
        ) {
          message.warning(
            "Screen is sharing on your side, but peer connection is down — fixing…",
          );
          iceRestartAttemptsRef.current = 0;
          clearIceCache();
          offerSentRef.current = false;
          await createPeerConnectionRef.current?.();
          if (roomSizeRef.current >= 2) {
            await sendOfferRef.current?.({ iceRestart: true });
          }
        } else if (iceState !== "connected" && iceState !== "completed") {
          message.info(
            "Connecting to peer… they will see your screen once linked",
          );
        }

        // Keep camera in the side preview; main stage shows the screen
        if (localVideoRef.current && localStream.current) {
          localVideoRef.current.srcObject = localStream.current;
          localVideoRef.current.play?.().catch(() => {});
        }
        syncStageVideo();

        setEditorOpenDuringShare(false);

        displayTrack.onended = () => {
          stopScreenShare({ silent: false });
        };

        // replaceTrack() alone points the sender at the screen track, but
        // Chrome's encoder for an already-established sender doesn't reliably
        // pick up the new source — confirmed via live two-peer testing: the
        // guest keeps decoding real frames (framesDecoded climbing) at the
        // OLD resolution, rendering the frozen/stale camera image forever
        // instead of the screen, even though the sender is genuinely on the
        // right track. A fresh offer/answer is what actually fixes it.
        if (!relayModeRef.current && peerConnection.current) {
          await sendOfferRef.current?.({ iceRestart: true });
        }

        notifyScreenShare(true);
        message.success("You are sharing your screen");
      } catch (err) {
        if (displayStream) {
          displayStream.getTracks().forEach((t) => {
            try {
              t.stop();
            } catch (_) {
              /* ignore */
            }
          });
        }
        if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
          return;
        }
        console.error("Screen share failed:", err);
        message.error("Could not start screen sharing");
        isScreenSharingRef.current = false;
        setIsScreenSharing(false);
        screenStreamRef.current = null;
        screenTrackRef.current = null;
      } finally {
        screenShareStartingRef.current = false;
      }
    };

    // Only one screen at a time — confirm take-over if peer is already sharing
    if (isRemoteScreenSharingRef.current) {
      Modal.confirm({
        title: "Replace peer’s screen share?",
        content:
          "Only one person can share at a time. Starting yours will stop their share for both of you.",
        okText: "Share my screen",
        cancelText: "Cancel",
        onOk: () => beginShare(),
      });
      return;
    }

    await beginShare();
  }, [
    isBlurred,
    notifyScreenShare,
    replaceOutboundVideoTrack,
    stopScreenShare,
    syncStageVideo,
  ]);

  const toggleScreenShare = useCallback(() => {
    if (isScreenSharingRef.current) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  }, [startScreenShare, stopScreenShare]);

  stopScreenShareRef.current = stopScreenShare;

  // BodyPix blur
  useEffect(() => {
    blurActiveRef.current = isBlurred;

    if (!isBlurred) {
      blurTrackRef.current = null;
      if (blurRafRef.current) {
        cancelAnimationFrame(blurRafRef.current);
        blurRafRef.current = null;
      }
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      // Don't clobber an active (or starting) screen share when blur turns
      // off. startScreenShare() flips isBlurred off first, then awaits the
      // native screen picker for as long as the user takes — isScreenSharingRef
      // only becomes true after that resolves, so without the starting-ref
      // check this effect races in and replaces the outbound video sender
      // back to the raw camera while the picker is still open.
      if (
        !isScreenSharingRef.current &&
        !screenShareStartingRef.current &&
        rawVideoTrackRef.current
      ) {
        replaceOutboundVideoTrack(rawVideoTrackRef.current).then(() => {
          // Mirror the on-switch fix: swapping back to the raw camera is
          // just as much a "genuinely different source" as swapping to the
          // canvas was, and needs the same nudge to actually show up.
          if (!relayModeRef.current && peerConnection.current) {
            sendOfferRef.current?.({ iceRestart: true });
          }
        });
      }
      return undefined;
    }

    // Can't blur while screen sharing
    if (isScreenSharingRef.current) {
      setIsBlurred(false);
      return undefined;
    }

    let cancelled = false;
    let blurStream = null;

    const runBlur = async () => {
      try {
        if (!bodyPixNetRef.current) {
          await tf.ready();
          try {
            await tf.setBackend("webgl");
          } catch (_) {
            /* fallback */
          }
          bodyPixNetRef.current = await bodyPix.load({
            architecture: "MobileNetV1",
            outputStride: 16,
            multiplier: 0.5,
            quantBytes: 2,
          });
        }

        const video = localVideoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || cancelled) return;

        // Wait for video dimensions
        if (!video.videoWidth) {
          await new Promise((resolve) => {
            const onMeta = () => {
              video.removeEventListener("loadedmetadata", onMeta);
              resolve();
            };
            video.addEventListener("loadedmetadata", onMeta);
            setTimeout(resolve, 800);
          });
        }

        const videoWidth = video.videoWidth || 640;
        const videoHeight = video.videoHeight || 480;
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        // bodyPix's drawBokehEffect reads image.width/height (the HTML
        // width/height attributes, which default to 0 on a <video> that
        // never had them set) rather than videoWidth/videoHeight — without
        // this it collapses its internal canvas to 0x0 and every frame
        // throws "canvas element with a width or height of 0".
        video.width = videoWidth;
        video.height = videoHeight;

        try {
          blurStream = canvas.captureStream(24);
          const blurTrack = blurStream.getVideoTracks()[0];
          if (blurTrack) {
            blurTrackRef.current = blurTrack;
            await replaceOutboundVideoTrack(blurTrack);
            // Same fix as startScreenShare: replaceTrack() alone points the
            // sender at the canvas track, but the guest's decoder doesn't
            // reliably pick up a source swap this different (raw camera ->
            // canvas.captureStream) without a fresh offer/answer — otherwise
            // they keep rendering the last real camera frame forever.
            if (!relayModeRef.current && peerConnection.current) {
              await sendOfferRef.current?.({ iceRestart: true });
            }
          }
        } catch (capErr) {
          console.warn("canvas.captureStream unavailable:", capErr);
        }

        const processFrame = async () => {
          if (!blurActiveRef.current || cancelled || isScreenSharingRef.current)
            return;
          try {
            const net = bodyPixNetRef.current;
            if (!net || !localVideoRef.current) return;

            const segmentation = await net.segmentPerson(
              localVideoRef.current,
              {
                flipHorizontal: false,
                internalResolution: "medium",
                segmentationThreshold: 0.65,
              },
            );

            if (bgModeRef.current === "color") {
              compositeSegmentedBackground(
                canvas,
                localVideoRef.current,
                segmentation,
                bgScratchCanvasRef,
                (ctx, w, h) => {
                  ctx.fillStyle = bgColorRef.current;
                  ctx.fillRect(0, 0, w, h);
                },
              );
            } else if (bgModeRef.current === "image" && bgImageElRef.current) {
              compositeSegmentedBackground(
                canvas,
                localVideoRef.current,
                segmentation,
                bgScratchCanvasRef,
                (ctx, w, h) => {
                  const img = bgImageElRef.current;
                  const scale = Math.max(w / img.width, h / img.height);
                  const dw = img.width * scale;
                  const dh = img.height * scale;
                  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
                },
              );
            } else {
              // drawBokehEffect keeps the person sharp and gaussian-blurs
              // everything else — drawMask+toMask (the old approach here)
              // just painted a flat opaque color over the background instead.
              bodyPix.drawBokehEffect(
                canvas,
                localVideoRef.current,
                segmentation,
                blurAmountRef.current,
                8,
                false,
              );
            }
          } catch (frameErr) {
            console.warn("BodyPix frame skipped:", frameErr);
          }

          if (
            blurActiveRef.current &&
            !cancelled &&
            !isScreenSharingRef.current
          ) {
            blurRafRef.current = requestAnimationFrame(processFrame);
          }
        };

        processFrame();
      } catch (err) {
        console.error("BodyPix blur failed:", err);
        message.warning("Background blur unavailable on this device");
        setIsBlurred(false);
      }
    };

    runBlur();

    return () => {
      cancelled = true;
      if (blurRafRef.current) {
        cancelAnimationFrame(blurRafRef.current);
        blurRafRef.current = null;
      }
      if (blurStream) {
        blurStream.getTracks().forEach((t) => t.stop());
        if (blurTrackRef.current && blurStream.getVideoTracks().includes(blurTrackRef.current)) {
          blurTrackRef.current = null;
        }
      }
    };
  }, [isBlurred, replaceOutboundVideoTrack]);

  const getCursor = () => {
    const editor = editorRef.current;
    if (!editor) return null;
    const pos = editor.getPosition();
    if (!pos) return null;
    return { lineNumber: pos.lineNumber, column: pos.column };
  };

  const emitCodeUpdate = useCallback(
    (nextCode, nextLanguage) => {
      if (!socketRef.current || !roomId || isViewOnlyRef.current) return;
      revisionRef.current += 1;
      socketRef.current.emit("code_update", {
        roomId,
        code: nextCode,
        language: nextLanguage || language,
        cursor: getCursor(),
        name: displayNameRef.current,
        revision: revisionRef.current,
      });
    },
    [roomId, language],
  );

  const emitTyping = useCallback(() => {
    if (!socketRef.current || !roomId || isViewOnlyRef.current) return;
    socketRef.current.emit("typing", {
      roomId,
      name: displayNameRef.current,
      cursor: getCursor(),
      isTyping: true,
    });
  }, [roomId]);

  const handleEditorChange = (value) => {
    if (isViewOnly || applyingRemoteRef.current) return;
    const next = value ?? "";
    setCode(next);

    if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
    emitTimerRef.current = setTimeout(() => emitCodeUpdate(next), 100);

    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    emitTyping();
    typingTimerRef.current = setTimeout(() => {
      if (socketRef.current && roomId) {
        socketRef.current.emit("typing", {
          roomId,
          name: displayNameRef.current,
          isTyping: false,
        });
      }
    }, 1200);
  };

  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Inject remote cursor styles once
    if (!document.getElementById("live-remote-cursor-css")) {
      const style = document.createElement("style");
      style.id = "live-remote-cursor-css";
      style.textContent = `
        .remote-cursor-line { background: rgba(88, 166, 255, 0.08); }
        .remote-cursor-caret {
          border-left: 2px solid #58a6ff;
          margin-left: -1px;
        }
        .remote-cursor-glyph {
          background: #58a6ff;
          width: 4px !important;
          margin-left: 3px;
        }
        .remote-typing-label {
          background: #58a6ff;
          color: #fff;
          font-size: 11px;
          font-weight: 600;
          padding: 1px 6px;
          border-radius: 3px;
          white-space: nowrap;
          pointer-events: none;
          line-height: 16px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.35);
        }
      `;
      document.head.appendChild(style);
    }

    editor.onDidChangeCursorPosition(() => {
      if (applyingRemoteRef.current || isViewOnlyRef.current) return;
      emitTyping();
    });
  };

  // Stable reference so Monaco's updateOptions() effect only re-runs when
  // font size or view-only state actually change, not on every keystroke
  // (a fresh object identity here forces a layout pass on every render).
  const monacoOptions = useMemo(
    () => ({
      ...getCrafterhackEditorOptions(fontSize),
      readOnly: isViewOnly,
      glyphMargin: true,
    }),
    [fontSize, isViewOnly],
  );

  const handleLanguageChange = (value) => {
    if (isViewOnly || !(isCompilerLanguage(value) || isNoteLanguage(value)))
      return;
    setLanguage(value);
    const nextCode = defaultCode[value] ?? getNoteLanguage(value)?.starter ?? "";
    setCode(nextCode);
    emitCodeUpdate(nextCode, value);
  };

  const handleRunCode = async () => {
    if (isViewOnly) {
      message.info("View-only mode — ask the host to run code");
      return;
    }
    if (isNoteLanguage(language)) {
      message.info("Switch to a programming language to run code");
      return;
    }
    setIsCompiling(true);
    setOutput("");
    setOutputMeta(`${displayName} is running…`);
    setOutputError(false);
    setOutputVisible(true);
    try {
      const requestBody = {
        source_code: code,
        language_id: languageMapping[language],
      };
      const { data } = await proxy.post("/code/compile", requestBody, {
        headers: { "Content-Type": "application/json" },
      });
      const result = data?.message ?? "No output";
      const success = data?.status === "Accepted";
      // Broadcast to room (including self via server)
      if (socketRef.current && roomId) {
        socketRef.current.emit("run_result", {
          roomId,
          name: displayName,
          output: result,
          success,
          language,
        });
      } else {
        setOutput(result);
        setOutputError(!success);
        setOutputMeta(`${displayName} ran the code`);
      }
    } catch (error) {
      console.error("Error:", error);
      const result = error?.response?.data?.message || "Compilation failed";
      if (socketRef.current && roomId) {
        socketRef.current.emit("run_result", {
          roomId,
          name: displayName,
          output: result,
          success: false,
          language,
        });
      } else {
        setOutput(result);
        setOutputError(true);
        setOutputMeta(`${displayName} ran the code (error)`);
      }
    } finally {
      setIsCompiling(false);
    }
  };

  // Same bindings as the problem page (matchProblemShortcut) — this editor
  // has no separate submit action, so both run and submit trigger Run.
  // Alt+F toggles full screen the same way it does on the problem/compiler
  // pages; Ctrl/Cmd +/-/0 zoom whichever video surface is currently on
  // screen (the same modifier the scroll-to-zoom handlers require).
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!shortcutsOn) return;

      const target = event.target;
      const inCodeEditor = Boolean(target?.closest?.(".monaco-editor"));
      const tag = target?.tagName?.toLowerCase();
      const typingInField =
        !inCodeEditor && (tag === "input" || tag === "textarea" || tag === "select");

      const action = matchProblemShortcut(event);
      if (action) {
        // Chat / name fields own plain typing — only let run/submit reach through.
        if (typingInField && action !== "run" && action !== "submit") return;

        event.preventDefault();
        event.stopPropagation();
        if (action === "run" || action === "submit") handleRunCode();
        else if (action === "close-tab") setOutputVisible((v) => !v);
        else if (action === "fullscreen") toggleFullscreen();
        return;
      }

      if (typingInField) return;

      const metaOrCtrl = event.metaKey || event.ctrlKey;
      if (metaOrCtrl && !event.altKey) {
        const activeZoom =
          isScreenSharing || isRemoteScreenSharing
            ? screenShareZoom
            : hasRemoteStream
              ? remoteVideoZoom
              : null;
        if (activeZoom) {
          if (event.key === "=" || event.key === "+") {
            event.preventDefault();
            activeZoom.zoomIn();
          } else if (event.key === "-" || event.key === "_") {
            event.preventDefault();
            activeZoom.zoomOut();
          } else if (event.key === "0") {
            event.preventDefault();
            activeZoom.reset();
          }
        }
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  });

  const startOutputResize = (e) => {
    e.preventDefault();
    const pointY = (ev) =>
      ev.touches?.[0]?.clientY ?? ev.changedTouches?.[0]?.clientY ?? ev.clientY;
    const startY = pointY(e);
    const startHeight = outputHeight;
    const maxH = isMobile ? 280 : 420;

    const onMove = (moveEvent) => {
      if (moveEvent.cancelable) moveEvent.preventDefault();
      // Dragging handle upward increases height
      const next = Math.min(
        maxH,
        Math.max(88, startHeight + (startY - pointY(moveEvent))),
      );
      setOutputHeight(next);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };

  const startSidePaneResize = (e) => {
    e.preventDefault();
    const pointX = (ev) =>
      ev.touches?.[0]?.clientX ?? ev.changedTouches?.[0]?.clientX ?? ev.clientX;
    const startX = pointX(e);
    const startWidth = sidePaneWidth;
    const maxW = Math.min(640, window.innerWidth - 320);

    const onMove = (moveEvent) => {
      if (moveEvent.cancelable) moveEvent.preventDefault();
      // The pane sits on the right edge — dragging the handle left widens it.
      const next = Math.min(
        maxW,
        Math.max(220, startWidth + (startX - pointX(moveEvent))),
      );
      setSidePaneWidth(next);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };

  // Video-only mode gives the tiles a lot more room, so the mute/camera/blur
  // controls overlaid on them can afford to be bigger and easier to hit.
  const bigControls = !isMobile && videoOnlyMode;
  const controlSize = bigControls ? "large" : "small";
  const controlIconSize = bigControls ? 20 : 14;

  const shareLink = useMemo(() => {
    if (!roomId) return "";
    const base = `${window.location.origin}/live-coding/${roomId}`;
    return shareAsViewOnly ? `${base}?viewOnly=1` : base;
  }, [roomId, shareAsViewOnly]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      message.success("Link copied to clipboard");
    } catch (_) {
      message.error("Could not copy link");
    }
  };

  const handleShareViewOnlyChange = (checked) => {
    setShareAsViewOnly(checked);
    // Broadcast to current peer so they become view-only without re-joining
    if (socketRef.current && roomId && !urlViewOnly) {
      socketRef.current.emit("set_view_only", { roomId, viewOnly: checked });
    }
  };

  const retryAvConnection = async () => {
    iceRestartAttemptsRef.current = 0;
    clearIceCache();
    offerSentRef.current = false;
    stopServerRelay();
    setAvStatus("connecting");
    message.loading({
      content: "Reconnecting audio/video…",
      key: "av-retry",
      duration: 2,
    });
    try {
      await createPeerConnectionRef.current?.();
      if (roomSizeRef.current >= 2) {
        await sendOfferRef.current?.({ iceRestart: true });
      }
      // If P2P still fails, relay kicks in automatically
      setTimeout(() => {
        if (
          roomSizeRef.current >= 2 &&
          !relayModeRef.current &&
          peerConnection.current?.connectionState !== "connected"
        ) {
          startServerRelay();
        }
      }, 6000);
    } catch (err) {
      console.error(err);
      startServerRelay();
    }
  };

  const sendChatMessage = () => {
    const text = chatDraft.trim();
    if (!text || !socketRef.current || !roomId) return;
    socketRef.current.emit("chat_message", {
      roomId,
      text,
      name: displayName,
    });
    setChatDraft("");
  };

  const openChatPanel = () => {
    setChatOpen(true);
    setChatUnread(0);
  };

  const toggleMute = () => {
    const stream = localStream.current || localVideoRef.current?.srcObject;
    if (!stream?.getAudioTracks) return;
    const nextMuted = !isMuted;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);
    // User gesture — also resume remote playback if autoplay was blocked
    if (remoteAudioRef.current?.srcObject) {
      remoteAudioRef.current.muted = false;
      remoteAudioRef.current.play?.().catch(() => {});
    }
    if (socketRef.current && roomId) {
      socketRef.current.emit("toggle_mute", { roomId, isMuted: nextMuted });
    }
  };

  const toggleVideo = () => {
    if (isScreenSharingRef.current) {
      const nextEnabled = !videoEnabled;
      videoEnabledRef.current = nextEnabled;
      setVideoEnabled(nextEnabled);
      message.info(
        nextEnabled
          ? "Camera will turn on when you stop sharing"
          : "Camera will stay off when you stop sharing",
      );
      return;
    }
    const stream = localStream.current || localVideoRef.current?.srcObject;
    if (!stream?.getVideoTracks) return;
    const nextEnabled = !videoEnabled;
    stream.getVideoTracks().forEach((track) => {
      track.enabled = nextEnabled;
    });
    videoEnabledRef.current = nextEnabled;
    setVideoEnabled(nextEnabled);
  };

  const requireNoScreenShare = () => {
    if (isScreenSharingRef.current) {
      message.warning("Turn off screen share before changing your background");
      return false;
    }
    return true;
  };

  const turnOffBackgroundEffect = () => {
    setIsBlurred(false);
    message.success("Background effect off");
    setBlurPickerOpen(false);
  };

  const applyBlurLevel = (amount) => {
    if (!requireNoScreenShare()) return;
    setBgMode("blur");
    setBlurAmount(amount);
    setIsBlurred(true);
    message.success(amount >= 16 ? "Strong background blur on" : "Background blur on");
    setBlurPickerOpen(false);
  };

  const applyBgColor = (c) => {
    if (!requireNoScreenShare()) return;
    setBgMode("color");
    setBgColor(c);
    setIsBlurred(true);
    message.success("Background color applied");
    setBlurPickerOpen(false);
  };

  const applyBgImage = (dataUrl) => {
    if (!requireNoScreenShare()) return;
    setBgMode("image");
    setBgImageUrl(dataUrl);
    setIsBlurred(true);
    message.success("Background image applied");
    setBlurPickerOpen(false);
  };

  const handleBgImageUpload = (file) => {
    if (!file) return false;
    if (!file.type?.startsWith("image/")) {
      message.error("Please choose an image file");
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => applyBgImage(reader.result);
    reader.onerror = () => message.error("Could not read that image");
    reader.readAsDataURL(file);
    return false; // prevent antd Upload's own auto-upload behavior
  };

  const sendReaction = useCallback(
    (emoji) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setReactions((prev) => [...prev, { id, emoji, target: "local" }]);
      setTimeout(() => {
        setReactions((prev) => prev.filter((r) => r.id !== id));
      }, REACTION_DURATION_MS);
      if (socketRef.current && roomId) {
        socketRef.current.emit("reaction", { roomId, emoji });
      }
      setReactionPickerOpen(false);
    },
    [roomId],
  );

  const leaveSession = () => {
    if (socketRef.current) {
      socketRef.current.emit("leave_room");
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    cleanupMedia();
    stopServerRelay();
    setChatMessages([]);
    setChatUnread(0);
    navigate("/live-coding");
  };

  const endSession = () => {
    Modal.confirm({
      title: "End session for everyone?",
      content:
        "This closes the room for you and your guest. They will be disconnected.",
      okText: "End session",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        if (socketRef.current && roomId) {
          socketRef.current.emit("end_room", { roomId });
          // room_ended handler navigates; also clean up locally as fallback
          setTimeout(() => {
            cleanupMedia();
            if (socketRef.current) {
              socketRef.current.disconnect();
              socketRef.current = null;
            }
            navigate("/live-coding");
          }, 150);
        } else {
          cleanupMedia();
          navigate("/live-coding");
        }
      },
    });
  };

  const refreshMediaDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioDevices(devices.filter((d) => d.kind === "audioinput"));
      setVideoDevices(devices.filter((d) => d.kind === "videoinput"));
      setOutputDevices(devices.filter((d) => d.kind === "audiooutput"));
    } catch (err) {
      console.warn("enumerateDevices failed:", err);
    }
  }, []);

  const switchSpeaker = useCallback(async (deviceId) => {
    if (!deviceId) return;
    if (!supportsAudioOutputSelect()) {
      message.warning("Speaker selection isn't supported in this browser");
      return;
    }
    setSelectedSpeakerId(deviceId);
    localStorage.setItem(SPEAKER_DEVICE_KEY, deviceId);
    selectedSpeakerIdRef.current = deviceId;

    try {
      await applySinkId(remoteAudioRef.current, deviceId);
      message.success("Speaker updated");
    } catch (err) {
      console.error("switchSpeaker failed:", err);
      message.error("Could not switch speaker");
    }
  }, []);

  const switchMic = useCallback(async (deviceId) => {
    if (!deviceId) return;
    setSelectedMicId(deviceId);
    localStorage.setItem(MIC_DEVICE_KEY, deviceId);
    selectedMicIdRef.current = deviceId;

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      const newTrack = newStream.getAudioTracks()[0];
      if (!newTrack) return;

      // Swap the sender onto the new track BEFORE touching the old one — once a
      // track backing an active RTCRtpSender is stopped, the sender must go
      // silent until replaceTrack() recovers it, so stopping first forces an
      // audible gap (heard by the guest as the audio glitching/dropping).
      const sender = peerConnection.current
        ?.getSenders()
        ?.find((s) => s.track?.kind === "audio");
      if (sender) {
        await sender.replaceTrack(newTrack);
      }

      const oldTrack = localStream.current?.getAudioTracks?.()?.[0];
      if (localStream.current && oldTrack) {
        localStream.current.removeTrack(oldTrack);
        localStream.current.addTrack(newTrack);
        oldTrack.stop();
      } else if (localStream.current) {
        localStream.current.addTrack(newTrack);
      }

      // Server-relay mode records audio via a MediaRecorder bound to the old
      // track — that track is now stopped, so the recorder must be rebuilt
      // against the new one or relay audio goes silent for the rest of the call.
      if (relayModeRef.current) {
        mediaRelayRef.current?.restartAudio?.();
      }

      // replaceTrack() alone updates what the sender *points at*, but Chrome's
      // encoder for an already-established sender does not reliably pick up
      // the new track's actual audio — confirmed via live two-peer testing:
      // the guest's receiver reports the track as live/unmuted/enabled the
      // whole time, yet no real audio ever arrives, indefinitely, until a
      // fresh offer/answer happens. Force one here.
      if (!relayModeRef.current && peerConnection.current) {
        sendOfferRef.current?.({ iceRestart: true });
      }

      newTrack.enabled = !isMutedRef.current;
      message.success("Microphone updated");
    } catch (err) {
      console.error("switchMic failed:", err);
      message.error("Could not switch microphone");
    }
  }, []);

  const switchCamera = useCallback(async (deviceId) => {
    if (!deviceId || isScreenSharingRef.current) {
      if (isScreenSharingRef.current) {
        message.info("Stop screen share before switching camera");
      }
      return;
    }
    setSelectedCameraId(deviceId);
    localStorage.setItem(CAMERA_DEVICE_KEY, deviceId);
    selectedCameraIdRef.current = deviceId;

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      const newTrack = newStream.getVideoTracks()[0];
      if (!newTrack) return;

      rawVideoTrackRef.current = newTrack;

      // Swap the sender onto the new track before stopping the old one — see
      // switchMic for why (avoids a forced blank/frozen gap on the guest side).
      const sender = peerConnection.current
        ?.getSenders()
        ?.find((s) => s.track?.kind === "video");
      if (sender) {
        await sender.replaceTrack(newTrack);
      }

      const oldTrack = localStream.current?.getVideoTracks?.()?.[0];
      if (localStream.current && oldTrack) {
        localStream.current.removeTrack(oldTrack);
        localStream.current.addTrack(newTrack);
        oldTrack.stop();
      } else if (localStream.current) {
        localStream.current.addTrack(newTrack);
      }

      if (localVideoRef.current && localStream.current) {
        localVideoRef.current.srcObject = localStream.current;
        localVideoRef.current.play?.().catch(() => {});
      }

      // See switchMic — replaceTrack() alone doesn't reliably make the guest's
      // side actually render the new source; force a fresh offer/answer.
      if (!relayModeRef.current && peerConnection.current) {
        sendOfferRef.current?.({ iceRestart: true });
      }

      newTrack.enabled = videoEnabledRef.current;
      message.success("Camera updated");
    } catch (err) {
      console.error("switchCamera failed:", err);
      message.error("Could not switch camera");
    }
  }, []);

  const monacoTheme =
    theme === CRAFTERHACK_LIGHT_THEME
      ? CRAFTERHACK_LIGHT_THEME
      : CRAFTERHACK_DARK_THEME;

  const applyFontSize = (size) => {
    setFontSize(size);
    localStorage.setItem(EDITOR_FONT_KEY, String(size));
  };

  const applyTheme = (next) => {
    setTheme(next);
    localStorage.setItem(EDITOR_THEME_KEY, next);
  };

  const pageTheme = {
    algorithm: isAppDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: { colorPrimary: "#137274" },
  };

  if (!roomId) {
    return (
      <ConfigProvider theme={pageTheme}>
      <Layout style={styles.layout}>
        <Helmet>
          <title>Live Coding | Pair Programming Room | CrafterHack</title>
          <meta
            name="description"
            content="Open a private live coding room — share a link, pair program with video, and collaborate in real time."
          />
          <link rel="canonical" href="https://crafterhack.com/live-coding" />
        </Helmet>
        <Header style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BrandMark size={28} />
            <Title level={4} style={{ ...styles.brand, margin: 0 }}>
              <span style={styles.brandAccent}>CrafterHack</span>
              {" · Live Coding"}
            </Title>
          </div>
        </Header>
        <Content style={styles.landing}>
          <div style={styles.landingCard}>
            <Title
              level={2}
              style={{ color: textColor, margin: 0, letterSpacing: "-0.03em" }}
            >
              Pair programming, live
            </Title>
            <Text style={{ color: mutedColor, maxWidth: 360 }}>
              Create a private room, share the link, and code together with
              video —{COMPILER_LANGUAGES.length} languages including Python,
              Java, Go, Rust, and TypeScript.
            </Text>
            <Button
              type="primary"
              size="large"
              onClick={createRoom}
              style={{
                background: "#137274",
                borderColor: "#137274",
                fontWeight: 700,
                height: 44,
                paddingInline: 22,
              }}
            >
              Start a room
            </Button>
          </div>
        </Content>
      </Layout>
      </ConfigProvider>
    );
  }

  if (awaitingAdmission) {
    return (
      <ConfigProvider theme={pageTheme}>
      <Layout style={styles.layout}>
        <Helmet>
          <title>Waiting to join | Live Coding | CrafterHack</title>
        </Helmet>
        <Header style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BrandMark size={28} />
            <Title level={4} style={{ ...styles.brand, margin: 0 }}>
              <span style={styles.brandAccent}>CrafterHack</span>
              {" · Live Coding"}
            </Title>
          </div>
        </Header>
        <Content style={styles.landing}>
          <div style={styles.landingCard}>
            <Spin size="large" />
            <Title
              level={3}
              style={{
                color: textColor,
                margin: "16px 0 0",
                letterSpacing: "-0.03em",
              }}
            >
              {roomNotStarted
                ? "The room hasn't been started yet"
                : "Waiting for the host to let you in…"}
            </Title>
            <Text style={{ color: mutedColor, maxWidth: 360 }}>
              {roomNotStarted
                ? "The host hasn't opened this room yet. You'll be connected automatically as soon as they arrive and let you in."
                : "You’ll be connected automatically as soon as they admit you."}
            </Text>
            <Button
              onClick={() => navigate("/live-coding")}
              style={{ marginTop: 8 }}
            >
              Cancel
            </Button>
          </div>
        </Content>
      </Layout>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={pageTheme}>
    <Layout style={styles.layout}>
      <Helmet>
        <title>Live Coding Room | CrafterHack</title>
        <link rel="canonical" href="https://crafterhack.com/live-coding" />
      </Helmet>

      <style>{`
        @keyframes ch-reaction-float {
          0% { transform: translate(-50%, 0) scale(0.5); opacity: 0; }
          15% { transform: translate(-50%, -12px) scale(1.2); opacity: 1; }
          100% { transform: translate(-50%, -150px) scale(1); opacity: 0; }
        }
      `}</style>

      <Modal
        title="What's your name?"
        open={!nameReady}
        closable={false}
        maskClosable={false}
        keyboard={false}
        okText="Join room"
        onOk={confirmDisplayName}
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Others will see this name on your video and when you run code.
        </Text>
        <Input
          autoFocus
          size="large"
          placeholder="e.g. Afzal"
          value={nameDraft}
          maxLength={32}
          onChange={(e) => setNameDraft(e.target.value)}
          onPressEnter={confirmDisplayName}
        />
      </Modal>

      <Modal
        title={
          <span>
            <UserAddOutlined style={{ marginRight: 8, color: "#137274" }} />
            Someone wants to join
          </span>
        }
        open={!!pendingGuestRequest}
        closable={false}
        maskClosable={false}
        keyboard={false}
        onOk={() => respondToJoinRequest(pendingGuestRequest?.socketId, true)}
        onCancel={() =>
          respondToJoinRequest(pendingGuestRequest?.socketId, false)
        }
        okText="Admit"
        cancelText="Deny"
        okButtonProps={{
          style: { background: "#137274", borderColor: "#137274" },
        }}
      >
        <Text>
          <strong>{pendingGuestRequest?.name || "Someone"}</strong> is waiting
          to join this room.
        </Text>
      </Modal>

      <Header
        style={{
          ...styles.header,
          ...(isMobile
            ? {
                height: "auto",
                minHeight: 48,
                lineHeight: "normal",
                padding: "8px 10px",
                flexWrap: "wrap",
                alignItems: "flex-start",
                gap: 8,
              }
            : null),
        }}
      >
        <div
          style={{
            ...styles.headerLeft,
            ...(isMobile ? { flexWrap: "wrap", gap: 6, flex: 1 } : null),
          }}
        >
          <Title level={4} style={styles.brand}>
            <span style={styles.brandAccent}>Live</span>
            {isMobile ? "" : " Coding"}
          </Title>
          <Tag
            style={{
              margin: 0,
              background: isAppDarkMode
                ? "rgba(19,114,116,0.18)"
                : "rgba(19,114,116,0.10)",
              borderColor: isAppDarkMode
                ? "rgba(19,114,116,0.35)"
                : "rgba(19,114,116,0.45)",
              color: isAppDarkMode ? "#7dd3d5" : "#0b4547",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 12,
              fontWeight: isAppDarkMode ? 400 : 600,
              maxWidth: isMobile ? 88 : undefined,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {roomId}
          </Tag>
          <Tooltip title="Participants in room" placement="bottom">
            <Tag icon={<TeamOutlined />} style={{ margin: 0 }}>
              {roomSize || 1}/2
            </Tag>
          </Tooltip>
          {isHost && (
            <Tag color="gold" style={{ margin: 0 }}>
              Host
            </Tag>
          )}
          {isViewOnly && (
            <Tag color="orange" style={{ margin: 0 }}>
              View only
            </Tag>
          )}
        </div>

        <div
          style={{
            ...styles.toolbar,
            ...(isMobile
              ? {
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  width: "100%",
                  gap: 6,
                }
              : null),
          }}
          data-tour="live-header-actions"
        >
          {(output || outputMeta) && (
            <Tooltip
              title={outputVisible ? "Hide output" : "Show output"}
              placement="bottom"
            >
              <Button
                size="small"
                type="default"
                icon={outputVisible ? <DownOutlined /> : <UpOutlined />}
                onClick={() => {
                  setOutputVisible((v) => !v);
                  if (!outputVisible && isMobile) setOutputHeight(140);
                }}
              >
                {isMobile ? "" : outputVisible ? "Hide output" : "Output"}
              </Button>
            </Tooltip>
          )}

          {isMobile && (
            <Tooltip
              title={videoFeedsOpen ? "Hide video" : "Show video"}
              placement="bottom"
            >
              <Button
                size="small"
                type={videoFeedsOpen ? "primary" : "default"}
                icon={
                  videoFeedsOpen ? (
                    <VideoCameraOutlined />
                  ) : (
                    <VideoCameraAddOutlined />
                  )
                }
                onClick={() => setVideoFeedsOpen((v) => !v)}
              />
            </Tooltip>
          )}

          {!isMobile && !isScreenSharing && !isRemoteScreenSharing && (
            <Popover
              open={layoutPickerOpen}
              onOpenChange={setLayoutPickerOpen}
              trigger="click"
              placement="bottom"
              content={
                <div style={{ display: "flex", gap: 10 }}>
                  {[
                    {
                      key: "editor",
                      label: "Editor",
                      active: !videoOnlyMode && mainPaneMode === "editor",
                      onSelect: () => {
                        setVideoOnlyMode(false);
                        setMainPaneMode("editor");
                      },
                      preview: (
                        <div style={{ display: "flex", gap: 3, width: "100%", height: 52 }}>
                          <div style={{ flex: 2, background: "#137274", borderRadius: 3 }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
                            <div style={{ flex: 1, background: "#7dd3d5", borderRadius: 3 }} />
                            <div style={{ flex: 1, background: "#7dd3d5", borderRadius: 3, opacity: 0.55 }} />
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: "video",
                      label: "Video",
                      active: videoOnlyMode,
                      onSelect: () => setVideoOnlyMode(true),
                      preview: (
                        <div style={{ display: "flex", gap: 3, width: "100%", height: 52 }}>
                          <div style={{ flex: 1, background: "#7dd3d5", borderRadius: 3 }} />
                          <div style={{ flex: 1, background: "#7dd3d5", borderRadius: 3, opacity: 0.55 }} />
                        </div>
                      ),
                    },
                  ].map((opt) => (
                    <div
                      key={opt.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        opt.onSelect();
                        setLayoutPickerOpen(false);
                      }}
                      style={{
                        cursor: "pointer",
                        padding: 8,
                        borderRadius: 8,
                        textAlign: "center",
                        width: 100,
                        border: opt.active
                          ? "2px solid #137274"
                          : "1px solid rgba(19,114,116,0.25)",
                        background: opt.active
                          ? "rgba(19,114,116,0.08)"
                          : "transparent",
                      }}
                    >
                      {opt.preview}
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                        }}
                      >
                        {opt.active && (
                          <CheckCircleFilled style={{ color: "#137274" }} />
                        )}
                        {opt.label}
                      </div>
                    </div>
                  ))}
                </div>
              }
            >
              <Tooltip title="Change layout" placement="bottom">
                <Button
                  size="small"
                  type={videoOnlyMode ? "primary" : "default"}
                  icon={<LayoutOutlined />}
                >
                  Layout
                </Button>
              </Tooltip>
            </Popover>
          )}

          <Tooltip title="Chat" placement="bottom">
            <Badge count={chatUnread} size="small" offset={[-2, 2]}>
              <Button
                size="small"
                type={chatOpen ? "primary" : "default"}
                icon={<MessageOutlined />}
                onClick={() => {
                  if (chatOpen) {
                    setChatOpen(false);
                  } else {
                    openChatPanel();
                    if (isMobile) setVideoFeedsOpen(true);
                  }
                }}
              >
                {isMobile ? "" : "Chat"}
              </Button>
            </Badge>
          </Tooltip>

          {isHost && (
            <Tooltip title="Invite" placement="bottom">
              <Button
                size="small"
                icon={<ShareAltOutlined />}
                onClick={() => setIsModalVisible(true)}
              >
                {isMobile ? "" : "Invite"}
              </Button>
            </Tooltip>
          )}

          <Tooltip
            title={
              isScreenSharing
                ? "Stop sharing your screen"
                : "Share your screen with the room"
            }
            placement="bottom"
          >
            <Button
              size="small"
              type={isScreenSharing ? "primary" : "default"}
              danger={isScreenSharing}
              icon={<DesktopOutlined />}
              onClick={toggleScreenShare}
            >
              {isMobile
                ? ""
                : isScreenSharing
                  ? "Stop share"
                  : isRemoteScreenSharing
                    ? "Take over"
                    : "Share screen"}
            </Button>
          </Tooltip>

          {!isMobile && (
            <Tooltip title="Replay live coding tour" placement="bottom">
              <Button
                size="small"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent(LIVE_TOUR_START_EVENT))
                }
              >
                Tour
              </Button>
            </Tooltip>
          )}

          <Tooltip
            title={isFullscreen ? "Exit full screen" : "Full screen"}
            placement="bottom"
          >
            <Button
              size="small"
              type={isFullscreen ? "primary" : "default"}
              icon={
                isFullscreen ? (
                  <FullscreenExitOutlined />
                ) : (
                  <FullscreenOutlined />
                )
              }
              onClick={toggleFullscreen}
            />
          </Tooltip>

          {isHost ? (
            <Tooltip title="End session" placement="bottom">
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={endSession}
              >
                {isMobile ? "" : "End"}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Leave session" placement="bottom">
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={leaveSession}
              >
                {isMobile ? "" : "Leave"}
              </Button>
            </Tooltip>
          )}
        </div>
      </Header>

        <Modal
          title="Room settings"
          open={settingsOpen}
          onCancel={() => setSettingsOpen(false)}
          afterOpenChange={(open) => {
            if (open) refreshMediaDevices();
          }}
          footer={[
            <Button
              key="done"
              type="primary"
              onClick={() => setSettingsOpen(false)}
              style={{ background: "#137274", borderColor: "#137274" }}
            >
              Done
            </Button>,
          ]}
          destroyOnClose={false}
          width={520}
        >
          <Tabs
            defaultActiveKey="general"
            items={[
              {
                key: "general",
                label: "General",
                children: (
                  <div style={styles.settingsRow}>
                    <div>
                      <span style={styles.settingsLabel}>THEME</span>
                      <Select
                        value={theme}
                        onChange={applyTheme}
                        style={{ width: "100%" }}
                        options={[
                          { value: CRAFTERHACK_DARK_THEME, label: "Dark" },
                          { value: CRAFTERHACK_LIGHT_THEME, label: "Light" },
                        ]}
                      />
                    </div>
                    <div>
                      <span style={styles.settingsLabel}>FONT SIZE</span>
                      <Select
                        value={fontSize}
                        onChange={applyFontSize}
                        style={{ width: "100%" }}
                        options={[
                          { value: 12, label: "12 — Compact" },
                          { value: 14, label: "14 — Default" },
                          { value: 16, label: "16 — Comfortable" },
                          { value: 18, label: "18 — Large" },
                          { value: 20, label: "20 — Extra large" },
                        ]}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          ...styles.settingsLabel,
                          marginBottom: 0,
                          color: isAppDarkMode ? "#8b949e" : "#57606a",
                        }}
                      >
                        KEYBOARD SHORTCUTS
                      </span>
                      <Switch
                        size="small"
                        checked={shortcutsOn}
                        onChange={(on) => {
                          setShortcutsOn(on);
                          setShortcutsEnabled(on);
                        }}
                      />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Theme, font size, and shortcut preference are saved on
                      this device.
                    </Text>
                  </div>
                ),
              },
              {
                key: "audio",
                label: "Audio",
                children: (
                  <div style={styles.settingsRow}>
                    <div>
                      <span style={styles.settingsLabel}>MICROPHONE</span>
                      <Select
                        value={selectedMicId || undefined}
                        onChange={switchMic}
                        style={{ width: "100%" }}
                        placeholder="Select microphone"
                        options={audioDevices.map((d, i) => ({
                          value: d.deviceId,
                          label: d.label || `Microphone ${i + 1}`,
                        }))}
                        notFoundContent="Allow mic access to list devices"
                      />
                    </div>
                    <div>
                      <span style={styles.settingsLabel}>SPEAKER</span>
                      <Select
                        value={selectedSpeakerId || undefined}
                        onChange={switchSpeaker}
                        style={{ width: "100%" }}
                        placeholder={
                          supportsAudioOutputSelect()
                            ? "Select speaker / headphones"
                            : "Not supported in this browser"
                        }
                        disabled={!supportsAudioOutputSelect()}
                        options={outputDevices.map((d, i) => ({
                          value: d.deviceId,
                          label: d.label || `Speaker ${i + 1}`,
                        }))}
                        notFoundContent={
                          supportsAudioOutputSelect()
                            ? "Allow mic access to list speakers"
                            : "Use Chrome or Edge to pick speakers"
                        }
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: "video",
                label: "Video",
                children: (
                  <div style={styles.settingsRow}>
                    <div>
                      <span style={styles.settingsLabel}>CAMERA</span>
                      <Select
                        value={selectedCameraId || undefined}
                        onChange={switchCamera}
                        style={{ width: "100%" }}
                        placeholder="Select camera"
                        options={videoDevices.map((d, i) => ({
                          value: d.deviceId,
                          label: d.label || `Camera ${i + 1}`,
                        }))}
                        notFoundContent="Allow camera access to list devices"
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: "shortcuts",
                label: "Shortcuts",
                children: (
                  <div
                    style={{
                      border: `1px solid ${isAppDarkMode ? "#30363d" : "#d9d9d9"}`,
                      borderRadius: 8,
                      overflow: "hidden",
                      opacity: shortcutsOn ? 1 : 0.55,
                    }}
                  >
                    {LIVE_CODING_SHORTCUTS.map((row, i) => (
                      <div
                        key={row.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          padding: "9px 12px",
                          borderTop:
                            i === 0
                              ? "none"
                              : `1px solid ${isAppDarkMode ? "#30363d" : "#d9d9d9"}`,
                          background: isAppDarkMode
                            ? i % 2 === 0
                              ? "rgba(255,255,255,0.03)"
                              : "transparent"
                            : i % 2 === 0
                              ? "#fafafa"
                              : "#fff",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: isAppDarkMode ? "#e6edf3" : "#1f2328",
                          }}
                        >
                          {row.action}
                        </span>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {row.keys().map((k, ki) => (
                            <React.Fragment key={`${row.id}-${ki}`}>
                              {ki > 0 && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: isAppDarkMode
                                      ? "#8b949e"
                                      : "#8c8c8c",
                                    fontWeight: 700,
                                  }}
                                >
                                  +
                                </span>
                              )}
                              <kbd
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: 22,
                                  height: 22,
                                  padding: "0 6px",
                                  borderRadius: 4,
                                  border: `1px solid ${isAppDarkMode ? "#30363d" : "#d9d9d9"}`,
                                  background: isAppDarkMode
                                    ? "#0d1117"
                                    : "#fff",
                                  boxShadow: isAppDarkMode
                                    ? "none"
                                    : "0 1px 0 rgba(15,23,31,0.08)",
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: isAppDarkMode ? "#e6edf3" : "#1f2328",
                                }}
                              >
                                {k}
                              </kbd>
                            </React.Fragment>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </Modal>

      <Content style={styles.content}>
        <div
          style={{
            ...styles.mainRow,
            ...(isMobile
              ? { flexDirection: "column", position: "relative" }
              : null),
          }}
        >
          <div
            style={{
              ...styles.editorPane,
              ...(isMobile
                ? { borderRight: "none", flex: 1, minHeight: 0 }
                : null),
              // Content stays mounted (Monaco keeps its state/undo history) —
              // just collapsed out of view while video-only mode is on.
              ...(!isMobile && videoOnlyMode
                ? { flex: "0 0 0px", width: 0, minWidth: 0, borderRight: "none", overflow: "hidden" }
                : null),
            }}
          >
            {(isScreenSharing || isRemoteScreenSharing) && (
              <div
                style={{
                  ...styles.screenStage,
                  cursor:
                    screenShareZoom.scale > ZOOM_MIN
                      ? "grab"
                      : "zoom-in",
                }}
                {...screenShareZoom.handlers}
                onDoubleClick={screenShareZoom.reset}
              >
                <div style={styles.screenStageBar}>
                  <Tag
                    icon={<DesktopOutlined />}
                    color="blue"
                    style={{ margin: 0 }}
                  >
                    {isScreenSharing
                      ? "You are sharing"
                      : `${peerName} is sharing`}
                  </Tag>
                  {isScreenSharing && (
                    <Button
                      size="small"
                      danger
                      type="primary"
                      onClick={() => stopScreenShare()}
                    >
                      Stop sharing
                    </Button>
                  )}
                </div>
                <video
                  ref={stageVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    ...styles.screenStageVideo,
                    transform: `scale(${screenShareZoom.scale}) translate(${screenShareZoom.offset.x / screenShareZoom.scale}px, ${screenShareZoom.offset.y / screenShareZoom.scale}px)`,
                    transition: screenShareZoom.isZoomed
                      ? "none"
                      : "transform 0.15s ease-out",
                  }}
                />
                <ZoomControls zoom={screenShareZoom} />
              </div>
            )}

            {/* Dedicated remote audio — keeps both sides audible even if video tiles are muted/collapsed */}
            <audio
              ref={remoteAudioRef}
              autoPlay
              playsInline
              style={{ display: "none" }}
            />

            {(isScreenSharing || isRemoteScreenSharing) && (
              <div style={styles.editorShareBar}>
                <span style={{ color: mutedColor, fontSize: 12 }}>
                  Editor tucked away while a screen is shared
                </span>
                <Button
                  size="small"
                  type="link"
                  onClick={() => setEditorOpenDuringShare((v) => !v)}
                  style={{ color: "#137274", padding: 0, fontWeight: 600 }}
                >
                  {editorOpenDuringShare ? "Hide editor" : "Show editor"}
                </Button>
              </div>
            )}

            {(!(isScreenSharing || isRemoteScreenSharing) ||
              editorOpenDuringShare) && (
              <div
                style={{
                  display: "flex",
                  flex: isScreenSharing || isRemoteScreenSharing ? "0 0 38%" : 1,
                  minHeight: isScreenSharing || isRemoteScreenSharing ? 180 : 0,
                  maxHeight:
                    isScreenSharing || isRemoteScreenSharing ? "42%" : undefined,
                  borderTop:
                    isScreenSharing || isRemoteScreenSharing
                      ? "1px solid #30363d"
                      : undefined,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                {!isMobile && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: 40,
                      flexShrink: 0,
                      borderRight: `1px solid ${isAppDarkMode ? "#30363d" : "rgba(15,60,70,0.12)"}`,
                      background: isAppDarkMode ? "#161b22" : "#f6f8fa",
                    }}
                    data-tour="live-main-pane-tabs"
                  >
                    {MAIN_PANE_TABS.map((t) => {
                      const active = mainPaneMode === t.key;
                      return (
                        <Tooltip key={t.key} title={t.label} placement="right">
                          <button
                            type="button"
                            onClick={() => setMainPaneMode(t.key)}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 6,
                              padding: "12px 0",
                              background: active
                                ? "rgba(19,114,116,0.12)"
                                : "transparent",
                              border: "none",
                              borderLeft: active
                                ? "2px solid #137274"
                                : "2px solid transparent",
                              color: active ? "#137274" : mutedColor,
                              cursor: "pointer",
                            }}
                          >
                            <span style={{ fontSize: 16 }}>{t.icon}</span>
                            <span
                              style={{
                                writingMode: "vertical-rl",
                                textOrientation: "mixed",
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: "0.02em",
                              }}
                            >
                              {t.label}
                            </span>
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  {isMobile && (
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        padding: "6px 8px",
                        borderBottom: `1px solid ${isAppDarkMode ? "#30363d" : "rgba(15,60,70,0.12)"}`,
                      }}
                      data-tour="live-main-pane-tabs"
                    >
                      {MAIN_PANE_TABS.map((t) => (
                        <Button
                          key={t.key}
                          size="small"
                          type={mainPaneMode === t.key ? "primary" : "default"}
                          icon={t.icon}
                          onClick={() => setMainPaneMode(t.key)}
                        >
                          {t.label}
                        </Button>
                      ))}
                    </div>
                  )}

                  {mainPaneMode === "whiteboard" ? (
                    <Whiteboard
                      socketRef={socketRef}
                      roomId={roomId}
                      active
                      viewOnly={isViewOnly}
                      isDark={isAppDarkMode}
                    />
                  ) : (
                    <>
                      <div
                        style={{
                          ...styles.editorToolbar,
                          ...(isMobile ? { padding: "6px 8px", gap: 6 } : null),
                        }}
                        data-tour="live-editor-toolbar"
                      >
                        <div style={styles.editorToolbarLeft}>
                          <Select
                            value={language}
                            onChange={handleLanguageChange}
                            style={{
                              ...styles.select,
                              ...(isMobile ? { width: 108 } : { minWidth: 148 }),
                            }}
                            size="small"
                            disabled={isViewOnly}
                            showSearch
                            optionFilterProp="label"
                            options={languageOptions}
                          />
                          <Tooltip
                            title={
                              isNoteLanguage(language)
                                ? "Switch to a programming language to run code"
                                : ""
                            }
                          >
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlayCircleOutlined />}
                              onClick={handleRunCode}
                              loading={isCompiling}
                              disabled={isViewOnly || isNoteLanguage(language)}
                              style={{
                                background: "#137274",
                                borderColor: "#137274",
                                fontWeight: 600,
                              }}
                            >
                              {isMobile ? "" : "Run"}
                            </Button>
                          </Tooltip>
                          {isViewOnly && (
                            <Text style={{ color: mutedColor, fontSize: 12 }}>
                              View only
                            </Text>
                          )}
                        </div>
                        <div style={styles.editorToolbarRight}>
                          <Tooltip title="Font size & theme">
                            <Button
                              size="small"
                              icon={<SettingOutlined />}
                              onClick={() => setSettingsOpen(true)}
                            >
                              {isMobile ? "" : "Settings"}
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                      <div style={{ ...styles.editorWrap, flex: 1 }}>
                        <MonacoEditor
                          height="100%"
                          language={
                            getNoteLanguage(language)?.monaco ??
                            getMonacoLanguage(language)
                          }
                          theme={monacoTheme}
                          value={code}
                          onChange={handleEditorChange}
                          beforeMount={defineCrafterhackThemes}
                          onMount={handleEditorMount}
                          options={monacoOptions}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {outputVisible && (
              <div style={{ ...styles.outputPanel, height: outputHeight }}>
                <div
                  style={{
                    ...styles.outputResizeHandle,
                    ...(isMobile ? { height: 10, touchAction: "none" } : null),
                  }}
                  onMouseDown={startOutputResize}
                  onTouchStart={startOutputResize}
                  title="Drag to resize"
                >
                  <div
                    style={{
                      width: 36,
                      height: 3,
                      borderRadius: 2,
                      background: "#484f58",
                      margin: "1px auto 0",
                    }}
                  />
                </div>
                <div style={styles.outputHeader}>
                  <div style={styles.outputMeta}>{outputMeta || "Output"}</div>
                  <Button
                    size="small"
                    type="text"
                    onClick={() => setOutputVisible(false)}
                    style={{ color: mutedColor, fontSize: 12 }}
                  >
                    Hide
                  </Button>
                </div>
                <div style={styles.outputBody}>
                  <pre
                    style={{
                      ...styles.outputText,
                      color: outputError
                        ? isAppDarkMode
                          ? "#ffa198"
                          : "#cf222e"
                        : textColor,
                    }}
                  >
                    {isCompiling
                      ? "Executing…"
                      : output || "Run code to see shared output here."}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {!isMobile && !videoOnlyMode && (
            <div
              style={styles.sidePaneResizeHandle}
              onMouseDown={startSidePaneResize}
              onTouchStart={startSidePaneResize}
              title="Drag to resize"
            />
          )}

          <aside
            style={{
              ...styles.sidePane,
              ...(!isMobile && !videoOnlyMode
                ? { width: sidePaneWidth }
                : null),
              ...(isMobile
                ? {
                    width: "100%",
                    flexDirection: chatOpen ? "row" : "row",
                    flexWrap: chatOpen ? "wrap" : "nowrap",
                    alignItems: "stretch",
                    order: -1,
                    borderBottom: `1px solid ${isAppDarkMode ? "#30363d" : "rgba(15, 60, 70, 0.12)"}`,
                    flexShrink: 0,
                    overflow: "hidden",
                    padding: videoFeedsOpen || chatOpen ? 8 : "6px 8px",
                    gap: 8,
                    maxHeight: chatOpen
                      ? videoFeedsOpen
                        ? 360
                        : 240
                      : videoFeedsOpen
                        ? 132
                        : 48,
                    minHeight: chatOpen ? 200 : videoFeedsOpen ? 120 : 48,
                  }
                : null),
              ...(!isMobile && videoOnlyMode
                ? { flex: 1, width: "100%", padding: 20, gap: 16 }
                : null),
            }}
            data-tour="live-video-pane"
          >
            {isMobile && !videoFeedsOpen && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 8,
                }}
              >
                <span style={{ color: mutedColor, fontSize: 12 }}>
                  Video hidden · {roomSize || 1}/2 in room
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                    <Button
                      size="small"
                      type={isMuted ? "primary" : "default"}
                      danger={isMuted}
                      icon={
                        isMuted ? <AudioMutedOutlined /> : <AudioOutlined />
                      }
                      onClick={toggleMute}
                      shape="circle"
                    />
                  </Tooltip>
                  <Tooltip title={videoEnabled ? "Camera off" : "Camera on"}>
                    <Button
                      size="small"
                      type={!videoEnabled ? "primary" : "default"}
                      icon={
                        videoEnabled ? (
                          <VideoCameraOutlined />
                        ) : (
                          <VideoCameraAddOutlined />
                        )
                      }
                      onClick={toggleVideo}
                      shape="circle"
                    />
                  </Tooltip>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => setVideoFeedsOpen(true)}
                    style={{ color: "#137274", fontWeight: 600, padding: 0 }}
                  >
                    Show
                  </Button>
                </div>
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: !isMobile && videoOnlyMode ? "row" : "column",
                gap: !isMobile && videoOnlyMode ? 16 : 10,
                flex: !isMobile && videoOnlyMode ? 1 : "initial",
                minHeight: 0,
                minWidth: 0,
              }}
            >
            <div
              style={{
                ...styles.videoCard,
                ...(isMobile
                  ? {
                      flex: 1,
                      aspectRatio: "16 / 10",
                      maxHeight: 116,
                    }
                  : null),
                ...(isMobile && !videoFeedsOpen
                  ? {
                      position: "absolute",
                      width: 1,
                      height: 1,
                      opacity: 0,
                      overflow: "hidden",
                      pointerEvents: "none",
                    }
                  : null),
                ...(!isMobile && videoOnlyMode
                  ? { flex: 1, aspectRatio: "auto", height: "100%", minWidth: 0 }
                  : null),
              }}
            >
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  ...styles.videoEl,
                  opacity: videoEnabled ? (isBlurred ? 0 : 1) : 0.35,
                }}
              />
              {reactions
                .filter((r) => r.target === "local")
                .map((r, i) => (
                  <span
                    key={r.id}
                    style={{
                      position: "absolute",
                      left: `calc(50% + ${((i % 3) - 1) * 16}px)`,
                      bottom: 44,
                      fontSize: 28,
                      pointerEvents: "none",
                      animation: "ch-reaction-float 2.2s ease-out forwards",
                      zIndex: 5,
                    }}
                  >
                    {r.emoji}
                  </span>
                ))}
              <canvas
                ref={canvasRef}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  transform: "scaleX(-1)",
                  pointerEvents: "none",
                  display: isBlurred && !isScreenSharing ? "block" : "none",
                }}
              />
              <span style={styles.videoLabel}>
                {displayName || "You"}
                {isMuted ? " · muted" : ""}
              </span>
              {isScreenSharing && (
                <span style={styles.muteBadge}>
                  <Tag icon={<DesktopOutlined />} color="blue">
                    Sharing
                  </Tag>
                </span>
              )}
              <div
                style={{
                  ...styles.videoControls,
                  ...(bigControls
                    ? { gap: 10, padding: "8px 16px", bottom: 16 }
                    : null),
                }}
              >
                <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                  <Button
                    size={controlSize}
                    type={isMuted ? "primary" : "default"}
                    danger={isMuted}
                    icon={
                      isMuted ? (
                        <AudioMutedOutlined style={{ fontSize: controlIconSize }} />
                      ) : (
                        <AudioOutlined style={{ fontSize: controlIconSize }} />
                      )
                    }
                    onClick={toggleMute}
                    shape="circle"
                  />
                </Tooltip>
                <Tooltip title={videoEnabled ? "Camera off" : "Camera on"}>
                  <Button
                    size={controlSize}
                    type={!videoEnabled ? "primary" : "default"}
                    icon={
                      videoEnabled ? (
                        <VideoCameraOutlined style={{ fontSize: controlIconSize }} />
                      ) : (
                        <VideoCameraAddOutlined style={{ fontSize: controlIconSize }} />
                      )
                    }
                    onClick={toggleVideo}
                    shape="circle"
                  />
                </Tooltip>
                <Popover
                  open={reactionPickerOpen}
                  onOpenChange={setReactionPickerOpen}
                  trigger="click"
                  placement="top"
                  content={
                    <div style={{ display: "flex", gap: 4 }}>
                      {REACTION_EMOJIS.map((emoji) => (
                        <Button
                          key={emoji}
                          type="text"
                          size="large"
                          onClick={() => sendReaction(emoji)}
                          style={{
                            fontSize: 22,
                            padding: "2px 6px",
                            height: "auto",
                          }}
                        >
                          {emoji}
                        </Button>
                      ))}
                    </div>
                  }
                >
                  <Tooltip title="React">
                    <Button
                      size={controlSize}
                      icon={<SmileOutlined style={{ fontSize: controlIconSize }} />}
                      shape="circle"
                    />
                  </Tooltip>
                </Popover>
                <Popover
                  open={blurPickerOpen && !isScreenSharing}
                  onOpenChange={setBlurPickerOpen}
                  trigger="click"
                  placement="top"
                  content={
                    <div style={{ width: 260 }}>
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: isAppDarkMode ? "#8b949e" : "#57606a",
                        }}
                      >
                        BLUR
                      </Text>
                      <div style={{ display: "flex", gap: 8, marginTop: 6, marginBottom: 12 }}>
                        {[
                          { key: "none", label: "None", filter: "none" },
                          { key: "light", label: "Light", filter: "blur(3px)" },
                          { key: "strong", label: "Strong", filter: "blur(7px)" },
                        ].map((opt) => {
                          const active =
                            opt.key === "none"
                              ? !isBlurred
                              : isBlurred &&
                                bgMode === "blur" &&
                                (opt.key === "strong" ? blurAmount >= 16 : blurAmount < 16);
                          const previewRef =
                            opt.key === "none"
                              ? bgPreviewNoneRef
                              : opt.key === "light"
                                ? bgPreviewLightRef
                                : bgPreviewStrongRef;
                          return (
                            <div
                              key={opt.key}
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                opt.key === "none"
                                  ? turnOffBackgroundEffect()
                                  : applyBlurLevel(opt.key === "strong" ? 18 : 8)
                              }
                              style={{
                                cursor: "pointer",
                                textAlign: "center",
                                flex: 1,
                              }}
                            >
                              <div
                                style={{
                                  position: "relative",
                                  width: "100%",
                                  aspectRatio: "1 / 1",
                                  borderRadius: 8,
                                  overflow: "hidden",
                                  border: active
                                    ? "2px solid #137274"
                                    : `1px solid ${isAppDarkMode ? "#30363d" : "rgba(15,60,70,0.2)"}`,
                                  background: "#000",
                                }}
                              >
                                <video
                                  ref={previewRef}
                                  autoPlay
                                  muted
                                  playsInline
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    transform: "scaleX(-1)",
                                    filter: opt.filter,
                                  }}
                                />
                                {active && (
                                  <CheckCircleFilled
                                    style={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      color: "#137274",
                                      background: "#fff",
                                      borderRadius: "50%",
                                      fontSize: 14,
                                    }}
                                  />
                                )}
                              </div>
                              <div style={{ fontSize: 11, marginTop: 4 }}>{opt.label}</div>
                            </div>
                          );
                        })}
                      </div>

                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: isAppDarkMode ? "#8b949e" : "#57606a",
                        }}
                      >
                        BACKGROUND
                      </Text>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          marginTop: 6,
                        }}
                      >
                        {BG_COLORS.map((c) => {
                          const active = isBlurred && bgMode === "color" && bgColor === c;
                          return (
                            <button
                              key={c}
                              type="button"
                              aria-label={`Background color ${c}`}
                              onClick={() => applyBgColor(c)}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                background: c,
                                cursor: "pointer",
                                padding: 0,
                                border: active
                                  ? "2px solid #137274"
                                  : `1px solid ${isAppDarkMode ? "#30363d" : "rgba(15,60,70,0.25)"}`,
                                boxShadow: active ? "0 0 0 2px rgba(19,114,116,0.25)" : "none",
                              }}
                            />
                          );
                        })}

                        {bgImageUrl && (
                          <button
                            type="button"
                            aria-label="Uploaded background"
                            onClick={() => applyBgImage(bgImageUrl)}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              padding: 0,
                              cursor: "pointer",
                              backgroundImage: `url(${bgImageUrl})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              border:
                                isBlurred && bgMode === "image"
                                  ? "2px solid #137274"
                                  : `1px solid ${isAppDarkMode ? "#30363d" : "rgba(15,60,70,0.25)"}`,
                              boxShadow:
                                isBlurred && bgMode === "image"
                                  ? "0 0 0 2px rgba(19,114,116,0.25)"
                                  : "none",
                            }}
                          />
                        )}

                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={handleBgImageUpload}
                        >
                          <button
                            type="button"
                            aria-label="Upload a background photo"
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              padding: 0,
                              cursor: "pointer",
                              background: "transparent",
                              border: `1px dashed ${isAppDarkMode ? "#8b949e" : "#57606a"}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <PlusOutlined style={{ fontSize: 12 }} />
                          </button>
                        </Upload>
                      </div>
                    </div>
                  }
                >
                  <Tooltip
                    title={
                      isScreenSharing
                        ? "Unavailable while sharing"
                        : "Change background"
                    }
                  >
                    <Button
                      size={controlSize}
                      type={isBlurred ? "primary" : "default"}
                      icon={<BgColorsOutlined style={{ fontSize: controlIconSize }} />}
                      shape="circle"
                      disabled={isScreenSharing}
                    />
                  </Tooltip>
                </Popover>
                <Tooltip title="Camera & mic">
                  <Button
                    size={controlSize}
                    icon={<SettingOutlined style={{ fontSize: controlIconSize }} />}
                    onClick={() => setSettingsOpen(true)}
                    shape="circle"
                  />
                </Tooltip>
              </div>
            </div>

            <div
              style={{
                ...styles.videoCard,
                ...(isMobile
                  ? {
                      flex: 1,
                      aspectRatio: "16 / 10",
                      maxHeight: 116,
                    }
                  : null),
                ...(isMobile && !videoFeedsOpen
                  ? {
                      position: "absolute",
                      width: 1,
                      height: 1,
                      opacity: 0,
                      overflow: "hidden",
                      pointerEvents: "none",
                    }
                  : null),
                ...(!isMobile && videoOnlyMode
                  ? { flex: 1, aspectRatio: "auto", height: "100%", minWidth: 0 }
                  : null),
                cursor: hasRemoteStream
                  ? remoteVideoZoom.scale > ZOOM_MIN
                    ? "grab"
                    : "zoom-in"
                  : "default",
              }}
              {...(hasRemoteStream ? remoteVideoZoom.handlers : null)}
              onDoubleClick={hasRemoteStream ? remoteVideoZoom.reset : undefined}
            >
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  ...styles.videoEl,
                  objectFit: isRemoteScreenSharing ? "contain" : "cover",
                  objectPosition: "center",
                  // Mirror goes LAST (innermost/applied-first) so it only flips
                  // the image, not the pan direction — otherwise dragging the
                  // mirrored tile moves the content opposite to the cursor.
                  transform: `scale(${remoteVideoZoom.scale}) translate(${remoteVideoZoom.offset.x / remoteVideoZoom.scale}px, ${remoteVideoZoom.offset.y / remoteVideoZoom.scale}px) ${isRemoteScreenSharing ? "" : "scaleX(-1)"}`,
                  transition: remoteVideoZoom.isZoomed
                    ? "none"
                    : "transform 0.15s ease-out",
                  visibility: hasRemoteStream ? "visible" : "hidden",
                }}
              />
              {hasRemoteStream && <ZoomControls zoom={remoteVideoZoom} />}
              {reactions
                .filter((r) => r.target === "remote")
                .map((r, i) => (
                  <span
                    key={r.id}
                    style={{
                      position: "absolute",
                      left: `calc(50% + ${((i % 3) - 1) * 16}px)`,
                      bottom: 44,
                      fontSize: 28,
                      pointerEvents: "none",
                      animation: "ch-reaction-float 2.2s ease-out forwards",
                      zIndex: 5,
                    }}
                  >
                    {r.emoji}
                  </span>
                ))}
              {!hasRemoteStream && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: mutedColor,
                    fontSize: isMobile ? 12 : 13,
                    background: isAppDarkMode ? "#010409" : "#eef2f5",
                    padding: isMobile ? 8 : 12,
                    textAlign: "center",
                  }}
                >
                  {isMobile ? "Waiting…" : "Waiting for peer…"}
                </div>
              )}
              {hasRemoteStream && isRemoteMuted && (
                <span style={styles.muteBadge}>
                  <Tag icon={<AudioMutedOutlined />} color="magenta">
                    Muted
                  </Tag>
                </span>
              )}
              {hasRemoteStream && isRemoteScreenSharing && (
                <span
                  style={{
                    ...styles.muteBadge,
                    top: isRemoteMuted ? 36 : 8,
                  }}
                >
                  <Tag icon={<DesktopOutlined />} color="blue">
                    Screen
                  </Tag>
                </span>
              )}
              <div
                style={{
                  ...styles.videoLabel,
                  // The base videoLabel style bakes in maxWidth/overflow:hidden/
                  // nowrap for a single text node — with a 2nd flex child (the
                  // remove icon) that combo clips the icon out of view entirely
                  // once the name is long enough to fill the box. Those three
                  // move onto just the name span below instead, with minWidth:0
                  // so it can actually shrink inside the flex row (flex items
                  // don't shrink below content size by default) and flexShrink:0
                  // on the icon so it's never the one that gets squeezed.
                  maxWidth: "72%",
                  overflow: "visible",
                  textOverflow: "clip",
                  whiteSpace: "normal",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {hasRemoteStream ? peerName : "Peer"}
                </span>
                {isHost && hasRemoteStream && (
                  <Popconfirm
                    title="Remove this participant?"
                    description={`${peerName} will be disconnected and will need to be re-admitted to rejoin.`}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    onConfirm={removeGuest}
                    placement="bottom"
                  >
                    <Tooltip title="Remove from room">
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{
                          display: "inline-flex",
                          flexShrink: 0,
                          cursor: "pointer",
                          color: "#ff7875",
                        }}
                      >
                        <UserDeleteOutlined />
                      </span>
                    </Tooltip>
                  </Popconfirm>
                )}
              </div>
            </div>
            </div>

            {(avStatus === "failed" ||
              avStatus === "relay" ||
              (avStatus === "connecting" &&
                roomSize >= 2 &&
                !hasRemoteStream)) && (
              <div
                style={{
                  borderRadius: 8,
                  border: `1px solid ${
                    avStatus === "failed"
                      ? "rgba(245,0,87,0.45)"
                      : avStatus === "relay"
                        ? "rgba(19,114,116,0.45)"
                        : isAppDarkMode
                          ? "#30363d"
                          : "rgba(15, 60, 70, 0.12)"
                  }`,
                  background:
                    avStatus === "failed"
                      ? "rgba(245,0,87,0.1)"
                      : avStatus === "relay"
                        ? "rgba(19,114,116,0.12)"
                        : isAppDarkMode
                          ? "#0d1117"
                          : "#f6f8fa",
                  padding: "8px 10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <Text style={{ color: textColor, fontSize: 12, margin: 0 }}>
                  {avStatus === "relay"
                    ? "Free server relay active (works without paid TURN)."
                    : avStatus === "failed"
                      ? "Direct A/V blocked by network."
                      : "Connecting audio/video…"}
                </Text>
                {(avStatus === "failed" || avStatus === "relay") && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {avStatus === "failed" && !relayMode && (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => startServerRelay()}
                        style={{
                          background: "#137274",
                          borderColor: "#137274",
                        }}
                      >
                        Use free relay
                      </Button>
                    )}
                    <Button size="small" onClick={retryAvConnection}>
                      Retry P2P
                    </Button>
                  </div>
                )}
              </div>
            )}

            {chatOpen && (
              <div
                style={{
                  ...styles.chatPanel,
                  ...(isMobile
                    ? {
                        flex: "1 1 100%",
                        minHeight: 160,
                        maxHeight: 220,
                        order: 3,
                      }
                    : null),
                }}
                data-tour="live-chat"
              >
                <div style={styles.chatHeader}>
                  <span style={styles.chatTitle}>
                    <MessageOutlined style={{ marginRight: 6 }} />
                    Chat
                  </span>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setChatOpen(false)}
                    style={{ color: mutedColor }}
                    aria-label="Close chat"
                  />
                </div>
                <div ref={chatListRef} style={styles.chatList}>
                  {chatMessages.length === 0 ? (
                    <Text
                      style={{
                        color: mutedFaintColor,
                        fontSize: 12,
                        textAlign: "center",
                        marginTop: 12,
                      }}
                    >
                      Say hi — messages stay in this room.
                    </Text>
                  ) : (
                    chatMessages.map((msg) => {
                      const mine =
                        msg.socketId && msg.socketId === mySocketIdRef.current;
                      return (
                        <div
                          key={msg.id || `${msg.at}-${msg.name}-${msg.text}`}
                          style={
                            mine ? styles.chatBubbleMine : styles.chatBubblePeer
                          }
                        >
                          {!mine && (
                            <span style={styles.chatName}>{msg.name}</span>
                          )}
                          <span style={styles.chatText}>{msg.text}</span>
                          <span style={styles.chatTime}>
                            {msg.at
                              ? new Date(msg.at).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : ""}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
                <div style={styles.chatInputRow}>
                  <Input
                    size="small"
                    value={chatDraft}
                    onChange={(e) => setChatDraft(e.target.value)}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        sendChatMessage();
                      }
                    }}
                    placeholder="Message…"
                    maxLength={500}
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="primary"
                    size="small"
                    icon={<SendOutlined />}
                    onClick={sendChatMessage}
                    disabled={!chatDraft.trim()}
                    style={{
                      background: "#137274",
                      borderColor: "#137274",
                    }}
                  />
                </div>
              </div>
            )}
          </aside>
        </div>
      </Content>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShareAltOutlined style={{ color: "#137274", fontSize: 18 }} />
            <span>Invite to this room</span>
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={440}
        centered
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            paddingTop: 6,
          }}
        >
          <Text type="secondary" style={{ fontSize: 13 }}>
            Anyone with this link can join — share it with your interviewer
            or candidate.
          </Text>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 10px 10px 14px",
              borderRadius: 10,
              border: `1px solid ${isAppDarkMode ? "#30363d" : "rgba(15,60,70,0.15)"}`,
              background: isAppDarkMode ? "#0d1117" : "#f6f8fa",
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: textColor,
              }}
            >
              {shareLink}
            </span>
            <Button
              size="small"
              type="primary"
              icon={<CopyOutlined />}
              onClick={handleCopy}
              style={{
                background: "#137274",
                borderColor: "#137274",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              Copy
            </Button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 14px",
              borderRadius: 10,
              border: `1px solid ${isAppDarkMode ? "#30363d" : "rgba(15,60,70,0.15)"}`,
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: textColor }}>
                View-only link
              </div>
              <div style={{ fontSize: 12, color: mutedColor, marginTop: 2 }}>
                {urlViewOnly
                  ? "You joined via a view-only link — you can't edit."
                  : "Guests with this link can watch but not edit."}
              </div>
            </div>
            <Switch
              checked={shareAsViewOnly}
              onChange={handleShareViewOnlyChange}
              disabled={urlViewOnly}
            />
          </div>
        </div>
      </Modal>
      <LiveCodingTour />
    </Layout>
    </ConfigProvider>
  );
};

export default LiveCodingRoom;
